import {
	BOOST_MAX,
	CLICK_ANIMATION_DEPTH_CORRECTION,
	CLICK_ANIMATION_DURATION,
	EXPLORE_ANIMATION_DEPTH_CORRECTION,
	FADE_ANIMATION_DURATION,
	FLAG_DISABLE_TUTORIAL,
	FLAG_REVEAL_MAP,
	IMAGES_CDN_URL,
	INCOME_BOOST_COEFFICIENT,
	IS_DEV,
	IS_QA,
	PROGRESS_BAR_DEPTH_CORRECTION,
	SAFE_COORDINATE_OUTSIDE_MAP,
	SEGMENT_BORDER_ENABLED,
	SELECTOR_DEPTH_CORRECTION,
	SUPER_CLICKER_TOOLTIP_THRESHOLD,
	UNSUPPORTED_BITMAP_LANGUAGES,
	ZOOM_NO_TILE_DETAIL,
} from "@/config/constants";
import {
	ensureEventMapAssetsLoaded,
	eventRepository,
	useEventStore,
} from "@/event";
import { ResourceType } from "@/game/entities/config/resources";
import { settlementNames } from "@/game/entities/config/settlements.ts";
import { TileVisibilityType } from "@/game/entities/config/terrain.ts";
import { TileToastType, ToastPlacement } from "@/game/ToastMessage";
import type {
	AudioEmitterData,
	IncomeReward,
	PhaserBlockedByEnemyPayload,
	PhaserMessageText,
	PhaserRemoveProgressBarPayload,
	PhaserRevealRangePayload,
	PhaserTileFogAddedPayload,
	PhaserTileLabelClickPayload,
	PhaserUnlockRegionPayload,
	PhaserUpdateTileLabelPayload,
	PlagueProgressUpdatePayload,
	SiegeSystemProgressUpdatePayload,
} from "@/hooks/useEmitter";
import useEmitter, {
	EmitterEvents,
	TileLabelAction,
	type PhaserDisconnectedTilesPayload,
	type PhaserPutTileAtPayload,
	type PhaserTileUpdateProgressBarPayload,
	type PhaserTileClickPayload,
	type PhaserTileClickResource,
	type PhaserTileClickText,
	type TileRepositoryUpgradePayload,
} from "@/hooks/useEmitter";
import useGameTicker from "@/hooks/useGameTicker";
import useSegmentManager from "@/hooks/useSegmentManager";
import useSiegeSystemRepository from "@/hooks/useSiegeSystemRepository";
import useTileRepository from "@/hooks/useTileRepository";
import type AppCommunicator from "@/libs/AppCommunicatior";
import Communicator from "@/libs/AppCommunicatior";
import router from "@/router";
import useMapExpansionStore, {
	type MapExpansionKey,
} from "@/store/useMapExpansionStore";
import {
	PopupDictionary,
	PopupType,
	usePopupStore,
} from "@/store/usePopupStore";
import useQuestStore from "@/store/useQuestStore";
import useQuickToolStore from "@/store/useQuickToolStore";
import useResourcesStore from "@/store/useResourcesStore";
import useRuntimeStore, { SuperClickerEnum } from "@/store/useRuntimeStore";
import useSettingsStore from "@/store/useSettingsStore";
import useSettlementsStore from "@/store/useSettlementsStore";
import useUpgradesStore from "@/store/useUpgradesStore";
import useUserStore from "@/store/useUserStore";
import { type QuestActive } from "@/types/quest";
import { type TexturePackerConfigTextureFrame } from "@/types/texture-packer";
import {
	TileEntityParam,
	TileLabelSkin,
	TileLabelType,
	tileLabelZoomConfig,
	type SettlementItem,
	type TileCoordinates,
	type TileCoordinatesWithValue,
	type TileId,
	type TileIndex,
} from "@/types/tile";
import { RouteNames } from "@/types/ui";
import { formatNumber } from "@/utils/format";
import {
	ceilNumber,
	floorNumber,
	getNumber,
	roundNumber,
} from "@/utils/number";
import { sizeDpr } from "@/utils/sizeDpr";
import { capitalize } from "@/utils/string";
import { isAstcSupported } from "@/utils/textures";
import { translate } from "@/utils/translate";
import { useDebounceFn } from "@vueuse/core";
import { hideAllPoppers } from "floating-vue";
import Phaser from "phaser";
import Stats from "stats.js";
import { AudioLazyLoader } from "./AudioLazyLoader";
import ChestRepository from "./ChestRepository";
import type SettlementCastleBuildingTileEntity from "./entities/buildings/SettlementCastleBuildingTileEntity";
import {
	FrameName,
	MAIN_SETTLEMENT_TILE_INDEX,
	tilerepoFightableEnemiesFrameNames,
	tilerepoFriendlyUnitsFrameNames,
	tilerepoGemsMinesFrameNames,
	tilerepoHideRangeIndicatorsFrameNames,
	tilerepoPalisades1FrameNames,
	tilerepoPalisades3FrameNames,
	tilerepoPoiFrameNames,
	tilerepoSeasFrameNames,
	tilerepoSettlementCastleBuildingsFrameNames,
	tilerepoSettlementEmptyFrameNames,
} from "./entities/config";
import type {
	CutscenesAudioNames,
	FX1AudioNames,
	UIAudioNames,
} from "./entities/config/audio/audioConfig";
import {
	AmbientSoundConfig,
	AudioNames,
	AudioSpriteNames,
} from "./entities/config/audio/audioConfig";
import { getBuildingConfig } from "./entities/config/buildings";
import { getMapAssets } from "./entities/config/map_assets";
import { QuestObjectiveActions } from "./entities/config/quests/quest_objective_actions";
import { QuestTypes } from "./entities/config/quests/quest_types";
import { UpgradeIds } from "./entities/config/upgrades";
import { ChestType, type Chest } from "./entities/objects/chests";
import type TileEntity from "./entities/TileEntity";
import {
	type TileEntityWithOnActivateMethod,
	type TileEntityWithOnClickMethod,
	type TileEntityWithOnRevealMethod,
} from "./entities/TileEntity";
import { levelManager } from "./LevelManager";
import type { PlagueTile } from "./PlagueRepository";
import { plagueRepository } from "./PlagueRepository";
import QuestRepository from "./QuestRepository";
import { type SegmentId } from "./SegmentManager";
import SoundService from "./SoundService";
import type SpecialLocationRepository from "./SpecialLocation";
import {
	getNewSpecialLocation,
	getOldSpecialLocations,
} from "./SpecialLocation";
import TileLabel from "./TileLabel";
import { ToastMessagePool } from "./ToastMessage";
import { manageQuickTool } from "./utils/quickTool";
import {
	getSettlementCastleFrameNameByCoordinates,
	getSettlementPalisadeFrameNameByCoordinates,
} from "./utils/settlement";
import { getUpgradeCurrentReplaceValueById } from "./utils/upgrades";

type DirectAtlasTileKind = "base" | "overlap";

interface TileTintState {
	tint: number;
	tint2: number;
	tintMode: number;
}

/**
 * Lightweight tile descriptor consumed by Phaser's TilemapLayer WebGL renderer.
 * It deliberately contains only the fields read by TilemapLayerWebGLRenderer and
 * TransformerTile, avoiding a full Phaser.Tilemaps.Tile object per atlas frame.
 */
interface DirectAtlasRenderTile {
	index: number;
	x: number;
	y: number;
	pixelX: number;
	pixelY: number;
	alpha: number;
	visible: boolean;
	rotation: number;
	flipX: boolean;
	flipY: boolean;
	tint: number;
	tint2: number;
	tintMode: number;
	renderDepth: number;
}

type DirectAtlasCullCallback = (
	layer: Phaser.Tilemaps.LayerData,
	camera: Phaser.Cameras.Scene2D.Camera,
	outputArray?: Phaser.Tilemaps.Tile[],
	renderOrder?: number,
) => Phaser.Tilemaps.Tile[];

type DirectAtlasTilemapLayer = Phaser.Tilemaps.TilemapLayer & {
	cullCallback: DirectAtlasCullCallback;
	setTilesets(tilesets: Phaser.Tilemaps.Tileset[]): void;

	// Phaser ich vo verejných TypeScript typoch označuje ako readonly,
	// no jeho interné RunCull/CullTiles implementácie ich aktualizujú každý frame.
	// Keďže používame vlastný cull callback, pre tento interný adapter ich
	// zámerne deklarujeme ako zapisovateľné, aby sme zachovali rovnaké štatistiky.
	tilesDrawn: number;
	tilesTotal: number;
};

type TexureConfigDataImg = {
	img: { multiAtlasURL: string };
};

type TexureConfigDataAstc = {
	astc: { type: string; multiAtlasURL: string };
};

type TextureConfig = {
	name: string;
	data: TexureConfigDataImg | TexureConfigDataAstc;
};

interface ChestTile extends Chest {
	img: TileLabel;
}

type MapAsset = ReturnType<typeof getMapAssets>[string];

const TEXTURE_PACKER_DIR = "LKTexturePackerExport-2026-08-31";
const TEXTURE_FORMAT_DIR = isAstcSupported() ? "PVRs" : "PNGs";

const TILESET_NAME_REGEXP = /(?<name>tileset_\d{4})(?:_v\d+)?\.json$/;
const ANIMATIONS_NAME_REGEXP = /(?<name>animations_\d{2})(?:_v\d+)?\.json$/;

const CLICK_SCALE = 0.75;

export const DURABILITY_EXPLORED = 4_294_967_294; // max uint32 value

// Toto musi byt mimo classy, aby to bolo perzistentne. Boh vas chran, co presunut do classy.
let clickedTileEntityCache: TileEntity | null = null;

class MainMapScene extends Phaser.Scene {
	/**
	 * Uchováva odkaz na hlavnú 2D kameru scény. Používa sa pri posúvaní a približovaní mapy, výpočte viditeľnej
	 * oblasti, prevode vstupných súradníc a práci s ambientným zvukom.
	 */
	private camera: Phaser.Cameras.Scene2D.Camera | null = null;

	/**
	 * Určuje, či sa aktuálne gesto vstupu vyhodnotilo ako posúvanie mapy. Hodnota zabraňuje tomu, aby sa drag
	 * po uvoľnení pointera spracoval ako klik na tile.
	 */
	private isDragging: boolean = false;

	/**
	 * Obsahuje obrazovkové súradnice pointera v okamihu začiatku stlačenia. Slúži na výpočet vzdialenosti
	 * pohybu a rozlíšenie kliku od drag operácie.
	 */
	private dragStartPoint: Phaser.Math.Vector2 = new Phaser.Math.Vector2();

	/**
	 * Uchováva pozíciu kamery v okamihu začiatku drag operácie. V aktuálnej implementácii sa hodnota pri
	 * pointerdown nastaví.
	 */
	private cameraStartPoint: Phaser.Math.Vector2 = new Phaser.Math.Vector2();

	/**
	 * Pamätá si poslednú zaznamenanú pozíciu kamery. Používa sa na zistenie, či sa kamera od poslednej
	 * interakcie zmenila a či treba aktualizovať čas poslednej zmeny.
	 */
	private cameraLastChangedPoint: Phaser.Math.Vector2 =
		new Phaser.Math.Vector2();

	/**
	 * Predstavuje aktuálnu silu jedného kliknutia pri odhaľovaní tile. Hodnota zahŕňa základné nastavenie a
	 * používateľské modifikátory zo store. Toto je user dependent hodnota, default je 100 a navysuje sa upgradami atd
	 */
	private explorationSpeed: number = 100;

	/**
	 * Obsahuje konfiguračný násobiteľ rýchlosti odhaľovania použitý v prípade, že používateľský store ešte
	 * neposkytuje vlastnú hodnotu.
	 */
	private explorationSpeedConf: number = 1;

	/**
	 * Definuje základnú rýchlosť odhaľovania, z ktorej sa počíta aktuálna hodnota explorationSpeed.
	 */
	private explorationSpeedDefault: number = 100;

	/**
	 * Jedná sa o cache obsahujúcu grafické objekty reprezentujúce progress bar odhalľovania pre
	 * jednotlivé tiles na základe unikátneho identifikátora TileIndex
	 */
	private readonly progressBars = new Map<
		TileIndex,
		{ bar: Phaser.GameObjects.Graphics; progress: number }
	>();

	/**
	 * Uchováva aktuálne zobrazený grafický selektor označeného tile. Hodnota null znamená, že nie je vybraný
	 * žiadny tile.
	 */
	private clickSelector: Phaser.GameObjects.Image | null = null;

	/**
	 * Cache dočasných kontajnerov používaných pri click a fade animáciách jednotlivých tiles. Kľúčom je index
	 * tile, aby sa pred vytvorením novej animácie dala odstrániť predchádzajúca.
	 */
	private animationContainersCache = new Map<
		TileIndex,
		Phaser.GameObjects.Container
	>();

	/**
	 * Cache aktívnych tweenov pre tiles a tile labely. Slúži na zastavenie, dokončenie a odstránenie staršej
	 * animácie pred spustením novej animácie rovnakého objektu.
	 */
	private animationTweensCache: Map<string | TileIndex, Phaser.Tweens.Tween> =
		new Map<
			TileIndex | ReturnType<(typeof TileLabel)["formatKey"]>,
			Phaser.Tweens.Tween
		>();

	/**
	 * Referenčný objekt centrálneho tile repository. Poskytuje rozmery mapy, konverzie súradníc, entity,
	 * parametre, frame names a operácie meniace stav tiles.
	 */
	private readonly tilerepo: ReturnType<typeof useTileRepository>;

	/**
	 * Referenčný objekt aplikačného event busu. Scéna cez neho registruje reakcie na udalosti a oznamuje zmeny
	 * ostatným častiam aplikácie.
	 */
	private readonly emitter: ReturnType<typeof useEmitter>;

	/**
	 * Store spravujúci odomykateľné regióny mapy, ich hranice a kontrolu, či sú súradnice v uzamknutej oblasti.
	 */
	private readonly mapExpansionStore: ReturnType<typeof useMapExpansionStore>;

	/**
	 * Phaser Tilemap pre základnú direct-atlas vrstvu. Fyzické dáta obsahujú iba pomocnú
	 * bunku; skutočné tiles dodáva vlastný culling callback.
	 */
	private tilemap: Phaser.Tilemaps.Tilemap | null = null;

	/**
	 * Phaser TilemapLayer vykresľujúci základné, štvorcové časti mapy. Používa virtuálne tilesety smerujúce
	 * priamo na už načítané atlasové frames.
	 */
	private mapLayer: Phaser.Tilemaps.TilemapLayer | null = null;

	/**
	 * Phaser Tilemap pre vrstvu presahov a odrezkov označených príponou _Top.png.
	 */
	private overlapTilemap: Phaser.Tilemaps.Tilemap | null = null;

	/**
	 * Phaser TilemapLayer vykresľujúci presahy budov, palisád a ďalších objektov nad susednými tiles.
	 */
	private overlapLayer: Phaser.Tilemaps.TilemapLayer | null = null;

	/**
	 * Zoznam hlavných TilemapLayer objektov scény. Aktuálna implementácia doň po vytvorení uloží base a overlap
	 * vrstvu a pri shutdown ho vyprázdni; slúži ako spoločná lifecycle referencia.
	 */
	private readonly layers: Phaser.Tilemaps.TilemapLayer[] =
		new Array<Phaser.Tilemaps.TilemapLayer>();

	// Each used atlas frame gets a virtual one-tile Tileset. The Tileset points
	// directly at the already loaded atlas TextureSource; it does not allocate a
	// DynamicTexture, framebuffer or a second pixel copy in VRAM.
	/**
	 * Zoznam virtuálnych jedno-tile tilesetov vytvorených pre použité atlasové frames. Tilesety iba odkazujú na
	 * existujúce GPU textúry a nevytvárajú pixelové kópie.
	 */
	private readonly directAtlasTilesets: Phaser.Tilemaps.Tileset[] = [];

	/**
	 * Mapuje názov atlasového frame na už vytvorený virtuálny Tileset. Cache zabezpečuje, že každý frame
	 * dostane práve jeden GID a jeden tileset.
	 */
	private readonly directAtlasTilesetCache: Map<
		FrameName,
		Phaser.Tilemaps.Tileset
	> = new Map<FrameName, Phaser.Tilemaps.Tileset>();

	/**
	 * Dátová štruktpra určená pre culling. Vlastný culling callback z tejto
	 * mapy vyberá iba viditeľné položky.
	 */
	private readonly directBaseTilesByCoordinateKey: Map<
		TileIndex,
		DirectAtlasRenderTile[]
	> = new Map<TileIndex, DirectAtlasRenderTile[]>();

	/**
	 * Dátová štruktpra určená pre culling. Vlastný culling callback z tejto
	 * mapy vyberá iba viditeľné položky ale len pre odreskz - _Top.
	 */
	private readonly directOverlapTilesByCoordinateKey: Map<
		TileIndex,
		DirectAtlasRenderTile[]
	> = new Map<TileIndex, DirectAtlasRenderTile[]>();

	/**
	 * Opakovane používané pracovné polia pre rozdelenie viditeľných základných tiles podľa renderovacej hĺbky.
	 * Recyklovanie polí obmedzuje alokácie počas každého frame.
	 */
	private readonly directBaseCullDepthBuckets: DirectAtlasRenderTile[][] = [];

	/**
	 * Opakovane používané pracovné polia pre rozdelenie viditeľných overlap tiles podľa renderovacej hĺbky.
	 * Datova struktura pre odresky.
	 */
	private readonly directOverlapCullDepthBuckets: DirectAtlasRenderTile[][] =
		[];

	/**
	 * Uchováva farebný tint a tint mode jednotlivých tiles. Cache umožňuje zachovať zvýraznenie aj po
	 * prekreslení tile alebo počas dočasnej animácie.
	 */
	private readonly tileTintCache: Map<TileIndex, TileTintState> = new Map<
		TileIndex,
		TileTintState
	>();

	/**
	 * Obsahuje najbližší voľný globálny tile identifikátor pre nový virtuálny direct-atlas tileset.
	 */
	private nextDirectAtlasGid = 1;

	/**
	 * Signalizuje, že od posledného refreshu pribudol virtuálny tileset a GID mapy oboch TilemapLayer objektov
	 * treba znovu zostaviť.
	 */
	private directAtlasTilesetsDirty = false;

	// Dávkové rozloženie štítkov počas inicializácie synchrónnej mapy. Nechcem aktualiyovat TileLabels v rednerTile pri uvodnom nacitavani
	private deferInitialTileLabelUpdates = false;

	/**
	 * Základná depth hodnota pre selektory, labely, progress bary a efekty vykresľované nad všetkými mapovými
	 * atlasovými vrstvami.
	 */
	private mapOverlayBaseDepth = 1;

	/**
	 * Zostaví úplný a zámerne usporiadaný zoznam URL manifestov mapových multiatlasov a atlasu animácií.
	 * Poradie položiek sa neskôr používa ako poradie logických renderovacích vrstiev, preto ho metóda nemení
	 * ani dynamicky netriedi.
	 *
	 * @returns Usporiadané pole URL reťazcov atlasových JSON manifestov.
	 */
	private getAllAtlasUrls(): Array<`${string}/${typeof TEXTURE_PACKER_DIR}/${typeof TEXTURE_FORMAT_DIR}/${string}.json`> {
		return [
			`${IMAGES_CDN_URL}/${TEXTURE_PACKER_DIR}/${TEXTURE_FORMAT_DIR}/tileset_1000.json`,
			`${IMAGES_CDN_URL}/${TEXTURE_PACKER_DIR}/${TEXTURE_FORMAT_DIR}/tileset_1001.json`,
			`${IMAGES_CDN_URL}/${TEXTURE_PACKER_DIR}/${TEXTURE_FORMAT_DIR}/tileset_1002.json`,
			`${IMAGES_CDN_URL}/${TEXTURE_PACKER_DIR}/${TEXTURE_FORMAT_DIR}/tileset_1003.json`,
			`${IMAGES_CDN_URL}/${TEXTURE_PACKER_DIR}/${TEXTURE_FORMAT_DIR}/tileset_1004.json`,
			`${IMAGES_CDN_URL}/${TEXTURE_PACKER_DIR}/${TEXTURE_FORMAT_DIR}/tileset_1005.json`,
			`${IMAGES_CDN_URL}/${TEXTURE_PACKER_DIR}/${TEXTURE_FORMAT_DIR}/tileset_1006.json`,
			`${IMAGES_CDN_URL}/${TEXTURE_PACKER_DIR}/${TEXTURE_FORMAT_DIR}/tileset_1007.json`,
			`${IMAGES_CDN_URL}/${TEXTURE_PACKER_DIR}/${TEXTURE_FORMAT_DIR}/tileset_1008.json`,
			`${IMAGES_CDN_URL}/${TEXTURE_PACKER_DIR}/${TEXTURE_FORMAT_DIR}/tileset_2000.json`,
			`${IMAGES_CDN_URL}/${TEXTURE_PACKER_DIR}/${TEXTURE_FORMAT_DIR}/tileset_2001.json`,
			`${IMAGES_CDN_URL}/${TEXTURE_PACKER_DIR}/${TEXTURE_FORMAT_DIR}/tileset_2002.json`,
			`${IMAGES_CDN_URL}/${TEXTURE_PACKER_DIR}/${TEXTURE_FORMAT_DIR}/tileset_3000.json`,
			`${IMAGES_CDN_URL}/${TEXTURE_PACKER_DIR}/${TEXTURE_FORMAT_DIR}/tileset_3001.json`,
			`${IMAGES_CDN_URL}/${TEXTURE_PACKER_DIR}/${TEXTURE_FORMAT_DIR}/tileset_3002.json`,
			`${IMAGES_CDN_URL}/${TEXTURE_PACKER_DIR}/${TEXTURE_FORMAT_DIR}/tileset_3003.json`,
			`${IMAGES_CDN_URL}/${TEXTURE_PACKER_DIR}/${TEXTURE_FORMAT_DIR}/tileset_3004.json`,
			`${IMAGES_CDN_URL}/${TEXTURE_PACKER_DIR}/${TEXTURE_FORMAT_DIR}/tileset_3005.json`,
			`${IMAGES_CDN_URL}/${TEXTURE_PACKER_DIR}/${TEXTURE_FORMAT_DIR}/tileset_4000.json`,
			`${IMAGES_CDN_URL}/${TEXTURE_PACKER_DIR}/${TEXTURE_FORMAT_DIR}/tileset_4001.json`,
			`${IMAGES_CDN_URL}/${TEXTURE_PACKER_DIR}/${TEXTURE_FORMAT_DIR}/tileset_5000.json`,
			`${IMAGES_CDN_URL}/${TEXTURE_PACKER_DIR}/${TEXTURE_FORMAT_DIR}/tileset_6000.json`,
			`${IMAGES_CDN_URL}/${TEXTURE_PACKER_DIR}/${TEXTURE_FORMAT_DIR}/tileset_7000.json`,
			`${IMAGES_CDN_URL}/${TEXTURE_PACKER_DIR}/${TEXTURE_FORMAT_DIR}/animations_01.json`,
		];
	}

	/**
	 * Cache prevodu URL atlasového manifestu na interný Phaser texture key odvodený z názvu súboru.
	 */
	private readonly atlasUrlToAtlasNamesCache: Map<string, string> = new Map<
		string,
		string
	>();

	/**
	 * Mapuje každý názov frame na texture key atlasu, v ktorom je frame uložený. Vyhľadávanie tak neprechádza
	 * všetky textúry.
	 */
	private readonly frameNameToAtlasNameCache: Map<FrameName, string> =
		new Map<FrameName, string>();

	/**
	 * Mapuje frame na jeho logickú renderovaciu hĺbku odvodenú z poradia atlasov.
	 */
	private readonly frameNameToLayerIndexCache: Map<FrameName, number> =
		new Map<FrameName, number>();

	// Reverse lookup retained to preserve the former atlas render order.
	/**
	 * Reverzný zoznam množín framov pre jednotlivé logické vrstvy. Zachováva pôvodné poradie vykresľovania
	 * atlasov.
	 */
	private readonly frameNameToLayerCache: Set<FrameName>[] = new Array<
		Set<FrameName>
	>();

	/**
	 * Množina všetkých dostupných atlasových framov s príponou _Top.png. Umožňuje rýchlo rozlíšiť základný
	 * frame od presahu.
	 */
	private readonly overlapFrameNamesCache: Set<FrameName> =
		new Set<FrameName>();

	/**
	 * Inštancia Stats.js používaná na zobrazenie a aktualizáciu výkonnostných diagnostík v debug režime.
	 */
	private stats: Stats;

	/**
	 * Cache pomocných Phaser Graphics objektov, napríklad pulzujúcich kruhov a tutorialových značiek,
	 * zoskupených podľa súradnicového kľúča.
	 */
	private graphicsObjects: Map<string, Phaser.GameObjects.Graphics[]> =
		new Map<string, Phaser.GameObjects.Graphics[]>();

	/**
	 * Textový kľúč súradnice, na ktorú sa má zamerať úvodná tutorialová navigácia. Predvolená hodnota sa v
	 * konštruktore prispôsobí hlavnému sídlu.
	 */
	private tutorialStartingFocusKey = "512,510";

	/**
	 * Počítadlo postavených tutorialových ciest. Určuje, ktorý krok a grafické zvýraznenie tutorialu sa má
	 * aktivovať.
	 */
	private tutorialRoadBuilderCounter = 0;

	/**
	 * Front súradníc, ktoré má hráč postupne odhaliť počas tutorialu. Poradie frontu riadi presúvanie
	 * pulzujúcich markerov.
	 */
	private tutorialRevealQueue: TileCoordinates[] = [];

	/**
	 * Príznak určujúci, či sa tutorialové značky X majú v aktuálnej fáze nahradiť pulzujúcimi kruhmi.
	 */
	private tutorialConvertXsToPulses: boolean = false;

	/**
	 * Zoznam Graphics objektov zobrazujúcich dosah vybraného tile alebo budovy. Objekty sa hromadne ničia pri
	 * zrušení výberu.
	 */
	private rangeHighlights: Phaser.GameObjects.Graphics[] = [];

	/**
	 * Identifikátor intervalového časovača pre automatické opakovanie kliknutia. Null znamená, že auto-tapping
	 * nie je aktívny.
	 */
	private autoTappingInterval: ReturnType<typeof setInterval> | null = null;

	/**
	 * Zoznam truhlíc aktuálne umiestnených na mape spolu s ich TileLabel reprezentáciou.
	 */
	private chests: ChestTile[] = [];

	/**
	 * Cache všetkých bežných TileLabel objektov podľa kombinácie skinu a indexu tile. Zabraňuje duplikátom a
	 * urýchľuje update alebo delete operácie.
	 */
	private tileLabelsCache: Map<string, TileLabel> = new Map<
		string,
		TileLabel
	>();

	/**
	 * Určuje, či pointerdown vznikol nad tile labelom. Používa sa na koordináciu labelových klikov s drag a map
	 * click logikou.
	 */
	private tileLabelPointerDown: boolean = false;

	/**
	 * Cache aktívnych špeciálnych lokácií a ich labelov podľa formátovaného kľúča tile.
	 */
	private specialLocationsCache: Map<string, SpecialLocationRepository> =
		new Map<string, SpecialLocationRepository>();

	// Black tile detection for auto camera reset
	/**
	 * Čas začiatku stavu, keď viewport zobrazuje iba čierne tiles. Hodnota slúži na odmeranie timeoutu pred
	 * emitovaním požiadavky na zobrazenie tlačidla resetu kamery.
	 */
	private blackTileStartTime: number = 0;

	/**
	 * Príznak, či predchádzajúca kontrola zistila viewport obsahujúci iba neodhalené čierne tiles.
	 */
	private isViewingOnlyBlackTiles: boolean = false;

	/**
	 * Čas v milisekundách, po ktorom scéna pri pohľade iba na čierne tiles emituje udalosť na zobrazenie
	 * tlačidla resetu kamery.
	 */
	private readonly BLACK_TILE_TIMEOUT: number = 2000; // 2 seconds in milliseconds

	/**
	 * Pool ToastMessage objektov používaný na efektívne zobrazovanie odmien, varovaní a informačných správ bez
	 * opakovaného vytvárania všetkých zdrojov.
	 */
	private toastPool!: ToastMessagePool;

	// Effects are ordinary transient Image/Sprite Game Objects after the migration.
	/**
	 * Horný limit počtu dočasných obrázkov oblakov vytvorených úvodnou preletovou animáciou.
	 */
	private readonly MAX_CLOUD_ANIMATION_COUNT = 100;

	/**
	 * Voliteľná inštancia rexCameraController pluginu zabezpečujúca pan, pinch zoom a mouse-wheel zoom.
	 */
	private cameraController?: Phaser.Plugins.BasePlugin | null;

	/**
	 * Referenčný objekt Phaser animácie búrania. Používa sa pri vytváraní dočasného sprite efektu demolácie.
	 */
	private demolishAnim: Phaser.Animations.Animation | null = null;

	/**
	 * Graphics objekt hlavného rámu aktuálne odomknutej mapovej oblasti. Pri zmene zoomu alebo hraníc sa
	 * recykluje a prekreslí.
	 */
	private mapBorder: Phaser.GameObjects.Graphics | null = null;

	/**
	 * Mapuje ID herného segmentu na Graphics objekt jeho diagnostického ohraničenia.
	 */
	private readonly segmentMapBorders: Map<
		SegmentId,
		Phaser.GameObjects.Graphics
	> = new Map<SegmentId, Phaser.GameObjects.Graphics>();

	/**
	 * Mapuje kľúč rozšírenia mapy na Graphics objekt hranice daného odomykateľného regiónu.
	 */
	private readonly regionMapBorders: Map<
		MapExpansionKey,
		Phaser.GameObjects.Graphics
	> = new Map<MapExpansionKey, Phaser.GameObjects.Graphics>();

	/**
	 * Odkaz na špeciálny TileLabel používaný tutorialom. Null znamená, že tutorialový label momentálne nie je
	 * zobrazený.
	 */
	private tutorialLabel: TileLabel | null = null;

	/**
	 * Inicializuje Phaser scénu MainMapScene, pripojí hlavné repository, event bus a store rozšírení mapy,
	 * pripraví výkonnostné štatistiky a vypočíta počiatočný tutorialový bod. V developerskom režime zároveň
	 * sprístupní inštanciu scény cez window.map na diagnostické účely.
	 */
	public constructor() {
		super({
			key: "MainMapScene",
		});

		this.tilerepo = useTileRepository();
		this.emitter = useEmitter();
		this.mapExpansionStore = useMapExpansionStore();

		//toto je user dependent hodnota, default je 100 a navysuje sa upgradami atd
		this.updateExplorationSpeed();

		// Inicializácia Stats.js
		this.stats = new Stats();

		const capitalSettment: SettlementItem | undefined =
			settlementNames.get(1);

		if (capitalSettment) {
			const { x, y } = capitalSettment;
			// nastav zaciatok tutorialu na hlavne mesto
			this.tutorialStartingFocusKey = `${x},${y - 2}`;
		}

		// eslint-disable-next-line no-console
		console.log(this);

		// @ts-expect-error - missing type in window
		if (!window.map && IS_DEV) window.map = this;
	}

	/**
	 * Zaradí do Phaser loadera všetky atlasy, samostatné mapové assety a bitmapový font potrebné pred
	 * vytvorením scény. Podľa podpory zariadenia volí komprimovanú ASTC/PVR alebo nekomprimovanú obrazovú
	 * verziu a pri assetoch s osobitným spôsobom použitia zvolí spritesheet alebo image loader.
	 *
	 */
	public preload(): void {
		// Prejdi vešetky atlasy a načítaj ich PVR/PNG verziu
		this.getAtlasConfig().forEach((config: TextureConfig): void => {
			if (this.textures.exists(config.name)) return;
			// @ts-expect-error - wrong types
			this.load.texture(config.name, config.data);
		});

		// Dynamické načítanie podľa podpory formátu
		const mapAssets = getMapAssets();
		// Prejdeme každú položku reprezentujúcu zoznam spritov a nazáklade podporvaného formátu ic hnačítame do
		// Phaser loaderu
		Object.entries(mapAssets).forEach(
			([key, { compressed, uncompressed, atlas = "" }]: [
				string,
				MapAsset,
			]): void => {
				//táto výnimka je to preto, že v čase implementácie tejto zmeny nemám k dispozícií
				//jednotlivé assety nato, aby som s nich cez Mareka Š vedel vytvoriť atlas vo formáte
				//PVR/ASTC. Mám len AVIF a teda keďže sa jedná o atlas/spritesheet a nedisponujem JSON
				//súborom ktorý určuje kde sa aký frame nachádza v danom spritesheete. Teda musím to ponechať
				//v nekomprimovanom formáte a nechať na Phaser aby dynamicky v rámci runtime určoval jednotlivé frames
				//toto sa pre kompresné texútry nedá spraviť - dá ale je to veľmi náročné. Keď bude čas, vymeníme tento asset
				//za PVR/ASTC aj s JSON súborom a túto podmienku vyhodíme(bude nutné urpaviť config lebo teraz tam nemám možnosť vkladať JSON atlas)
				if (key === "exploration-icons") {
					this.load.spritesheet(key, uncompressed, {
						frameWidth: 180,
						frameHeight: 180,
					});
					// ak sa jedná nie o spritesheet ale len o 1 obrázok tak použijeme nasledovné načítanie
				} else if (
					[
						"tutorial-label-bg-texture",
						"main-settlement-name-ribbon",
						"icon-fishing-eventium",
						"icon-easter-eventium",
						"icon-christmas-eventium",
						"icon-firefest-eventium",
						"icon-solarfair-eventium",
						"icon-hunting-eventium",
						"icon-halloween-eventium",
						"icon-pirates-eventium",
					].includes(key)
				) {
					//táto výnimka je tu preto, že v súbore TileLabel sa tento asset vyťahuje nie cez scene.add.image atd
					//ale cez špeciálny typ a síce scene.add.tileSprite. Ak vkladáme takýto typ gameObjectu do scény
					//je potrebné ho mať naloadovaný nie ako textúru ale ako jednoduchý obrázok bez kompresie lebo
					//sa používajú atribúty ako tileScaleX/Y a blendovanie, čo v kompresných textúrach funguje úplne inak.
					//Teda ak by sme načítali túto textúru cez this.load.texture nastane problém pri jej aktuálnom
					//použití. Dá sa to samozrejme upraviť ale museli by sme prerobiť logiku v rámci TileLabel.
					this.load.image(key, uncompressed);
				} else {
					// Toto by teoreticky nikdy nemalo nastať, lebo podľa správnosti sa tieto veci majú naťahovať
					// v sekcii atlasov, ale ak by náhodou niekto spravil chybu, tak aby sa predišlo nenačítaniu
					// príslušného atlasu / samostatného obrázku je tu táto časť kódu
					this.load.texture(key, {
						ASTC: {
							type: "PVR",
							textureURL: compressed,
							atlasURL: atlas,
						},
						IMG: { textureURL: uncompressed, atlasURL: atlas },
					});
				}
			},
		);

		// Načítanie fontu pre bitmap text, vo väčšine jazykov používame bitmap font, niekde však používame
		// aj štandardný Text object čo je canvas verzia
		this.load.bitmapFont(
			"texturina",
			`${IMAGES_CDN_URL}/fonts/Texturina/bitmap/Texturina-112pt-Regular-v5.avif`,
			`${IMAGES_CDN_URL}/fonts/Texturina/bitmap/Texturina-112pt-Regular-v5.fnt`,
		);
	}

	/**
	 * Vyhodnotí používateľské nastavenia zvuku a stav Phaser audio cache a zaradí chýbajúci UI audio sprite,
	 * hlavnú hudbu a mestský ambient.
	 *
	 * @returns Pole kľúčov explicitne pridaných do audiosToLoad.
	 */
	private loadCriticalAudioAssets(): (AudioNames | AudioSpriteNames)[] {
		const settingsStore = useSettingsStore();
		const audiosToLoad: (AudioNames | AudioSpriteNames)[] = [];

		// Load UI sounds if enabled and not already loaded
		if (settingsStore.soundsOn && !this.sound.get(AudioSpriteNames.UI)) {
			audiosToLoad.push(AudioSpriteNames.UI);
			this.load.audioSprite(
				AudioSpriteNames.UI,
				`${IMAGES_CDN_URL}/sounds/ui-sounds.json`,
				[
					`${IMAGES_CDN_URL}/sounds/ui-sounds.ogg`,
					`${IMAGES_CDN_URL}/sounds/ui-sounds.ac3`,
					`${IMAGES_CDN_URL}/sounds/ui-sounds.mp3`,
					`${IMAGES_CDN_URL}/sounds/ui-sounds.m4a`,
				],
			);
		}

		// Load main game music if enabled and not already loaded
		if (settingsStore.musicOn && !this.sound.get(AudioNames.MAIN_MUSIC)) {
			audiosToLoad.push(AudioNames.MAIN_MUSIC);
			this.load.audio(
				AudioNames.MAIN_MUSIC,
				[
					`${IMAGES_CDN_URL}/sounds/main/main-music.ogg`,
					`${IMAGES_CDN_URL}/sounds/main/main-music.ac3`,
					`${IMAGES_CDN_URL}/sounds/main/main-music.m4a`,
					`${IMAGES_CDN_URL}/sounds/main/main-music.mp3`,
				],
				{
					loop: true,
				},
			);
		}

		// Load city ambient sounds if enabled and not already loaded
		// we do not add this sound to audiosToLoad because city ambient
		// sound can be used only if you have certain level, it is not
		// playeble from the first start of the game
		if (
			settingsStore.soundsOn &&
			!this.sound.get(AudioNames.CITY_AMBIENT)
		) {
			this.load.audio(
				AudioNames.CITY_AMBIENT,
				[
					`${IMAGES_CDN_URL}/sounds/city-ambient/city-ambient.ogg`,
					`${IMAGES_CDN_URL}/sounds/city-ambient/city-ambient.ac3`,
					`${IMAGES_CDN_URL}/sounds/city-ambient/city-ambient.m4a`,
					`${IMAGES_CDN_URL}/sounds/city-ambient/city-ambient.mp3`,
				],
				{
					loop: true,
				},
			);
		}

		return audiosToLoad;
	}

	/**
	 * Vykoná hlavnú inicializáciu scény po načítaní assetov. Zostaví atlasové cache a mapové vrstvy, pripraví
	 * kameru, event listenery, labely, hranice, časovače, zvuk, questy, truhlice, špeciálne lokácie a herné
	 * repository a nakoniec oznámi dokončenie načítania.
	 *
	 * @returns Promise bez dátovej návratovej hodnoty, ktorý sa splní po dokončení asynchrónnej operácie.
	 */
	public async create(): Promise<void> {
		const communicator: AppCommunicator = Communicator.getInstance();

		this.createAtlasFrameNamesCache();
		//nastavím zamedzenie aktualizácii TileLabel pri vytváraní mapy - rednderTile === jeden updateTileLabel, to nechceme
		this.deferInitialTileLabelUpdates = true;
		try {
			this.createTileMap();
		} finally {
			//nech sa stane cokolvek, tak vždy vypneme nastavenie, čo zamedzuje updatovanie tile labels na mape
			this.deferInitialTileLabelUpdates = false;
		}

		this.setupSceneCamera();
		this.setCameraController();
		// Keď je veštko prirpavené jednorázovo aktualizujem všetky TileLabels
		this.updateTileLabels();

		// Emitujeme udalosť do aplikačného event busu, aby sa o zmene dozvedeli ostatné moduly.
		this.emitter.emit(EmitterEvents.PhaserSceneReady);

		this.addClickHandler();
		this.initEmitters();
		this.tilerepo.initTint();
		this.tilerepo.fixRedTiles();

		this.addOrModifyMapBorder();

		// AK sme na dev prostrediach a chceme to vidieť, vieme zapnúť mriežku
		// ktorá vykresľuje jednotlivé segmenty na mape pre lepší debugging
		if (SEGMENT_BORDER_ENABLED && (IS_DEV || IS_QA)) {
			this.addOrModifySegmentMapBorders();
		}

		this.addOrModifyMapExpansionBorders();

		communicator.setLoadingProgress(0.9);
		communicator.loadingDone();
		communicator.canVibrate();
		communicator.setLoadingProgress(1.0);

		this.initToastPool();

		const useQuest = useQuestStore();
		// Check if the backend quests are changed against the frontend quests.
		useQuest.checkActiveQuestDefinitions();
		// Prejdeme každú položku z kolekcie `useQuest.activeQuests` a nad každou vykonáme callback.
		useQuest.activeQuests.forEach((quest: QuestActive): void => {
			// Ak quest obsahuje badge na mape, tak zistíme aký badge graficky vykreslíme prislužný
			// obrázok na mape
			if (quest.badgeCoordinates) {
				const skin =
					quest.type === QuestTypes.MAIN
						? TileLabelSkin.QUEST_MAIN_Q
						: TileLabelSkin.QUEST_SIDE_Q;

				useEmitter().emit(EmitterEvents.PhaserUpdateTileLabel, {
					coordinates: quest.badgeCoordinates!,
					skin,
					action: TileLabelAction.CREATE,
				});
			}
		});
		// Spustí updateTileLabels každých 500 ms
		this.time.addEvent({
			delay: 500, // v milisekundách
			callback: (): void => {
				this.checkBlackTileTimeout();
			},
			callbackScope: this, // zabezpečí, že `this` odkazuje na túto scénu
			loop: true, // aby sa volalo opakovane
		});

		//spusti prekreslovanie ciar ohraničenia mapy každé 2 sekundy
		this.time.addEvent({
			delay: 2000,
			callback: (): void => {
				this.addOrModifyMapBorder();
				this.addOrModifyMapExpansionBorders();
				// Ak platí podmienka `SEGMENT_BORDER_ENABLED && (IS_DEV || IS_QA)`, vykonáme nasledujúcu vetvu
				// a jej operácie aktualizujú príslušný stav.
				if (SEGMENT_BORDER_ENABLED && (IS_DEV || IS_QA)) {
					this.addOrModifySegmentMapBorders();
				}
			},
			callbackScope: this,
			loop: true,
		});

		this.handleSettlementCtaMapLabel();
		this.addResizeListener();
		levelManager.init();
		await this.loadSoundAndMusicAssetsPromise();

		this.emitter.emit(EmitterEvents.GameLoadingDone);

		// Initialize ChestRepository with TileRepository
		const chestRepository = ChestRepository.getInstance();
		const tileRepository = useTileRepository();
		chestRepository.initialize(tileRepository);

		this.upgrade6NerfFix();
		this.initIncomeBoost();

		const locations = getOldSpecialLocations(this);
		// Prejdeme každú položku z kolekcie special locations a vytvorí mapovanie kľúč/hodnota
		locations.forEach(
			(location: SpecialLocationRepository | null): void => {
				if (!location) return;
				const key = TileLabel.formatKey(
					TileLabelSkin.SPECIAL_LOCATION,
					this.tilerepo.getTileIndexByTileCoordinates(
						location.coordinates,
					),
				);
				// Synchronizujeme príslušnú cache so zmeneným stavom, aby ďalšie lookupy a rendering používali
				// aktuálne údaje.
				this.specialLocationsCache.set(key, location);
			},
		);

		const revealedSettlement: TileCoordinates | null =
			useUserStore().revealedSettlementFromOfferCoordinates;
		// Ak má pužívateľ zakúpený offer cez shop, ktorý mu na mape zobrazí najbližší neodhaľený settlement
		// tak je potrebné mu pridať label reprezentujúci tento marker na mapu na príslušné kooordináty
		if (revealedSettlement) {
			this.emitter.emit(EmitterEvents.PhaserUpdateTileLabel, {
				coordinates: {
					x: revealedSettlement.x,
					y: revealedSettlement.y,
				} as TileCoordinates,
				skin: TileLabelSkin.SETTLEMENT_HIGHLIGHT,
				action: TileLabelAction.CREATE,
			});
		}

		this.emitter.emit(EmitterEvents.PhaserCreateGemsBadge);

		eventRepository.init(this.tilerepo);
		plagueRepository.init(this.tilerepo);

		useSiegeSystemRepository().rerenderAllBattlefieldLabels();

		//načíta assety pre event ale len ak je event aktívny
		void ensureEventMapAssetsLoaded(this);

		// Zobraznie markerov na mape pre side questy ktoré sú k dispozícií pre hráča
		QuestRepository.getInstance().initializeSideQuestsMarkers();
		useSiegeSystemRepository().initTileLabels();

		this.updateProgressBars();
	}

	/**
	 * Obnoví runtime stav príjmového boostu zo stavu používateľa. Ak boost ešte trvá, aktivuje ho; inak podľa
	 * denného limitu nastaví informačnú notifikáciu o dostupnom použití.
	 *
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private initIncomeBoost(): void {
		const runtimeStore = useRuntimeStore();
		const userStore = useUserStore();

		// Ak platí podmienka `userStore.incomeBoostTicksRemaining > 0`, vykonáme nasledujúcu vetvu a jej
		// operácie aktualizujú príslušný stav.
		if (userStore.incomeBoostTicksRemaining > 0) {
			// Zapíšeme výsledok operácie do centrálneho store alebo spustíme nadväzujúce prepočty závislého
			// stavu.
			runtimeStore.setIncomeBoostActive(true);
		} else {
			// Ak predchádzajúca podmienka neplatí, vykonáme alternatívnu vetvu.
			// Ak platí podmienka `userStore.incomeBoostsUsedToday < BOOST_MAX +
			// getUpgradeCurrentReplaceValueById(UpgradeIds.witch_lake)`, vykonáme nasledujúcu vetvu a jej
			// operácie aktualizujú príslušný stav.
			if (
				userStore.incomeBoostsUsedToday <
				BOOST_MAX +
					getUpgradeCurrentReplaceValueById(UpgradeIds.witch_lake)
			) {
				// Zapíšeme výsledok operácie do centrálneho store alebo spustíme nadväzujúce prepočty závislého
				// stavu.
				runtimeStore.setInfoNotifications(1);
			}
		}
	}

	/**
	 * Vykoná jednorazovú migráciu používateľských dát súvisiacu so zmenou upgradu. Skopíruje aktuálnu úroveň
	 * stewardship do regent a uloží príznak, aby sa migrácia pri ďalšom spustení neopakovala.
	 *
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private upgrade6NerfFix(): void {
		// Ak platí podmienka `useUserStore().upgrade6NerfFixedv2`, vykonáme ochrannú vetvu a aktuálna metóda
		// alebo callback sa následne ukončí.
		if (useUserStore().upgrade6NerfFixedv2) return;
		const stewardshipLevel = useUpgradesStore().getCurrentLevelById(
			UpgradeIds.stewardship,
		);
		// Zapíšeme výsledok operácie do centrálneho store alebo spustíme nadväzujúce prepočty závislého stavu.
		useUpgradesStore().setUpgradeLevelById(
			UpgradeIds.regent,
			stewardshipLevel,
		);
		// Zapíšeme výsledok operácie do centrálneho store alebo spustíme nadväzujúce prepočty závislého stavu.
		useUserStore().setUpgrade6NerfFixedv2(true);
	}

	/**
	 * Spustí alebo obnoví story/tutorial režim podľa aktuálneho tutorialového kroku. Pri prvom kroku otvorí
	 * úvodný príbeh, pri pokračovaní obnoví kameru a indikátory a mimo tutorialu iba spustí herný ticker, ak
	 * nie je zamknutý.
	 *
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private initStoryMode(): void {
		// eslint-disable-next-line no-console
		console.log("MainMapScene: initStoryMode");

		// Check if the app is the first time started.
		const questStore = useQuestStore();
		//const userStore = useUserStore();

		// Skontrolujeme, či je hráč v tutoriáli a či zatiaľ nemá priradenú skupinu
		/*if (questStore.tutorialActive && !userStore.abTestGroup) {
			const currentTimestamp = Date.now();
			// Ak je čas párny (zvyšok po delení dvomi je 0), priradíme "A", inak "B"
			const group = currentTimestamp % 2 === 0 ? "A" : "B";
			userStore.setAbTestGroup(group);
		}*/

		if (questStore.tutorialActive && questStore.tutorialStep === 0) {
			// Zapíšeme výsledok operácie do centrálneho store alebo spustíme nadväzujúce prepočty závislého
			// stavu.
			questStore.setTutorialStep(1);
			// Aktualizujeme prehrávanie alebo hlasitosť zvuku podľa výsledku aktuálnej hernej operácie.
			SoundService.getInstance().playIntroMusic();
			// Emitujeme udalosť do aplikačného event busu, aby sa o zmene dozvedeli ostatné moduly.
			useEmitter().emit(EmitterEvents.TutorialStart);
			// Otvárame príslušnú UI route alebo popup a odovzdávame mu potrebný herný kontext.
			router.push({
				name: RouteNames.STORY,
				params: { chapter: "1", id: "1" },
			});
			// Ak predchádzajúca podmienka neplatí, overíme alternatívu `questStore.tutorialActive &&
			// questStore.tutorialStep !== 0`.
		} else if (questStore.tutorialActive && questStore.tutorialStep !== 0) {
			// Resume tutorial at the correct step
			useEmitter().emit(EmitterEvents.TutorialStart);
			// Emitujeme udalosť do aplikačného event busu, aby sa o zmene dozvedeli ostatné moduly.
			useEmitter().emit(EmitterEvents.PhaserAnimateIntroCamera);

			// Resume tutorial indicator/highlight/label
			this.handleTutorialSteps(questStore.tutorialStep);

			const ticker = useGameTicker();
			// Ak platí podmienka `!ticker.isLocked`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú
			// príslušný stav.
			if (!ticker.isLocked) ticker.start();
			// Aktualizujeme prehrávanie alebo hlasitosť zvuku podľa výsledku aktuálnej hernej operácie.
			SoundService.getInstance().playMainMusic();
			// Aktualizujeme prehrávanie alebo hlasitosť zvuku podľa výsledku aktuálnej hernej operácie.
			SoundService.getInstance().playCityAmbient();
		} else {
			// Ak predchádzajúca podmienka neplatí, vykonáme alternatívnu vetvu.
			const ticker = useGameTicker();
			// Ak platí podmienka `!ticker.isLocked`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú
			// príslušný stav.
			if (!ticker.isLocked) ticker.start();
		}
	}

	/**
	 * Zaregistruje všetky event listenery, ktorými MainMapScene komunikuje s UI, store vrstvou a hernými
	 * repository. Callbacky pokrývajú výber a prekreslenie tiles, audio, toast správy, tutorial, truhlice,
	 * questy, rozšírenia mapy, animácie, progres, tick udalosti a ďalšie runtime zmeny.
	 *
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	initEmitters(): void {
		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(EmitterEvents.GameInitStoryMode, (): void => {
			// Ak platí podmienka `!FLAG_DISABLE_TUTORIAL`, vykonáme nasledujúcu vetvu a jej operácie
			// aktualizujú príslušný stav.
			if (!FLAG_DISABLE_TUTORIAL) this.initStoryMode();
		});

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(EmitterEvents.PhaserLoadEventMapAssets, (): void => {
			void ensureEventMapAssetsLoaded(this);
		});

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(EmitterEvents.PhaserLoadAudio, (): void => {
			this.loadSoundAndMusicAssetsPromise();
		});

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(
			EmitterEvents.PhaserTileSelect,
			({ x, y }: { x: number; y: number }): void => {
				const tile = this.tilerepo.getTileEntityFactoriesByTileIndex(
					this.tilerepo.getTileIndexByTileCoordinates({
						x,
						y,
					}),
				)[0]();
				// Ak platí podmienka `!tile`, vykonáme ochrannú vetvu a aktuálna metóda alebo callback sa
				// následne ukončí.
				if (!tile) return;
				this.selectTile(tile);
			},
		);

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(EmitterEvents.PhaserTileDeselect, (): void => {
			this.deselectTile();
		});

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(EmitterEvents.PhaserGoToCapital, (): void => {
			this.goToCapital();
		});

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(EmitterEvents.PhaserMakeScreenshot, (): void => {
			this.makeScreenshot();
		});

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(EmitterEvents.PingSpecificTile, ({ x, y }): void => {
			this.showClickPulse(x, y);
		});

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(EmitterEvents.PhaserAnimateIntroCamera, (): void => {
			this.animateIntroCamera();
		});

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(
			EmitterEvents.PhaserPlayAudio,
			({ spriteName, audioName, options }: AudioEmitterData): void => {
				this.playAudio(spriteName, audioName!, options);
			},
		);

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(
			EmitterEvents.PhaserStopAudio,
			({ spriteName }: AudioEmitterData): void => {
				this.stopAudio(spriteName);
			},
		);

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(EmitterEvents.PhaserPlayMainAudio, (): void => {
			this.playMainMusic();
		});

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(EmitterEvents.PhaserPlayCityAmbient, (): void => {
			this.playCityAmbient();
		});

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(EmitterEvents.PhaserPlayIntroAudio, (): void => {
			this.playIntroMusic();
		});

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(EmitterEvents.PhaserStopIntroAudio, (): void => {
			this.stopIntroMusic();
		});

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(EmitterEvents.PhaserChangeMainMusicVolume, (): void => {
			this.changeMainMusicVolume();
		});

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(EmitterEvents.PhaserStopMainAudio, (): void => {
			this.stopMainAudio();
		});

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(EmitterEvents.PhaserStopCityAmbient, (): void => {
			this.stopCityAmbient();
		});

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(EmitterEvents.UnpingSpecificTile, ({ x, y }): void => {
			this.deleteGraphicsObjects(x, y);
		});

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(
			EmitterEvents.PhaserPutTileAt,
			({ coordinates, fade }: PhaserPutTileAtPayload): void => {
				// Ak platí podmienka `fade && useSettingsStore().animationsOn`, vykonáme ochrannú vetvu a
				// aktuálna metóda alebo callback sa následne ukončí.
				if (fade && useSettingsStore().animationsOn) {
					this.animateFade(coordinates);
					return;
				}

				const tileIndex =
					this.tilerepo.getTileIndexByTileCoordinates(coordinates);
				// Po zmene herného stavu znovu synchronizujeme vizuálnu reprezentáciu dotknutého tile.
				this.rerenderTile(tileIndex);
			},
		);

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(
			EmitterEvents.PhaserTileClick,
			({
				x,
				y,
				reward,
				messageTop,
				messageScale,
				messageNoAnim,
				placement,
				randomX,
				isSpecialChest,
				messageNumberOffset = 0,
			}: PhaserTileClickPayload): void => {
				// Ak platí podmienka `reward`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú príslušný
				// stav.
				if (reward) {
					let index = 0;
					// Prejdeme všetky hodnoty z `Object.entries(reward)`; v každej iterácii spracujeme `const
					// [icon, income]`.
					for (const [icon, income] of Object.entries(reward)) {
						index++;

						const toast = this.toastPool.getForVariant(
							TileToastType.INCOME,
							Object.keys(reward).length <= 5
								? 5
								: Object.keys(reward).length,
						);
						const upgradeBonus =
							1 +
							getUpgradeCurrentReplaceValueById(
								UpgradeIds.inflation,
							);
						const incomeMutated =
							icon === ResourceType.COINS && isSpecialChest
								? ceilNumber(income * upgradeBonus)
								: income;

						toast.showToast(
							incomeMutated,
							{
								messageType: TileToastType.INCOME,
								position: {
									x,
									y,
								},
								iconKey: `icon-${icon.toLowerCase()}`,
								messageNumber: index + messageNumberOffset,
								topCorrection: messageTop ?? 450,
								messageNoAnim,
								messageScale,
								placement,
								randomX: randomX,
							},
							this.cameras.main.zoom,
						);
						// Aktualizujeme prehrávanie alebo hlasitosť zvuku podľa výsledku aktuálnej hernej
						// operácie.
						SoundService.getInstance().playSettlementIncome();
					}
				}
			},
		);

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(
			EmitterEvents.PhaserSettlementCastleClick,
			({ x, y }): void => {
				const tileIndex = this.tilerepo.getTileIndexByTileCoordinates({
					x,
					y,
				});
				// Ak platí podmienka `!tileIndex`, vykonáme ochrannú vetvu a aktuálna metóda alebo callback sa
				// následne ukončí.
				if (!tileIndex) return;

				const labelKey = TileLabel.formatKey(
					TileLabelSkin.LEVEL,
					tileIndex,
				);
				// Ak platí podmienka `this.animationTweensCache.has(labelKey)`, vykonáme nasledujúcu vetvu a
				// jej operácie aktualizujú príslušný stav.
				if (this.animationTweensCache.has(labelKey)) {
					this.animationTweensCache.get(labelKey)?.complete();
					this.animationTweensCache.delete(labelKey);
				}

				const label = this.tileLabelsCache.get(labelKey);
				// Ak platí podmienka `!label?.container`, vykonáme ochrannú vetvu a aktuálna metóda alebo
				// callback sa následne ukončí.
				if (!label?.container) return;

				const originalScale = label.container.scale;
				label.container.setScale(originalScale * CLICK_SCALE);

				// Vytvoríme tween, ktorý plynulo mení uvedené vlastnosti cieľového objektu a po dokončení
				// vykoná cleanup callback.
				const tween = this.tweens.add({
					targets: label.container,
					scale: originalScale,
					duration: CLICK_ANIMATION_DURATION,
					ease: "Sine.easeOut",
					onComplete: (): void => {
						this.animationTweensCache.delete(labelKey);
						label.container?.setScale(originalScale);
					},
				});
				this.animationTweensCache.set(labelKey, tween);
			},
		);

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(
			EmitterEvents.PhaserTileUpdateProgressBar,
			({ x, y }: PhaserTileUpdateProgressBarPayload): void => {
				this.updateTileProgressBar({
					x,
					y,
				});
			},
		);

		this.emitter.on(
			EmitterEvents.PhaserRemoveProgressBar,
			({ x, y }: PhaserRemoveProgressBarPayload): void => {
				this.removeProgressBar(
					this.tilerepo.getTileIndexByTileCoordinates({
						x,
						y,
					}),
				);
			},
		);

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(EmitterEvents.TutorialStart, (): void => {
			// Tutorial krok: odhalenie tutorialu
			this.handleTutorialSteps(1);

			// Tutorial krok: postav cesty a spoj dve mesta
			this.handleTutorialSteps(6);
		});

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(EmitterEvents.TranslationsChanged, (): void => {
			const tileKeys = this.handleInactiveTutorialTileLabels();
			// Prejdeme každú položku z kolekcie `tileKeys` a nad každou vykonáme callback.
			tileKeys.forEach((key: string): void => {
				this.createTutorialUpgradeTownLabel(key);
			});
		});

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(EmitterEvents.TutorialEnd, (): void => {
			this.handleSettlementCtaMapLabel();
			// this.destroyTutorialParticles();
			// this.destroyCloudParticles();
		});

		//TODO toto je user dependent hodnota, default je 100 a navysuje sa upgradami atd
		this.emitter.on(
			EmitterEvents.PhaserRecalculateExplorationSpeed,
			(): void => {
				this.updateExplorationSpeed();
			},
		);

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(
			EmitterEvents.PhaserAddChestToMap,
			(chest: Chest): void => {
				this.addChestToMap(chest);
			},
		);

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(
			EmitterEvents.PhaserAddDarkChestToMap,
			(chest: Chest): void => {
				this.addDarkChestToMap(chest);
			},
		);

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(
			EmitterEvents.PhaserRemoveChestFromMap,
			(chest: Chest): void => {
				this.removeChest(chest);
				this.exploreClaimAnimation(chest.x, chest.y, true, true);
			},
		);

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(
			EmitterEvents.PhaserTileFogAdded,
			({ x, y, fade }: PhaserTileFogAddedPayload): void => {
				/// Check if there is a chest on coordinates if yes
				const chests = this.chests.filter(
					(chest: ChestTile): boolean => {
						return chest.x === x && chest.y === y;
					},
				);

				// Ak platí podmienka `chests.length > 0`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú
				// príslušný stav.
				if (chests.length > 0) {
					// Prejdeme každú položku z kolekcie `chests` a nad každou vykonáme callback.
					chests.forEach((chest: ChestTile): void => {
						// Remove the chest image and change for normal
						this.swapChest(chest.x, chest.y);
					});
				}

				const expansionDirection =
					!FLAG_REVEAL_MAP &&
					this.getExpansionRegionArrowByTileIndex(
						this.tilerepo.getTileIndexByTileCoordinates({ x, y }),
					);
				// Ak platí podmienka `expansionDirection`, vykonáme nasledujúcu vetvu a jej operácie
				// aktualizujú príslušný stav.
				if (expansionDirection) {
					const skin =
						`expansionArrow${capitalize(expansionDirection)}` as TileLabelSkin;
					// Ak platí podmienka `!this.hasTileLabel({ x, y }, skin)`, vykonáme nasledujúcu vetvu a jej
					// operácie aktualizujú príslušný stav.
					if (!this.hasTileLabel({ x, y }, skin)) {
						// Synchronizujeme sprievodný vizuálny prvok s aktuálnym stavom tile alebo entity.
						this.updateTileLabel(
							{
								x,
								y,
							},
							skin,
							TileLabelAction.CREATE,
						);

						this.updateTileLabels();
					}
					// Ak predchádzajúca podmienka neplatí, overíme alternatívu `fade`.
				} else if (fade) {
					this.animateFade({ x, y });
				}
			},
		);

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(EmitterEvents.PhaserActiveDebug, (): void => {
			this.activeDebug();
		});

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(
			EmitterEvents.PhaserRenderDisconnect,
			(
				disconnectedTilesCoordinates: PhaserDisconnectedTilesPayload,
			): void => {
				this.highlightTilesRed(disconnectedTilesCoordinates);
			},
		);

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(
			EmitterEvents.PhaserRenderBlockedByEnemy,
			(blockedByEnemyPayload: PhaserBlockedByEnemyPayload): void => {
				this.highlightTilesRed(blockedByEnemyPayload);
			},
		);

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(
			EmitterEvents.PhaserAddRangeSelector,
			({ x, y }: TileCoordinates): void => {
				this.addRangeSelector(x, y);
			},
		);

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(EmitterEvents.PhaserRemoveRangeSelector, (): void => {
			this.removeRangeSelector();
		});

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(
			EmitterEvents.PhaserUnlockRegion,
			({ id }: PhaserUnlockRegionPayload): void => {
				this.unlockMapExpansionById(id);
				this.rerenderMapExpansionBorderTiles();
			},
		);

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(
			EmitterEvents.PhaserToggleDragging,
			(state: boolean): void => {
				// Aktualizujeme interný stav scény podľa výsledku aktuálnej operácie: `this.isDragging =
				// state;`
				this.isDragging = state;
				// @ts-expect-error - missing types
				this.cameraController?.panScroll.pan.setEnable(state);
			},
		);

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(
			EmitterEvents.PhaserNotEnoughResource,
			({ x, y, resource }: PhaserTileClickResource): void => {
				const toast = this.toastPool.getForVariant(
					TileToastType.WARNING,
				);
				const resourceText =
					resource.toLowerCase() === "eventium"
						? "event.not_enough_resources"
						: `ui.not_enough_${resource.toLowerCase()}`;
				toast.showToast(
					translate(resourceText),
					{
						messageType: TileToastType.WARNING,
						position: {
							x,
							y,
						},
					},
					this.cameras.main.zoom,
				);
			},
		);
		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(
			EmitterEvents.PhaserWarningMessage,
			({
				x,
				y,
				text,
				messageTime,
				messageNumber,
				replace,
			}: PhaserTileClickText): void => {
				const toast = this.toastPool.getForVariant(
					TileToastType.WARNING,
				);
				let messageText = translate(text);
				// Ak platí podmienka `replace`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú príslušný
				// stav.
				if (replace) {
					messageText = messageText.replace(
						replace.placeholder,
						replace.value,
					);
				}
				toast.showToast(
					messageText,
					{
						messageType: TileToastType.WARNING,
						customMessageTime: messageTime,
						messageNumber: messageNumber ?? 0,

						position: {
							x,
							y,
						},
					},
					this.cameras.main.zoom,
				);
			},
		);
		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(
			EmitterEvents.PhaserSuccessMessage,
			({
				x,
				y,
				text,
				topCorrection,
				messageNoAnim,
			}: PhaserTileClickText): void => {
				const toast = this.toastPool.getForVariant(
					TileToastType.SUCCESS,
				);
				toast.showToast(
					translate(text),
					{
						messageType: TileToastType.SUCCESS,
						topCorrection,
						messageNoAnim,
						position: {
							x,
							y,
						},
					},
					this.cameras.main.zoom,
				);
			},
		);
		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(
			EmitterEvents.PhaserNeutralMessage,
			({
				x,
				y,
				text,
				topCorrection,
				messageNoAnim,
				ignoreBounds,
			}: PhaserTileClickText): void => {
				const toast = this.toastPool.getForVariant(
					TileToastType.NEUTRAL,
					5 + 10, // +10 for siege system
				);
				toast.showToast(
					text,
					{
						messageType: TileToastType.NEUTRAL,
						topCorrection,
						messageNoAnim,
						position: {
							x,
							y,
						},
						ignoreBounds,
					},
					this.cameras.main.zoom,
				);
			},
		);
		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(
			EmitterEvents.PhaserTextMessage,
			({ text, type }: PhaserMessageText): void => {
				const toast = this.toastPool.getForVariant(type);
				toast.showToast(
					translate(text),
					{
						messageType: type,
						placement: ToastPlacement.BOTTOM_CENTER,
					},
					this.cameras.main.zoom,
				);
			},
		);
		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(
			EmitterEvents.PhaserUpdateTileLabel,
			({
				coordinates,
				skin,
				action,
				type,
				scaleToZoom,
			}: PhaserUpdateTileLabelPayload): void => {
				// Synchronizujeme sprievodný vizuálny prvok s aktuálnym stavom tile alebo entity.
				this.updateTileLabel(
					coordinates,
					skin,
					action,
					type,
					"",
					scaleToZoom,
				);

				this.updateTileLabels();
			},
		);

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(
			EmitterEvents.PhaserTileLabelClick,
			({
				tile,
				callback = (): void => {},
			}: PhaserTileLabelClickPayload): void => {
				clickedTileEntityCache = tile;
				// Aktualizujeme interný stav scény podľa výsledku aktuálnej operácie:
				// `this.tileLabelPointerDown = true;`
				this.tileLabelPointerDown = true;
				// Ak platí podmienka `this.isDragging`, vykonáme ochrannú vetvu a aktuálna metóda alebo
				// callback sa následne ukončí.
				if (this.isDragging) return;
				callback();
			},
		);

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(EmitterEvents.TileRepositoryUpgrade, (): void => {
			//clickedTileEntityCache = null;
			this.handleSettlementCtaMapLabel(true);
		});

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(EmitterEvents.SettlementQuestUpdate, (): void => {
			this.handleSettlementCtaMapLabel(true);
		});

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(
			EmitterEvents.PhaserUpgradeAnimation,
			({ x, y }: TileCoordinates): void => {
				this.upgradeAnimation(x, y);
			},
		);

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(EmitterEvents.PhaserCreateGemsBadge, (): void => {
			// Ak platí podmienka `!useUserStore().gemMinesClaimable`, vykonáme ochrannú vetvu a aktuálna metóda
			// alebo callback sa následne ukončí.
			if (!useUserStore().gemMinesClaimable) return;

			// Synchronizujeme sprievodný vizuálny prvok s aktuálnym stavom tile alebo entity.
			this.updateTileLabel(
				{
					x: SAFE_COORDINATE_OUTSIDE_MAP,
					y: SAFE_COORDINATE_OUTSIDE_MAP,
				},
				TileLabelSkin.GEMS_BADGE,
				TileLabelAction.DELETE_ALL,
			);
			// Prejdeme každú položku z výsledku predchádzajúceho reťazca operácií a nad každou vykonáme
			// callback.
			this.tilerepo
				.getAllTileEntitesByFrameNames(new Set([FrameName.GEMS_MINE]))
				.forEach((entity: TileEntity): void => {
					const { x, y } = entity;
					// Synchronizujeme sprievodný vizuálny prvok s aktuálnym stavom tile alebo entity.
					this.updateTileLabel(
						{ x, y },
						TileLabelSkin.GEMS_BADGE,
						TileLabelAction.CREATE,
					);
				});

			this.updateTileLabels();
		});

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(
			EmitterEvents.PhaserDestroyGemsBadge,
			({ x, y }: TileCoordinates): void => {
				// Synchronizujeme sprievodný vizuálny prvok s aktuálnym stavom tile alebo entity.
				this.updateTileLabel(
					{ x, y },
					TileLabelSkin.GEMS_BADGE,
					TileLabelAction.DELETE_ALL,
				);

				this.updateTileLabels();
			},
		);

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(
			EmitterEvents.PhaserRevealRange,
			({ x, y, range }: PhaserRevealRangePayload): void => {
				// Opakujeme blok s počítadlom `let i = -range`, kým platí `i <= range`; po iterácii vykonáme
				// `i++`.
				for (let i = -range; i <= range; i++) {
					// Opakujeme blok s počítadlom `let j = -range`, kým platí `j <= range`; po iterácii
					// vykonáme `j++`.
					for (let j = -range; j <= range; j++) {
						const tileIndex =
							this.tilerepo.getTileIndexByTileCoordinates({
								x: x + i,
								y: y + j,
							});
						// Ak platí podmienka `this.tilerepo.getParam( tileIndex, TileEntityParam.VISIBILITY, )
						// !== TileVisibilityType.BLACK`, preskočíme zostávajúce spracovanie aktuálnej iterácie
						// cyklu.
						if (
							this.tilerepo.getParam(
								tileIndex,
								TileEntityParam.VISIBILITY,
							) !== TileVisibilityType.BLACK
						)
							continue;

						this.tilerepo.setParam(
							tileIndex,
							TileEntityParam.VISIBILITY,
							TileVisibilityType.FOG,
						);
						// Po zmene herného stavu znovu synchronizujeme vizuálnu reprezentáciu dotknutého tile.
						this.rerenderTile(tileIndex);
					}
				}
			},
		);

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(EmitterEvents.GameShowInactivePopup, (): void => {
			this.turnOffAutoTapping();
		});
		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(
			EmitterEvents.GameShowConnectionErrorPopup,
			(): void => {
				this.turnOffAutoTapping();
			},
		);

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(EmitterEvents.LanguageChanged, (): void => {
			this.initToastPool();
		});

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(
			EmitterEvents.PhaserBuildAnimation,
			({ x, y }: TileCoordinates): void => {
				this.buildAnimation(x, y);
			},
		);

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(
			EmitterEvents.PhaserDemolishAnimation,
			({ x, y }: TileCoordinates): void => {
				this.demolishAnimation(x, y);
			},
		);

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(
			EmitterEvents.PhaserSettlementAnimation,
			({ x, y, value }: TileCoordinatesWithValue): void => {
				this.exploreClaimAnimation(x, y, value, true);
			},
		);

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(
			EmitterEvents.PlagueProgressUpdate,
			({
				x,
				y,
				currentHp,
				maxHp,
				heal,
			}: PlagueProgressUpdatePayload): void => {
				// Ak platí podmienka `currentHp <= 0`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú
				// príslušný stav.
				if (currentHp <= 0) {
					this.removeProgressBar(
						this.tilerepo.getTileIndexByTileCoordinates({ x, y }),
					);
				} else {
					// Ak predchádzajúca podmienka neplatí, vykonáme alternatívnu vetvu.
					// Synchronizujeme sprievodný vizuálny prvok s aktuálnym stavom tile alebo entity.
					this.updateProgressBar(x, y, maxHp, currentHp, {
						plague: true,
					});
					// Ak platí podmienka `!heal`, vykonáme ochrannú vetvu a aktuálna metóda alebo callback sa
					// následne ukončí.
					if (!heal) return;
					const toast = this.toastPool.getForVariant(
						TileToastType.NEUTRAL,
					);
					toast.showToast(
						`${currentHp}/${maxHp}`,
						{
							messageType: TileToastType.NEUTRAL,
							position: {
								x,
								y,
							},
						},
						this.cameras.main.zoom,
					);
				}
			},
		);

		this.emitter.on(
			EmitterEvents.SiegeSystemProgressUpdate,
			({ x, y }: SiegeSystemProgressUpdatePayload): void => {
				this.updateFriendlyUnitProgressBar({ x, y });
			},
		);

		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(EmitterEvents.GameTick, (): void => {
			const cam = this.cameras.main;
			const cameraZoom = cam.zoom;
			const runtimeStore = useRuntimeStore();
			this.updateProgressBars();

			// Ak platí podmienka `cameraZoom > 0.25`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú
			// príslušný stav.
			if (cameraZoom > 0.25) {
				const { TILE_SIZE } = this.tilerepo;

				// Get the viewport bounds in world coordinates
				const viewportLeft = cam.worldView.x;
				const viewportTop = cam.worldView.y;
				const viewportRight = viewportLeft + cam.worldView.width;
				const viewportBottom = viewportTop + cam.worldView.height;

				// Convert to tile coordinates
				const tileLeft = ceilNumber(viewportLeft / TILE_SIZE);
				const tileTop = ceilNumber(viewportTop / TILE_SIZE);
				const tileRight = floorNumber(viewportRight / TILE_SIZE);
				const tileBottom = floorNumber(viewportBottom / TILE_SIZE);

				let rewardIndex = 0;

				// Opakujeme blok s počítadlom `let x = tileLeft`, kým platí `x <= tileRight`; po iterácii
				// vykonáme `x++`.
				for (let x = tileLeft; x <= tileRight; x++) {
					// Opakujeme blok s počítadlom `let y = tileTop`, kým platí `y <= tileBottom`; po iterácii
					// vykonáme `y++`.
					for (let y = tileTop; y <= tileBottom; y++) {
						const entity =
							this.tilerepo.getTileEntityByTileCoordinates({
								x,
								y,
							});
						// Ak platí podmienka `entity && tilerepoSettlementCastleBuildingsFrameNames.has(
						// entity?.frameName, ) && entity.isConnectedToMainSettlement && ent…`, vykonáme
						// nasledujúcu vetvu a jej operácie aktualizujú príslušný stav.
						if (
							entity &&
							tilerepoSettlementCastleBuildingsFrameNames.has(
								entity?.frameName,
							) &&
							entity.isConnectedToMainSettlement &&
							entity.production.has(ResourceType.COINS_INCOME) &&
							typeof entity.production.get(
								ResourceType.COINS_INCOME,
							) === "number" &&
							(entity.production.get(
								ResourceType.COINS_INCOME,
							) as number) > 0
						) {
							rewardIndex++;

							const toast = this.toastPool.getForVariant(
								TileToastType.INCOME,
								rewardIndex,
							);

							let toastMessage = entity.production.get(
								ResourceType.COINS_INCOME,
							) as number;
							// Ak platí podmienka `runtimeStore.incomeBoostActive`, vykonáme nasledujúcu vetvu a
							// jej operácie aktualizujú príslušný stav.
							if (runtimeStore.incomeBoostActive) {
								toastMessage = floorNumber(
									toastMessage * INCOME_BOOST_COEFFICIENT,
								);
							}
							toast.showToast(
								toastMessage,
								{
									position: { x: entity.x, y: entity.y },
									messageType: TileToastType.INCOME,
									iconKey: "icon-coins",
								},
								cameraZoom,
							);
						}
					}
				}
			} else {
				// Ak predchádzajúca podmienka neplatí, vykonáme alternatívnu vetvu.
				const toast = this.toastPool.getForVariant(
					TileToastType.INCOME,
				);
				let toastMessage = useResourcesStore().coinsIncome;
				// Ak platí podmienka `runtimeStore.incomeBoostActive`, vykonáme nasledujúcu vetvu a jej
				// operácie aktualizujú príslušný stav.
				if (runtimeStore.incomeBoostActive) {
					toastMessage = floorNumber(
						toastMessage * INCOME_BOOST_COEFFICIENT,
					);
				}
				toast.showToast(
					toastMessage,
					{
						messageType: TileToastType.INCOME,
						iconKey: "icon-coins",
						placement: ToastPlacement.TOP_CENTER,
					},
					cameraZoom,
				);
			}
		});
		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(EmitterEvents.NewSpecialLocation, (): void => {
			this.generateNewSpecialLocation();
		});
		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(
			EmitterEvents.PhaserEventiumIncome,
			({ x, y, reward }): void => {
				const toast = this.toastPool.getForVariant(
					TileToastType.INCOME,
				);
				const toastMessage =
					(reward as Partial<Record<ResourceType, number>>)?.[
						ResourceType.EVENTIUM
					] ?? 0;

				toast.showToast(
					toastMessage,
					{
						messageType: TileToastType.INCOME,
						iconKey: `icon-${useEventStore().attributes.type}-eventium`,
						position: {
							x: x,
							y: y,
						},
					},
					this.cameras.main.zoom,
				);
			},
		);
		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(
			EmitterEvents.OfflineIncome,
			({ reward }: IncomeReward): void => {
				let index = 0;
				// Prejdeme všetky hodnoty z `Object.entries(reward)`; v každej iterácii spracujeme `const
				// [icon, income]`.
				for (const [icon, income] of Object.entries(reward)) {
					index++;

					const toast = this.toastPool.getForVariant(
						TileToastType.INCOME,
						Object.keys(reward).length <= 5
							? 5
							: Object.keys(reward).length,
					);
					toast.showToast(
						income,
						{
							messageType: TileToastType.OFFLINE,
							position: {
								x: 0,
								y: 0,
							},
							iconKey: `icon-${icon.toLowerCase()}`,
							messageNumber: index,
							placement: ToastPlacement.TOP_CENTER,
							customMessageTime: 3000,
						},
						this.cameras.main.zoom,
					);
					// Aktualizujeme prehrávanie alebo hlasitosť zvuku podľa výsledku aktuálnej hernej operácie.
					SoundService.getInstance().playSettlementIncome();
				}
			},
		);
		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(EmitterEvents.SoundVolumeUpdated, (): void => {
			this.updateAmbientSounds();
		});
		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(EmitterEvents.PhaserHighlightSettlements, (): void => {
			// Aktualizujeme polohu, zoom alebo hranice kamery podľa aktuálneho herného kontextu.
			this.camera?.zoomTo(0.15, 3000, "Sine.easeInOut");
		});
		// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom scény.
		this.emitter.on(
			EmitterEvents.PhaserSettlementSwitch,
			(settlement: TileEntity): void => {
				const { TILE_SIZE } = this.tilerepo;

				// Aktualizujeme polohu, zoom alebo hranice kamery podľa aktuálneho herného kontextu.
				this.cameras.main.pan(
					settlement.x * TILE_SIZE,
					settlement.y * TILE_SIZE,
					250,
					"Power2",
				);
				// Emitujeme udalosť do aplikačného event busu, aby sa o zmene dozvedeli ostatné moduly.
				this.emitter.emit(
					EmitterEvents.PhaserTileDetailOpen,
					settlement,
				);
			},
		);
	}

	/**
	 * Vytvorí novú inštanciu poolu toast správ podľa aktuálneho jazyka. Pri jazykoch nepodporovaných bitmapovým
	 * fontom vypne príslušnú bitmapovú textovú cestu.
	 *
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private initToastPool(): void {
		const userStore = useUserStore();

		// Aktualizujeme interný stav scény podľa výsledku aktuálnej operácie: `this.toastPool = new
		// ToastMessagePool( this, 1, !UNSUPPORTED_BITMAP_LANGUAGES.includes(userStore.language), );`
		this.toastPool = new ToastMessagePool(
			this,
			1,
			!UNSUPPORTED_BITMAP_LANGUAGES.includes(userStore.language),
		);
	}

	/**
	 * Odvodí interný názov atlasu z URL manifestu pomocou regulárnych výrazov pre tilesety a animácie. Výsledok
	 * uloží do cache, aby sa rovnaká URL pri ďalšom použití znova neparsovala.
	 *
	 * @param atlasUrl URL JSON manifestu atlasu, z ktorého sa má odvodiť interný názov.
	 * @returns Interný texture key atlasu; pri URL, ktorá nezodpovedá podporovanému názvu, prázdny reťazec.
	 */
	private getAtlasNameByAtlasUrl(atlasUrl: string): string {
		// Ak platí podmienka `!this.atlasUrlToAtlasNamesCache.has(atlasUrl)`, vykonáme nasledujúcu vetvu a jej
		// operácie aktualizujú príslušný stav.
		if (!this.atlasUrlToAtlasNamesCache.has(atlasUrl)) {
			const name =
				atlasUrl.match(TILESET_NAME_REGEXP)?.groups?.name ??
				atlasUrl.match(ANIMATIONS_NAME_REGEXP)?.groups?.name ??
				"";
			this.atlasUrlToAtlasNamesCache.set(atlasUrl, name);
		}
		return this.atlasUrlToAtlasNamesCache.get(atlasUrl)!;
	}

	/**
	 * Prevedie zoznam atlasových URL na konfigurácie akceptované Phaser texture loaderom. Pre zariadenia
	 * podporujúce ASTC vyberie PVR multiatlas, inak vytvorí konfiguráciu bežného obrazového multiatlasu.
	 *
	 * @returns Pole konfigurácií vhodných pre Phaser texture loader.
	 */
	private getAtlasConfig(): Array<TextureConfig> {
		const allAtlasUrls = this.getAllAtlasUrls();
		// Transformujeme položky z kolekcie `allAtlasUrls` na nové hodnoty a vytvoríme výsledné pole.
		return allAtlasUrls.map(
			(atlasUrl: string): TextureConfig =>
				isAstcSupported()
					? {
							name: this.getAtlasNameByAtlasUrl(atlasUrl),
							data: {
								astc: {
									type: "PVR",
									multiAtlasURL: atlasUrl + "?v2",
								},
							},
						}
					: {
							name: this.getAtlasNameByAtlasUrl(atlasUrl),
							data: {
								img: {
									multiAtlasURL: atlasUrl + "?v2",
								},
							},
						},
		);
	}

	/**
	 * Prejde načítané mapové atlasy v stabilnom renderovacom poradí a znovu vytvorí všetky lookup cache. Pre
	 * každý frame uloží jeho atlas, logickú depth vrstvu a prípadne ho označí ako overlap frame s príponou
	 * _Top.png.
	 */
	private createAtlasFrameNamesCache(): void {
		//táto metóda sa volá iba 1x a to pri úvodnom zsotavení mapy/scény
		//teda pre istotu sa vyčistia všetky cache štruktúry, ktoré naprieč inými
		//metódami používame na identifikáciu jendotlivých frameov
		this.frameNameToAtlasNameCache.clear();
		this.frameNameToLayerIndexCache.clear();
		this.frameNameToLayerCache.length = 0;
		this.overlapFrameNamesCache.clear();

		// Prechádzame všetky načítané textúry natiahnuté cez Phaser Loader - preload()
		Object.keys(this.textures.list)
			// Musime to mat zosortovane rovnako, ako mame v this.allAtlasUrls - teda 1000_squares, 1000, 2000_squares, 2000, ...
			.sort((a: string, b: string): number => {
				const numA = getNumber(a);
				const numB = getNumber(b);
				if (numA !== numB) return numA - numB;
				return a.endsWith("_square") ? -1 : 1;
			})
			.forEach((atlasName: string): void => {
				//preskocit interne phaser veci
				if (atlasName.startsWith("__")) return;
				//preskocit vsetko co neni tileset pre mapu - všetko čo nie je atals nás v tejto fáze nezaujíma(tileLabels atd.)
				if (!atlasName.startsWith("tileset_")) return;

				//pomocná štruktúra zoznamu našich finálynch framov
				const frameNameSet = new Set<FrameName>();
				// Pracujeme s textúrovými metadátami alebo GPU väzbou bez zbytočného kopírovania pixelových
				// dát.
				const { frames } = (
					this.textures.list as Record<
						string,
						{
							frames: Array<TexturePackerConfigTextureFrame>;
						}
					>
				)[atlasName];

				// Prechádzame už len predpsracované výsledné frames pre našu mapu
				(Object.keys(frames) as Array<FrameName>).forEach(
					(frameName: FrameName): void => {
						// Toto by sa nemalo stať ale pre istoru preskočíme interné Phaser textúry
						if (frameName.startsWith("__")) return;

						//v tejto chvíli sa jedná o validný frame a ukladáme si ho
						frameNameSet.add(frameName);
						//párujem frameName ku konkrétnemu atlasu
						this.frameNameToAtlasNameCache.set(
							frameName,
							atlasName,
						);
						//párujeme frameName ku konkrétnej "hĺbke" (poradie layeru)
						this.frameNameToLayerIndexCache.set(
							frameName,
							this.frameNameToLayerCache.length,
						);

						// Ak platí, že frameName obsahuje _Top.png, znamená to že ide do
						//špeciálnej cache pre "odresky"
						if (frameName.endsWith("_Top.png"))
							this.overlapFrameNamesCache.add(frameName);
					},
				);
				// nakoniec pridelíme výsledný zoznam frameNames do nasej cache
				this.frameNameToLayerCache.push(frameNameSet);
			});
	}

	/**
	 * Pripojí aktuálnu scénu k AudioLazyLoaderu, zaradí kritické audio assety a v prípade potreby spustí Phaser
	 * loader. Vrátený Promise sa splní po vykonaní inicializačného kroku; samotné ďalšie lazy audio sa môže
	 * načítať až pri použití.
	 *
	 * @returns Promise, ktorý sa splní po nakonfigurovaní lazy loadera a spustení prípadného kritického loadu.
	 */
	private async loadSoundAndMusicAssetsPromise(): Promise<void> {
		// Initialize lazy loader with scene reference
		const lazyLoader = AudioLazyLoader.getInstance();
		lazyLoader.setScene(this);

		return new Promise<void>((resolve): void => {
			const audios = this.loadCriticalAudioAssets();

			// Ak platí podmienka `audios.length > 0`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú
			// príslušný stav.
			if (audios.length > 0) {
				// Zaradíme požadovaný asset do Phaser loadera s konfiguráciou vhodnou pre jeho formát a spôsob
				// použitia.
				this.load.start();
			}

			resolve();
		});
	}

	/**
	 * Prehrá konkrétny zvuk z audio sprite s rešpektovaním stavu aplikácie a používateľských nastavení.
	 * Chýbajúci sprite najskôr lazy-loadne, nastaví hlasitosť a loop, po dokončení uvoľní inštanciu a emituje
	 * udalosť PhaserAudioFinished; chyby bezpečne zaloguje.
	 *
	 * @param spriteName Kľúč audio sprite v Phaser sound manageri a lazy loaderi.
	 * @param audioName Názov konkrétneho zvuku alebo cue, ktorý sa má v audio sprite prehrať.
	 * @param options Voliteľné nastavenia prehrávania; aktuálne podporujú najmä opakovanie pomocou loop.
	 * @returns Promise, ktorý sa splní po spustení zvuku alebo po bezpečnom ukončení preskočenej či chybovej
	 *          vetvy.
	 */
	private async playAudio(
		spriteName: AudioSpriteNames,
		audioName: UIAudioNames | CutscenesAudioNames | FX1AudioNames,
		options?: { loop?: boolean },
	): Promise<void> {
		// Don't play audio if app is in background
		if (SoundService.getInstance().isInBackground) return;

		const settings = useSettingsStore();
		// Ak platí podmienka `!settings.soundsOn`, vykonáme ochrannú vetvu a aktuálna metóda alebo callback sa
		// následne ukončí.
		if (!settings.soundsOn) return;

		// Lazy load cutscene audio sprites when needed
		const lazyLoader = AudioLazyLoader.getInstance();

		// Ak platí podmienka `!lazyLoader.isLoaded(spriteName)`, vykonáme nasledujúcu vetvu a jej operácie
		// aktualizujú príslušný stav.
		if (!lazyLoader.isLoaded(spriteName)) {
			await lazyLoader.loadAudio(spriteName);
		}

		// Rizikovú audio alebo runtime operáciu vykonáme v try bloku, aby prípadná chyba neukončila scénu.
		try {
			const volume = settings.soundsVolume / 100;
			// Aktualizujeme prehrávanie alebo hlasitosť zvuku podľa výsledku aktuálnej hernej operácie.
			const music = this.sound.addAudioSprite(spriteName);

			music.play(audioName, {
				loop: options?.loop ?? false,
				volume,
			});

			// Emitujeme udalosť do aplikačného event busu, aby sa o zmene dozvedeli ostatné moduly.
			music.on("complete", (): void => {
				// Uvoľníme už nepotrebný Phaser objekt alebo zdroj a odstránime súvisiacu referenciu, aby
				// nezostal v pamäti ani render liste.
				music.destroy();
				// Emitujeme udalosť do aplikačného event busu, aby sa o zmene dozvedeli ostatné moduly.
				this.emitter.emit(EmitterEvents.PhaserAudioFinished, audioName);
			});
		} catch (error: unknown) {
			// Pri výnimke vykonáme bezpečnostné logovanie, cleanup alebo retry namiesto prerušenia hry.
			console.error(
				`Failed to play audio: ${spriteName}/${audioName}`,
				error,
			);
		}
	}

	/**
	 * Overí používateľské nastavenie hudby a pri povolenom prehrávaní deleguje spustenie hlavnej hudby na
	 * internú metódu s retry logikou.
	 *
	 * @returns Promise bez dátovej návratovej hodnoty; signalizuje dokončenie pokusu o spustenie hudby.
	 */
	private async playMainMusic(): Promise<void> {
		const settings = useSettingsStore();
		// Ak platí podmienka `!settings.musicOn`, vykonáme ochrannú vetvu a aktuálna metóda alebo callback sa
		// následne ukončí.
		if (!settings.musicOn) return;

		this.playMainMusicInternal();
	}
	/**
	 * Overí používateľské nastavenie zvukov a pri povolenom prehrávaní deleguje spustenie mestského ambientu na
	 * internú metódu.
	 *
	 * @returns Promise bez dátovej návratovej hodnoty; signalizuje dokončenie pokusu o spustenie ambientu.
	 */
	private async playCityAmbient(): Promise<void> {
		const settings = useSettingsStore();
		// Ak platí podmienka `!settings.soundsOn`, vykonáme ochrannú vetvu a aktuálna metóda alebo callback sa
		// následne ukončí.
		if (!settings.soundsOn) return;

		this.playCityAmbientInternal();
	}

	/**
	 * Pokúsi sa spustiť hlavnú hudbu presne raz so správnou hlasitosťou a loop režimom. Ak AudioContext ešte
	 * nie je pripravený, odstráni prípadnú chybnú inštanciu a naplánuje opakovaný pokus.
	 *
	 * @returns True, ak hudba už hrala alebo sa ju podarilo spustiť; false, ak sa spustenie nepodarilo a bol
	 *          naplánovaný retry.
	 */
	private playMainMusicInternal(): boolean {
		const audioName = AudioNames.MAIN_MUSIC;
		const settings = useSettingsStore();

		// Aktualizujeme prehrávanie alebo hlasitosť zvuku podľa výsledku aktuálnej hernej operácie.
		const existingMusic = this.sound.get(audioName);

		// Prevent multiple instances of the same music
		if (existingMusic?.isPlaying) {
			return true;
		}

		// Rizikovú audio alebo runtime operáciu vykonáme v try bloku, aby prípadná chyba neukončila scénu.
		try {
			const volume = settings.musicVolume / 100;
			// Aktualizujeme prehrávanie alebo hlasitosť zvuku podľa výsledku aktuálnej hernej operácie.
			const started = this.sound.play(audioName, {
				loop: true,
				volume,
			});

			return started;
		} catch (error: unknown) {
			// Pri výnimke vykonáme bezpečnostné logovanie, cleanup alebo retry namiesto prerušenia hry.
			console.error(
				"Failed to play main music, retrying in 500ms...",
				error,
			);

			// AudioContext might not be ready - retry after delay
			setTimeout((): void => {
				// Clean up any existing music instance
				const existingMusic = this.sound.get(audioName);
				// Ak platí podmienka `existingMusic`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú
				// príslušný stav.
				if (existingMusic) {
					// Uvoľníme už nepotrebný Phaser objekt alebo zdroj a odstránime súvisiacu referenciu, aby
					// nezostal v pamäti ani render liste.
					this.sound.remove(existingMusic);
				}

				// Retry playback
				this.playMainMusicInternal();
			}, 500);

			return false;
		}
	}
	/**
	 * Pokúsi sa spustiť mestský ambient presne raz, nastaví jeho hlasitosť a po spustení ju prispôsobí polohe
	 * kamery. Pri chybe AudioContextu vyčistí starú inštanciu a naplánuje retry.
	 *
	 * @returns True, ak ambient už hral alebo sa ho podarilo spustiť; false, ak sa spustenie nepodarilo a bol
	 *          naplánovaný retry.
	 */
	private playCityAmbientInternal(): boolean {
		const audioName = AudioNames.CITY_AMBIENT;
		const settings = useSettingsStore();

		// Aktualizujeme prehrávanie alebo hlasitosť zvuku podľa výsledku aktuálnej hernej operácie.
		const existingMusic = this.sound.get(audioName);

		// Prevent multiple instances of the same music
		if (existingMusic?.isPlaying) {
			return true;
		}

		// Rizikovú audio alebo runtime operáciu vykonáme v try bloku, aby prípadná chyba neukončila scénu.
		try {
			const volume = settings.soundsVolume / 100;
			// Aktualizujeme prehrávanie alebo hlasitosť zvuku podľa výsledku aktuálnej hernej operácie.
			const started = this.sound.play(audioName, {
				loop: true,
				volume,
			});
			this.updateAmbientSounds();
			return started;
		} catch (error: unknown) {
			// Pri výnimke vykonáme bezpečnostné logovanie, cleanup alebo retry namiesto prerušenia hry.
			console.error(
				"Failed to play city ambient, retrying in 500ms...",
				error,
			);

			// AudioContext might not be ready - retry after delay
			setTimeout((): void => {
				// Clean up any existing music instance
				const existingMusic = this.sound.get(audioName);
				// Ak platí podmienka `existingMusic`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú
				// príslušný stav.
				if (existingMusic) {
					// Uvoľníme už nepotrebný Phaser objekt alebo zdroj a odstránime súvisiacu referenciu, aby
					// nezostal v pamäti ani render liste.
					this.sound.remove(existingMusic);
				}

				// Retry playback
				this.playCityAmbientInternal();
			}, 500);

			return false;
		}
	}

	/**
	 * Overí nastavenie hudby, podľa potreby lazy-loadne úvodnú hudbu a potom ju spustí internou metódou.
	 *
	 * @returns Promise, ktorý sa splní po načítaní a spustení intro hudby alebo po preskočení vypnutej hudby.
	 */
	private async playIntroMusic(): Promise<void> {
		const settings = useSettingsStore();
		// Ak platí podmienka `!settings.musicOn`, vykonáme ochrannú vetvu a aktuálna metóda alebo callback sa
		// následne ukončí.
		if (!settings.musicOn) return;

		// Ensure intro music is loaded (lazy loading if needed)
		const lazyLoader = AudioLazyLoader.getInstance();
		// Ak platí podmienka `!lazyLoader.isLoaded(AudioNames.INTRO_MUSIC)`, vykonáme nasledujúcu vetvu a jej
		// operácie aktualizujú príslušný stav.
		if (!lazyLoader.isLoaded(AudioNames.INTRO_MUSIC)) {
			await lazyLoader.loadAudio(AudioNames.INTRO_MUSIC);
		}

		await this.playIntroMusicInternal();
	}

	/**
	 * Spustí intro/tutorial hudbu s pevne nastavenou hlasitosťou a loop režimom, pokiaľ hudba nie je vypnutá
	 * alebo už nehrá.
	 *
	 * @returns Promise bez dátovej hodnoty po vykonaní kontroly a prípadnom spustení intro hudby.
	 */
	private async playIntroMusicInternal(): Promise<void> {
		const audioName = AudioNames.INTRO_MUSIC;
		const settings = useSettingsStore();

		// Ak platí podmienka `!settings.musicOn`, vykonáme ochrannú vetvu a aktuálna metóda alebo callback sa
		// následne ukončí.
		if (!settings.musicOn) return;

		// Prevent multiple instances
		const existingMusic = this.sound.get(audioName);
		// Ak platí podmienka `existingMusic?.isPlaying`, vykonáme ochrannú vetvu a aktuálna metóda alebo
		// callback sa následne ukončí.
		if (existingMusic?.isPlaying) {
			return;
		}

		// Aktualizujeme prehrávanie alebo hlasitosť zvuku podľa výsledku aktuálnej hernej operácie.
		this.sound.play(audioName, {
			loop: true,
			volume: 0.3, // Preset volume for intro music - do not change
		});
	}

	/**
	 * Zastaví všetky prehrávania audio sprite identifikovaného zadaným kľúčom.
	 *
	 * @param spriteName Kľúč audio sprite v Phaser sound manageri a lazy loaderi.
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private stopAudio(spriteName: AudioSpriteNames): void {
		// Aktualizujeme prehrávanie alebo hlasitosť zvuku podľa výsledku aktuálnej hernej operácie.
		this.sound.stopByKey(spriteName);
	}

	/**
	 * Ak intro hudba práve hrá, vyhľadá jej Phaser sound objekt a odstráni ho zo sound managera.
	 *
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private stopIntroMusic(): void {
		const audioName = AudioNames.INTRO_MUSIC;
		// Ak platí podmienka `!this.sound.isPlaying(audioName)`, vykonáme ochrannú vetvu a aktuálna metóda
		// alebo callback sa následne ukončí.
		if (!this.sound.isPlaying(audioName)) return;

		// Aktualizujeme prehrávanie alebo hlasitosť zvuku podľa výsledku aktuálnej hernej operácie.
		const music = this.sound.get(audioName);
		// Uvoľníme už nepotrebný Phaser objekt alebo zdroj a odstránime súvisiacu referenciu, aby nezostal v
		// pamäti ani render liste.
		this.sound.remove(music);
	}

	/**
	 * Prečíta aktuálne používateľské nastavenie hlasitosti a aplikuje ho na už prehrávanú hlavnú hudbu.
	 *
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private changeMainMusicVolume(): void {
		const settings = useSettingsStore();
		const volume = settings.musicVolume / 100;

		// Ak platí podmienka `!this.sound.isPlaying(AudioNames.MAIN_MUSIC)`, vykonáme ochrannú vetvu a aktuálna
		// metóda alebo callback sa následne ukončí.
		if (!this.sound.isPlaying(AudioNames.MAIN_MUSIC)) return;

		// Aktualizujeme prehrávanie alebo hlasitosť zvuku podľa výsledku aktuálnej hernej operácie.
		const music = this.sound.get(AudioNames.MAIN_MUSIC) as
			| Phaser.Sound.HTML5AudioSound
			| Phaser.Sound.WebAudioSound;
		music.setVolume(volume);
	}

	/**
	 * Zastaví a odstráni aktuálnu inštanciu hlavnej hudby, ak je prehrávaná.
	 *
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private stopMainAudio(): void {
		// Ak platí podmienka `!this.sound.isPlaying(AudioNames.MAIN_MUSIC)`, vykonáme ochrannú vetvu a aktuálna
		// metóda alebo callback sa následne ukončí.
		if (!this.sound.isPlaying(AudioNames.MAIN_MUSIC)) return;

		// Aktualizujeme prehrávanie alebo hlasitosť zvuku podľa výsledku aktuálnej hernej operácie.
		const music = this.sound.get(AudioNames.MAIN_MUSIC);
		// Uvoľníme už nepotrebný Phaser objekt alebo zdroj a odstránime súvisiacu referenciu, aby nezostal v
		// pamäti ani render liste.
		this.sound.remove(music);
	}

	/**
	 * Zastaví a odstráni aktuálnu inštanciu mestského ambientu, ak je prehrávaná.
	 *
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private stopCityAmbient(): void {
		// Ak platí podmienka `!this.sound.isPlaying(AudioNames.CITY_AMBIENT)`, vykonáme ochrannú vetvu a
		// aktuálna metóda alebo callback sa následne ukončí.
		if (!this.sound.isPlaying(AudioNames.CITY_AMBIENT)) return;

		// Aktualizujeme prehrávanie alebo hlasitosť zvuku podľa výsledku aktuálnej hernej operácie.
		const music = this.sound.get(AudioNames.CITY_AMBIENT);
		// Uvoľníme už nepotrebný Phaser objekt alebo zdroj a odstránime súvisiacu referenciu, aby nezostal v
		// pamäti ani render liste.
		this.sound.remove(music);
	}

	/**
	 * Vyhľadá Phaser Frame pre zadaný frameName cez predpripravenú frame-to-atlas cache. Pri chýbajúcom
	 * atlase, textúre alebo frame vráti null namiesto vyvolania chyby.
	 *
	 * @param frameName Názov frame v mapovom multiatlase.
	 * @returns Nájdený Phaser.Textures.Frame alebo null, ak frame nemožno bezpečne vyhľadať.
	 */
	private getAtlasFrame(frameName: FrameName): Phaser.Textures.Frame | null {
		const atlasName: string | undefined =
			this.frameNameToAtlasNameCache.get(frameName);
		// Ak nemáme meno altasu pre príslušný frameName alebo daný frameName je samostaný obrázok
		// ktorý ale nie je naloadovaný v pamäti Phaseru tak vrátime null
		if (!atlasName || !this.textures.exists(atlasName)) return null;

		// Pracujeme s textúrovými metadátami alebo GPU väzbou bez zbytočného kopírovania pixelových dát.
		const frame: Phaser.Textures.Frame = this.textures.getFrame(
			atlasName,
			frameName,
		);
		// Ak sa nepdoarí získať správny grafický objekt alebo nastane iná technická chyba, vratime null
		if (!frame || frame.name !== frameName) return null;

		return frame;
	}

	/**
	 * Vypočíta tileset offset potrebný na presné umiestnenie trimovaného atlasového frame do logickej mapovej
	 * bunky. Os Y spracuje odlišne pre základný frame a _Top.png presah, aby sa zachovala pôvodná kompozícia.
	 *
	 * @param frame Zdrojový Phaser frame vrátane trim a UV metadát.
	 * @param kind Druh mapovej reprezentácie: base pre štvorcovú časť alebo overlap pre _Top.png presah.
	 * @returns Objekt s pixelovým offsetom x a y pre virtuálny tileset.
	 */
	private getDirectAtlasTileOffset(
		frame: Phaser.Textures.Frame,
		kind: DirectAtlasTileKind,
	): { x: number; y: number } {
		const { TILE_SIZE, OVERLAP_TILE_SIZE } = this.tilerepo;

		// Reproduce the exact placement previously produced by stamping a trimmed
		// Image with origin 0.5 into the 256x256 runtime composite cell.
		const x = frame.realWidth / 2 - TILE_SIZE / 2 - frame.x;
		const y =
			kind === "overlap"
				? OVERLAP_TILE_SIZE / 2 + frame.realHeight / 2 - frame.y
				: frame.realHeight / 2 - TILE_SIZE / 2 - frame.y;

		return { x, y };
	}

	/**
	 * Nakonfiguruje virtuálny jedno-tile Tileset tak, aby ukazoval priamo na existujúci atlasový frame a jeho
	 * WebGL textúru. Nastaví rozmery, UV pozíciu a trim offset bez vytvorenia DynamicTexture, framebufferu
	 * alebo ďalšej kópie pixelov.
	 *
	 * @param tileset Virtuálny Phaser Tileset, ktorého rendererové metadata sa majú nastaviť alebo
	 *                aktualizovať.
	 * @param frameName Názov frame v mapovom multiatlase.
	 */
	private configureDirectAtlasTileset(
		tileset: Phaser.Tilemaps.Tileset,
		frameName: FrameName,
	): void {
		//potrebujem získať objektovú referenciu na danú textúru v podobe Phaser Frame
		const frame: Phaser.Textures.Frame | null =
			this.getAtlasFrame(frameName);
		if (!frame) {
			throw new Error(`Unable to find atlas frame: ${frameName}`);
		}
		// Ak platí podmienka `frame.rotated`, vyvoláme chybu, pretože nemožno bezpečne pokračovať.
		if (frame.rotated) {
			throw new Error(
				`Rotated atlas frames are not supported by the direct TilemapLayer renderer: ${frameName}`,
			);
		}

		//identifikujeme či ide o odrezok alebo o normálnu textúru
		const kind: DirectAtlasTileKind = this.overlapFrameNamesCache.has(
			frameName,
		)
			? "overlap"
			: "base";
		//získame offset potrebný pre X / Y súradnice preto, aby sme nemali odskočenú texúturu pri
		// jej renderingu na základe pointera na tento tileset
		const tileOffset: { x: number; y: number } =
			this.getDirectAtlasTileOffset(frame, kind);
		tileset.tileOffset.set(tileOffset.x, tileOffset.y);

		// setImage() / setTileSize() cannot be used here: once image is assigned,
		// both methods reinterpret the complete multi-atlas source as a regular
		// grid. Expose this frame as a one-tile virtual Tileset by replacing only
		// the renderer-facing metadata. No pixels or WebGL resources are allocated.
		Object.assign(tileset as Phaser.Tilemaps.Tileset, {
			tileWidth: frame.cutWidth,
			tileHeight: frame.cutHeight,
			image: frame.texture,
			// UPRAVENÉ: Namiesto frame.glTexture použijeme frame.source.glTexture
			glTexture: frame.source.glTexture,
			rows: 1,
			columns: 1,
			total: 1,
			texCoordinates: [{ x: frame.cutX, y: frame.cutY }],
		});
	}

	/**
	 * Vráti GID priradený atlasovému frame. Ak frame ešte nemá virtuálny tileset, vytvorí ho,
	 * nakonfiguruje, uloží do cache a označí mapovanie tilesetov ako zmenené.
	 *
	 * @param frameName Názov frame v mapovom multiatlase.
	 * @returns Existujúci alebo novo pridelený GID alebo null, ak zdrojový atlasový frame neexistuje.
	 */
	private getOrCreateDirectAtlasTileGid(frameName: FrameName): number | null {
		//vyber tileset pre pozadovany frameName
		const cachedTileset: Phaser.Tilemaps.Tileset | undefined =
			this.directAtlasTilesetCache.get(frameName);
		// Ak mame tileset tak len vratime GID
		if (cachedTileset) return cachedTileset.firstgid;

		const frame: Phaser.Textures.Frame | null =
			this.getAtlasFrame(frameName);
		// Ak nemáme konkrétnu textúru s Phaser pamäte vraciame null
		if (!frame) return null;

		// vytvoríme nový GID tak, že inkrementujeme posledne pridelné ID grafickému objektu o 1
		const gid: number = this.nextDirectAtlasGid++;
		// Vytvoríme nový tileset pre tento frameName
		const tileset = new Phaser.Tilemaps.Tileset(
			`main-map-atlas-frame-${gid}`,
			gid,
			frame.cutWidth,
			frame.cutHeight,
			0,
			0,
		);
		// keď mám vytvorenú základnú metadata štruktúru pre tileset v podobe Phaser.Tilemaps.Tileset
		// je potrebné nastaviť aj ostatné dáta ako napríklad trimming, spacing, konkrétnu textúru s naloadovaných
		// atlasov atd atd to robíme tu
		this.configureDirectAtlasTileset(tileset, frameName);
		// Synchronizujeme príslušnú cache so zmeneným stavom, aby ďalšie lookupy a rendering používali aktuálne údaje.
		this.directAtlasTilesetCache.set(frameName, tileset);
		this.directAtlasTilesets.push(tileset);
		// Aktualizujeme interný stav scény podľa výsledku aktuálnej operácie: `this.directAtlasTilesetsDirty =
		// true;`
		this.directAtlasTilesetsDirty = true;

		return gid;
	}

	/**
	 * Ak pribudli nové virtuálne tilesety a obe vrstvy už existujú, obnoví tileset a GID mapovanie základnej aj
	 * overlap vrstvy. Po úspešnom refreshi zruší dirty príznak.
	 *
	 */
	private refreshDirectAtlasLayerTilesets(): void {
		// Zmysel má pokračoval len ak máme vytvorené tilemapy pre obe vrstvy
		// a zároveň je potrebné refreshnuť tilesety
		if (
			!this.directAtlasTilesetsDirty ||
			!this.mapLayer ||
			!this.overlapLayer
		)
			return;

		// Aktualizujeme rendererové štruktúry Tilemapu a ich väzby na virtuálne tilesety.
		// ja pre normálne tiles tak aj pre odresky
		(this.mapLayer as DirectAtlasTilemapLayer).setTilesets(
			this.directAtlasTilesets,
		);
		(this.overlapLayer as DirectAtlasTilemapLayer).setTilesets(
			this.directAtlasTilesets,
		);
		// nastavíme, že nie je potrebné aktualizovať tilesety
		this.directAtlasTilesetsDirty = false;
	}

	/**
	 * Po obnovení WebGL kontextu znovu pripojí virtuálne tilesety k novým GPU wrapperom atlasových textúr.
	 * Následne obnoví GID mapovanie oboch TilemapLayer objektov.
	 *
	 */
	private refreshDirectAtlasTilesetBindings(): void {
		// Prejdeme všetky tilesety a vytvoríme nové
		for (const [frameName, tileset] of this.directAtlasTilesetCache) {
			this.configureDirectAtlasTileset(tileset, frameName);
		}

		// A restored WebGL context replaces the TextureSource wrappers. Rebuild the
		// gidMap references after all virtual Tilesets point at the restored atlases.
		this.directAtlasTilesetsDirty = true;
		this.refreshDirectAtlasLayerTilesets();
	}

	/**
	 * Uvoľní všetky cache a referencie direct-atlas rendereru pri shutdown scény a odregistruje WebGL restore
	 * listener. Metóda pripraví renderer na čisté vytvorenie pri ďalšom spustení scény.
	 *
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private destroyTilemapRenderer(): void {
		this.game.renderer.off(
			"restorewebgl",
			this.refreshDirectAtlasTilesetBindings,
			this,
		);

		this.directAtlasTilesets.length = 0;
		// Synchronizujeme príslušnú cache so zmeneným stavom, aby ďalšie lookupy a rendering používali aktuálne
		// údaje.
		this.directAtlasTilesetCache.clear();
		// Synchronizujeme príslušnú cache so zmeneným stavom, aby ďalšie lookupy a rendering používali aktuálne
		// údaje.
		this.directBaseTilesByCoordinateKey.clear();
		// Synchronizujeme príslušnú cache so zmeneným stavom, aby ďalšie lookupy a rendering používali aktuálne
		// údaje.
		this.directOverlapTilesByCoordinateKey.clear();
		this.directBaseCullDepthBuckets.length = 0;
		this.directOverlapCullDepthBuckets.length = 0;
		// Synchronizujeme príslušnú cache so zmeneným stavom, aby ďalšie lookupy a rendering používali aktuálne
		// údaje.
		this.tileTintCache.clear();
		// Aktualizujeme interný stav scény podľa výsledku aktuálnej operácie: `this.nextDirectAtlasGid = 1;`
		this.nextDirectAtlasGid = 1;
		// Aktualizujeme interný stav scény podľa výsledku aktuálnej operácie: `this.directAtlasTilesetsDirty =
		// false;`
		this.directAtlasTilesetsDirty = false;
		this.layers.length = 0;
		// Aktualizujeme interný stav scény podľa výsledku aktuálnej operácie: `this.mapLayer = null;`
		this.mapLayer = null;
		// Aktualizujeme interný stav scény podľa výsledku aktuálnej operácie: `this.overlapLayer = null;`
		this.overlapLayer = null;
		// Aktualizujeme interný stav scény podľa výsledku aktuálnej operácie: `this.tilemap = null;`
		this.tilemap = null;
		// Aktualizujeme interný stav scény podľa výsledku aktuálnej operácie: `this.overlapTilemap = null;`
		this.overlapTilemap = null;
	}

	/**
	 * Vyberie z logických frame names iba základné alebo overlap frames podľa požadovaného druhu. Na každej
	 * logickej depth ponechá posledný frame a výsledok vráti zoradený v pôvodnom atlasovom renderovacom poradí.
	 *
	 * @param frameNames Zoznam atlasových frame names, ktoré sa majú filtrovať alebo previesť na renderovacie
	 *                   položky.
	 * @param kind Druh mapovej reprezentácie: base pre štvorcovú časť alebo overlap pre _Top.png presah.
	 *
	 * @returns Zoradené pole frame names vhodných pre požadovanú vrstvu.
	 */
	private selectRenderableFrameNames(
		frameNames: FrameName[],
		kind: DirectAtlasTileKind,
	): FrameName[] {
		// The former SpriteGPULayer implementation allowed one member per atlas
		// layer and logical cell. Preserve that behaviour independently for the
		// square tile and for its `_Top.png` cut-out. Now we do not have this restriction.
		const frameByDepth = new Map<number, FrameName>();

		// Prechádzame všetky poskytnuté frameNames ktoré vstúpili ako argument do tejto metódy.
		// Teda nie všetky frameNames čo máme inicializované a poznáme, ale len vstupný argument.
		for (const frameName of frameNames) {
			const isOverlap = this.overlapFrameNamesCache.has(frameName);
			// Dvojitá kontrola, najskôr od programátora, ktorý vyžaduje spracovanie konkrétneho
			// frame typu (odrezok/normal) a potom, či sa daný frameName nachádza medzi odreskami
			if (kind === "base" ? isOverlap : !isOverlap) continue;

			const depth: number | undefined =
				this.frameNameToLayerIndexCache.get(frameName);
			// Ak nevieme identifikova5 hlbku dan0ho frameName vo vyztahu ku grafickym vrstvam tak ignorujeme
			if (depth === undefined) continue;
			//vsetko prebehlo spravne, vieme aj spravny typ (odrezok/normal) aj hlbku
			frameByDepth.set(depth, frameName);
		}

		// Transformujeme položky z výsledku predchádzajúceho reťazca operácií na nové hodnoty a vytvoríme
		// výsledné pole.
		return Array.from(frameByDepth.entries())
			.sort(
				(
					[depthA]: [number, FrameName],
					[depthB]: [number, FrameName],
				): number => depthA - depthB,
			)
			.map(([, frameName]: [number, FrameName]): FrameName => frameName);
	}

	/**
	 * Vytvorí objekt jedného atlasového frame na konkrétnej mapovej súradnici. Treba si uvedomiť
	 * že logika za týmto je taká, že už nemáme len atlas a vyberáme textúru s altalsu a potom ju v GPU modifikuje,
	 * ale robíme ako keby finálnu grafickú reprezentáciu za základe pointerov pre príslušný tile. Získa GID
	 * a depth, nastaví world pixel pozíciu a predvolené vizuálne vlastnosti a aplikuje prípadný uložený tint.
	 *
	 * @param frameName Názov frame v mapovom multiatlase.
	 * @param x Horizontálna mapová súradnica tile.
	 * @param y Vertikálna mapová súradnica tile.
	 * @param tintState Voliteľná uložená konfigurácia primárneho a sekundárneho tintu a tint mode.
	 * @returns Grafický tile pre príslušné súradnice alebo null, ak frame nemá platný GID či depth.
	 */
	private createDirectAtlasRenderTile(
		frameName: FrameName,
		x: number,
		y: number,
		tintState?: TileTintState,
	): DirectAtlasRenderTile | null {
		//na tomto riadku deFacto vyrábame to čo sme pri SpriteGPULayery volali Member
		// rozdiel je, že na tomto meiste sa buď vytvorí virtuályn tileset s 1 framemom v ňom
		// alebo sa vríti už GID daného grafického objektu
		const gid: number | null =
			this.getOrCreateDirectAtlasTileGid(frameName);
		//získame hlbkú daného tile pre správne vykrelsenie poradia tiles nad danými súradnicami
		const renderDepth: number | undefined =
			this.frameNameToLayerIndexCache.get(frameName);
		// Ak nemám hlbku alebo nemám GID tak končím
		if (gid === null || renderDepth === undefined) return null;

		//vytvorám logický tile pre rendering, niečo ako Member objekt v predchádzajúcej impleemntácii
		const tile: DirectAtlasRenderTile = {
			index: gid,
			x,
			y,
			pixelX: x * this.tilerepo.TILE_SIZE,
			pixelY: y * this.tilerepo.TILE_SIZE,
			alpha: 1,
			visible: true,
			rotation: 0,
			flipX: false,
			flipY: false,
			tint: 0xffffff,
			tint2: 0x000000,
			tintMode: Phaser.TintModes.MULTIPLY as number,
			renderDepth,
		};

		this.applyTintStateToTile(tile, tintState);

		return tile;
	}

	/**
	 * Zostaví a uloží všetky základné aj overlap frames pre jeden tile. Rozdelí
	 * logické frames podľa druhu, odstráni prázdne záznamy a voliteľne okamžite obnoví tileset mapovanie
	 * vrstiev.
	 *
	 * @param tileIndex Číselný index tile, pod ktorým sa vyhľadáva uložený tint pre daný tile.
	 * @param x Horizontálna mapová súradnica tile.
	 * @param y Vertikálna mapová súradnica tile.
	 * @param logicalFrameNames Úplný logický zoznam framov, z ktorých sa má zostaviť vizuál tile.
	 * @param refreshTilesets Určuje, či sa má po zmene okamžite obnoviť tileset a GID mapovanie oboch vrstiev.
	 *                        Voliteľný parameter.
	 */
	private setDirectAtlasTileEntries(
		tileIndex: TileIndex,
		x: number,
		y: number,
		logicalFrameNames: FrameName[],
		refreshTilesets: boolean = true,
	): void {
		const tintState: TileTintState | undefined =
			this.tileTintCache.get(tileIndex);
		const coordinateKey = y * this.tilerepo.MAP_SIZE + x;
		const baseEntries: DirectAtlasRenderTile[] = [];
		const overlapEntries: DirectAtlasRenderTile[] = [];

		// Prejdi vsetky frameNames pre base layer
		for (const frameName of this.selectRenderableFrameNames(
			logicalFrameNames,
			"base",
		)) {
			const tile: DirectAtlasRenderTile | null =
				this.createDirectAtlasRenderTile(frameName, x, y, tintState);
			// Ak sme boli schopny vytvorit objekt DirectAtlasRenderTile tak vkladame
			// dany tile object do pola tiles pre base vrstvu
			if (tile) baseEntries.push(tile);
		}

		// Prejdi vsetky frameNames pre odresky
		for (const frameName of this.selectRenderableFrameNames(
			logicalFrameNames,
			"overlap",
		)) {
			const tile: DirectAtlasRenderTile | null =
				this.createDirectAtlasRenderTile(frameName, x, y, tintState);
			// Ak sme boli schopny vytvorit objekt DirectAtlasRenderTile tak vkladame
			// dany tile object do pola tiles pre odreskovu vrstvu
			if (tile) overlapEntries.push(tile);
		}

		// Ak máme aspoň 1 záznam pre základnú vrstvu tak synchronizujeme príslušnú cache so
		// zmeneným stavom, aby ďalšie lookupy a rendering používali aktuálne údaje.
		if (baseEntries.length) {
			this.directBaseTilesByCoordinateKey.set(coordinateKey, baseEntries);
		} else {
			// Ak nemáme ani jeden záznam, tak pre túto kombináciu koordinátov
			// X/Y nemáme základnú vrstvu
			this.directBaseTilesByCoordinateKey.delete(coordinateKey);
		}

		// Ak máme aspoň 1 záznam pre odreskovu vrstvu tak synchronizujeme príslušnú cache so
		// zmeneným stavom, aby ďalšie lookupy a rendering používali aktuálne údaje.
		if (overlapEntries.length) {
			this.directOverlapTilesByCoordinateKey.set(
				coordinateKey,
				overlapEntries,
			);
		} else {
			// Ak nemáme ani jeden záznam, tak pre túto kombináciu koordinátov
			// X/Y nemáme odrezok vrstvu
			this.directOverlapTilesByCoordinateKey.delete(coordinateKey);
		}

		// Občas sa stalo, že pri úvodnom načítaní som mal čierne tiles po aktualizácii
		// sa tento problém stratil
		if (refreshTilesets) this.refreshDirectAtlasLayerTilesets();
	}

	/**
	 * Odstráni základné aj overlap renderovacie deskriptory patriace zadanej mapovej súradnici.
	 *
	 * @param x Horizontálna mapová súradnica tile.
	 * @param y Vertikálna mapová súradnica tile.
	 */
	private removeDirectAtlasTileEntries(x: number, y: number): void {
		const coordinateKey = y * this.tilerepo.MAP_SIZE + x;
		// Synchronizujeme príslušnú cache so zmeneným stavom, aby ďalšie lookupy a rendering používali aktuálne
		// údaje.
		this.directBaseTilesByCoordinateKey.delete(coordinateKey);
		this.directOverlapTilesByCoordinateKey.delete(coordinateKey);
	}

	/**
	 * Prejde všetky tiles repository a vytvorí počiatočný direct-atlas renderovací index iba pre platné,
	 * nečierne, odomknuté a priamo vykresľované súradnice. Refresh tilesetov odloží až do skončenia hromadnej
	 * inicializácie.
	 *
	 */
	private buildInitialDirectAtlasTileData(): void {
		// Prechadzame všetky logické tiles v repe
		for (let tileIndex = 0; tileIndex < this.tilerepo.size; tileIndex++) {
			const { x, y } =
				this.tilerepo.getTileCoordinatesByTileIndex(tileIndex);
			// Overneie aby nedošlo k snahe vytvárať vadné tiles a teda preskakujeme ak nemáme správne
			// atribúty na ich vytvorenie
			if (
				x < 0 ||
				y < 0 ||
				x >= this.tilerepo.MAP_SIZE ||
				y >= this.tilerepo.MAP_SIZE
			)
				continue;

			const visibility = this.tilerepo.getParam(
				tileIndex,
				TileEntityParam.VISIBILITY,
			);
			// Nerealizujeme vizuálnu podobu na tile based úrovni pre neodhalené tiles
			if (visibility === TileVisibilityType.BLACK) continue;
			// Nevykresľujeme tiles ktoré sú locknuté
			if (this.mapExpansionStore.isInLockedRegionByCoordinates({ x, y }))
				continue;
			// Keď nemáš odhlaenú mapu a existuje šipka tak chod dalej
			if (
				!FLAG_REVEAL_MAP &&
				this.getExpansionRegionArrowByTileIndex(tileIndex)
			)
				continue;

			// samotné vytváranie jednotlivých grafických tiles
			this.setDirectAtlasTileEntries(
				tileIndex,
				x,
				y,
				this.getCorrectFrameNamesByCoordinates({ x, y }),
				false,
			);
		}
	}

	/**
	 * Vyberie pracovné depth buckety pre základnú alebo overlap vrstvu, podľa potreby ich rozšíri a pred novým
	 * culling prechodom vyprázdni. Vracia recyklované polia pripravené na naplnenie viditeľnými tiles.
	 *
	 * @param kind Druh mapovej reprezentácie: base pre štvorcovú časť alebo overlap pre _Top.png presah.
	 * @returns Recyklované dvojrozmerné pole depth bucketov pripravené na aktuálny culling prechod.
	 */
	private prepareDirectAtlasCullDepthBuckets(
		kind: DirectAtlasTileKind,
	): DirectAtlasRenderTile[][] {
		const buckets: DirectAtlasRenderTile[][] =
			kind === "base"
				? this.directBaseCullDepthBuckets
				: this.directOverlapCullDepthBuckets;
		const requiredLength = this.frameNameToLayerCache.length;

		// predpirpravenie struktury pre culling operacie
		while (buckets.length < requiredLength) buckets.push([]);
		for (let i = 0; i < buckets.length; i++) buckets[i].length = 0;

		return buckets;
	}

	/**
	 * Vlastný culling callback vypočíta rozsah mapových buniek viditeľných aktuálnou kamerou, vyberie z indexu
	 * iba viditeľné a rozdelí ich podľa depth. Rešpektuje render order, cull padding, scale a
	 * skipCull a vráti Phaser rendereru zoradené tiles.
	 *
	 * @param kind Druh mapovej reprezentácie: base pre štvorcovú časť alebo overlap pre _Top.png presah.
	 * @param layer Phaser layer alebo LayerData, nad ktorým sa vykonáva inštalácia či culling.
	 * @param camera Kamera, ktorej world viewport určuje viditeľnú časť mapy.
	 * @param outputArray Voliteľné pracovné pole dodané Phaserom; metóda ho vyprázdni a naplní viditeľnými
	 *                    tiles.
	 * @param renderOrder Číselný Phaser render order určujúci smer prechodu osí X a Y. Voliteľný parameter.
	 * @returns Pole viditeľných tiles zoradených podľa renderovacej hĺbky a požadovaného render order.
	 */
	private cullDirectAtlasTiles(
		kind: DirectAtlasTileKind,
		layer: Phaser.Tilemaps.LayerData,
		camera: Phaser.Cameras.Scene2D.Camera,
		outputArray: Phaser.Tilemaps.Tile[] = [],
		renderOrder: number = 0,
	): Phaser.Tilemaps.Tile[] {
		outputArray.length = 0;

		const tilemapLayer = layer.tilemapLayer as DirectAtlasTilemapLayer;
		const mapSize = this.tilerepo.MAP_SIZE;
		const tileSize = this.tilerepo.TILE_SIZE;
		let drawLeft = 0;
		let drawRight = mapSize;
		let drawTop = 0;
		let drawBottom = mapSize;

		if (
			!tilemapLayer.skipCull &&
			tilemapLayer.scrollFactorX === 1 &&
			tilemapLayer.scrollFactorY === 1
		) {
			const scaledTileWidth = Math.max(
				1,
				Math.floor(tileSize * Math.abs(tilemapLayer.scaleX)),
			);
			const scaledTileHeight = Math.max(
				1,
				Math.floor(tileSize * Math.abs(tilemapLayer.scaleY)),
			);

			drawLeft = Math.max(
				0,
				Math.floor(
					(camera.worldView.x - tilemapLayer.x) / scaledTileWidth,
				) - tilemapLayer.cullPaddingX,
			);
			drawRight = Math.min(
				mapSize,
				Math.ceil(
					(camera.worldView.right - tilemapLayer.x) / scaledTileWidth,
				) + tilemapLayer.cullPaddingX,
			);
			drawTop = Math.max(
				0,
				Math.floor(
					(camera.worldView.y - tilemapLayer.y) / scaledTileHeight,
				) - tilemapLayer.cullPaddingY,
			);
			drawBottom = Math.min(
				mapSize,
				Math.ceil(
					(camera.worldView.bottom - tilemapLayer.y) /
						scaledTileHeight,
				) + tilemapLayer.cullPaddingY,
			);
		}

		// Kamera môže byť mimo mapy (boundsScrollEnable je nastavené na hodnotu false).
		// Ochrana pred zamrznutím pri cullingu
		if (drawLeft >= drawRight || drawTop >= drawBottom) {
			this.prepareDirectAtlasCullDepthBuckets(kind);
			tilemapLayer.tilesDrawn = 0;
			tilemapLayer.tilesTotal = mapSize * mapSize;
			return outputArray;
		}

		const entriesByCoordinateKey: Map<number, DirectAtlasRenderTile[]> =
			kind === "base"
				? this.directBaseTilesByCoordinateKey
				: this.directOverlapTilesByCoordinateKey;
		const buckets: DirectAtlasRenderTile[][] =
			this.prepareDirectAtlasCullDepthBuckets(kind);
		const reverseX = renderOrder === 1 || renderOrder === 3;
		const reverseY = renderOrder === 2 || renderOrder === 3;
		const xStart = reverseX ? drawRight - 1 : drawLeft;
		const xEnd = reverseX ? drawLeft - 1 : drawRight;
		const xStep = reverseX ? -1 : 1;
		const yStart = reverseY ? drawBottom - 1 : drawTop;
		const yEnd = reverseY ? drawTop - 1 : drawBottom;
		const yStep = reverseY ? -1 : 1;

		for (let y = yStart; y !== yEnd; y += yStep) {
			const rowKey = y * mapSize;
			for (let x = xStart; x !== xEnd; x += xStep) {
				const entries: DirectAtlasRenderTile[] | undefined =
					entriesByCoordinateKey.get(rowKey + x);
				if (!entries) continue;

				for (let i = 0; i < entries.length; i++) {
					const entry: DirectAtlasRenderTile = entries[i];
					if (!entry.visible || entry.alpha === 0) continue;
					buckets[entry.renderDepth].push(entry);
				}
			}
		}

		// Depth is resolved inside this one TilemapLayer: all visible frames from
		// atlas layer 0 are submitted first, then layer 1, and so on. This preserves
		// the old SpriteGPULayer order without allocating one MAP_SIZE² matrix per
		// atlas layer.
		for (const bucket of buckets) {
			for (const tile of bucket) {
				outputArray.push(tile as unknown as Phaser.Tilemaps.Tile);
			}
		}

		tilemapLayer.tilesDrawn = outputArray.length;
		tilemapLayer.tilesTotal = mapSize * mapSize;

		//vráti všetky tiles, ktoré sa mazjú v tom frame zobraziť
		return outputArray;
	}

	/**
	 * Nahradí štandardný culling callback zadanej TilemapLayer vlastným direct-atlas callbackom. Teda
	 * sa už neberú Tile objekty ale naše referencované inštancie v cahce ako nástroj prehľadávania
	 *
	 * @param layer Phaser layer alebo LayerData, nad ktorým sa vykonáva inštalácia či culling.
	 * @param kind Druh mapovej reprezentácie: base pre štvorcovú časť alebo overlap pre _Top.png presah.
	 */
	private installDirectAtlasCullCallback(
		layer: Phaser.Tilemaps.TilemapLayer,
		kind: DirectAtlasTileKind,
	): void {
		(layer as DirectAtlasTilemapLayer).cullCallback = (
			layerData: Phaser.Tilemaps.LayerData,
			camera: Phaser.Cameras.Scene2D.Camera,
			outputArray?: Phaser.Tilemaps.Tile[],
			renderOrder?: number,
		): Phaser.Tilemaps.Tile[] =>
			this.cullDirectAtlasTiles(
				kind,
				layerData,
				camera,
				outputArray,
				renderOrder,
			);
	}

	/**
	 * Vytvorí optimalizovaný direct-atlas mapový renderer nad dvoma 1×1 Tilemap objektami. Pripraví
	 * dáta, základnú a overlap vrstvu, vlastné culling callbacky, depth hodnoty, WebGL restore a shutdown
	 * listenery a inicializuje mapové labely, progress bary a demolish animáciu.
	 *
	 */
	private createTileMap(): void {
		// Ak platí podmienka `this.game.renderer.type !== Phaser.WEBGL`, vyvoláme chybu, pretože nemožno
		// bezpečne pokračovať.
		if (this.game.renderer.type !== Phaser.WEBGL) {
			throw new Error(
				"The direct-atlas TilemapLayer renderer requires Phaser WebGL",
			);
		}

		const { TILE_SIZE } = this.tilerepo;
		this.buildInitialDirectAtlasTileData();

		// The physical Tilemap data only serves as a lightweight host for Phaser's
		// TilemapLayer renderer. The custom cull callbacks below source their visible
		// entries from directBase/directOverlap maps, so a 1x1 host avoids allocating
		// two MAP_SIZE² arrays of null Tile objects.
		this.tilemap = this.make.tilemap({
			data: [[-1]],
			tileWidth: TILE_SIZE,
			tileHeight: TILE_SIZE,
			insertNull: true,
		});

		this.overlapTilemap = this.make.tilemap({
			data: [[-1]],
			tileWidth: TILE_SIZE,
			tileHeight: TILE_SIZE,
			insertNull: true,
		});

		// vytvoríme prázny layer so zoznamom tilesetov (1 tileset 1 frame)
		// pre halvnú vrstvu
		const baseLayer = this.tilemap.createLayer(
			0,
			this.directAtlasTilesets,
			-TILE_SIZE / 2,
			-TILE_SIZE / 2,
			false,
		) as Phaser.Tilemaps.TilemapLayer | null;

		if (!baseLayer) {
			throw new Error("Unable to create the main TilemapLayer");
		}

		// `_Top.png` frames use the same logical tile coordinate as the square tile.
		// Their virtual Tileset tileOffset places the bottom of the original overlap
		// canvas exactly on the square tile's top edge, so this layer no longer needs
		// to be shifted upward by an entire 256px runtime-composite cell.
		const overlapLayer = this.overlapTilemap.createLayer(
			0,
			this.directAtlasTilesets,
			-TILE_SIZE / 2,
			-TILE_SIZE / 2,
			false,
		) as Phaser.Tilemaps.TilemapLayer | null;

		if (!overlapLayer) {
			throw new Error("Unable to create the overlap TilemapLayer");
		}

		//Inicializácia vlastného culling mechanizmu pre obe vrstvy
		this.installDirectAtlasCullCallback(baseLayer, "base");
		this.installDirectAtlasCullCallback(overlapLayer, "overlap");

		// nastavenie base layeru do globálne prístupnej premmenej s hlbkou 0 - najnizsia hlbka
		this.mapLayer = baseLayer
			.setDepth(0)
			.setName("main-map-tilemap-layer")
			.setCullPadding(2, 2);
		// nastavenie odreskoveho layeru do globálne prístupnej premmenej s hlbkou 1 - nad base layerom
		this.overlapLayer = overlapLayer
			.setDepth(1)
			.setName("main-map-overlap-tilemap-layer")
			.setCullPadding(2, 3);
		this.layers.push(this.mapLayer, this.overlapLayer);
		// nastav nutnost aktualizacie tilesetov na false
		this.directAtlasTilesetsDirty = false;

		// Preserve the old depth range for labels, selectors and effects. The
		// overlap layer itself remains above every square map tile.
		this.mapOverlayBaseDepth = this.frameNameToLayerCache.length + 1;

		//nastaenie opetovneho zostavenie tilesetov po pade a obnoveni webgl kontextu
		this.game.renderer.on(
			"restorewebgl",
			this.refreshDirectAtlasTilesetBindings,
			this,
		);
		//ak by sme do budúcnosti pridávali scény je potrebné vyčistiť tieto tilesety vzhľadom nato
		// ze su to len virtualne tilesety
		this.events.once(
			Phaser.Scenes.Events.SHUTDOWN,
			this.destroyTilemapRenderer,
			this,
		);

		//konfiguracie animacie pre buranie
		const demolishAnimConfig = {
			key: "demolishAnimation",
			frames: [
				{ key: "animations_01", frame: "demolish-01.png" },
				{ key: "animations_01", frame: "demolish-02.png" },
				{ key: "animations_01", frame: "demolish-03.png" },
				{ key: "animations_01", frame: "demolish-04.png" },
				{ key: "animations_01", frame: "demolish-05.png" },
				{ key: "animations_01", frame: "demolish-06.png" },
				{ key: "animations_01", frame: "demolish-07.png" },
				{ key: "animations_01", frame: "demolish-08.png" },
				{ key: "animations_01", frame: "demolish-09.png" },
				{ key: "animations_01", frame: "demolish-10.png" },
				{ key: "animations_01", frame: "demolish-11.png" },
				{ key: "animations_01", frame: "demolish-12.png" },
				{ key: "animations_01", frame: "demolish-13.png" },
				{ key: "animations_01", frame: "demolish-14.png" },
				{ key: "animations_01", frame: "demolish-15.png" },
				{ key: "animations_01", frame: "demolish-16.png" },
				{ key: "animations_01", frame: "demolish-17.png" },
				{ key: "animations_01", frame: "demolish-18.png" },
			],
			frameRate: 18,
			repeat: 0,
		};
		// vytvorenie animacie pre buranie vo Phaser pamäti
		const existingDemolishAnimation = this.anims.get("demolishAnimation");
		const demolishAnimation =
			existingDemolishAnimation || this.anims.create(demolishAnimConfig);
		this.demolishAnim = demolishAnimation || null;

		//cyklicky sa prechádza každý jeden logický tile cez tilerepo
		for (let i = 0; i < this.tilerepo.size; i++) {
			const { x, y } = this.tilerepo.getTileCoordinatesByTileIndex(i);

			const visibility = this.tilerepo.getParam(
				i,
				TileEntityParam.VISIBILITY,
			);
			// Ak je tile vo fogu je potrebné nastaviť progress bar a samozrejme príšlušný FOG tile
			if (visibility === TileVisibilityType.FOG) {
				// Po zmene herného stavu znovu synchronizujeme vizuálnu reprezentáciu dotknutého tile.
				this.rerenderTile(i);

				const tileEntity: TileEntity | null =
					this.tilerepo.getTileEntityByTileIndex(i);
				//ak existuje tileEntity v tile repo znamená to že všetko je ako má byť
				if (tileEntity) {
					const currentTileDurability: number =
						tileEntity.getParamByName(TileEntityParam.DURABILITY) ??
						0;
					// Táto podmienka je len technická, lebo ak je tile v stave FOG tak vždy má currentTileDurability
					// inú ako 0
					if (currentTileDurability !== 0) {
						const correctionCoef = this.tilerepo.MAP_SIZE / 2;
						const distance =
							Math.abs(x - correctionCoef) +
							Math.abs(y - correctionCoef);

						const baseExplorationPrice =
							150 +
							(1 + distance / 1) *
								Math.pow((3 + distance) / 4.5, 2.3);

						const totalExplorationPriceForTile = roundNumber(
							baseExplorationPrice *
								tileEntity.exploreCoefficient! *
								(1 -
									getUpgradeCurrentReplaceValueById(
										UpgradeIds.compass,
									)),
						);

						// Nastavenie hodnoty progress baru pre FOG
						this.updateProgressBar(
							tileEntity.x,
							tileEntity.y,
							totalExplorationPriceForTile,
							currentTileDurability,
						);
					}
				}
			} else if (visibility === TileVisibilityType.EXPLORED) {
				const tile: TileEntity | null =
					this.tilerepo.getTileEntityByTileIndex(i);
				// Ak je tile odhalený ale s nejakého dôvodu nemáme tileEntitu tak je to chyba a ignorujeme ho - čierny tile
				if (!tile) continue;
				// Ak je daný tile súčasťou settmentu je potrebné mu nastaviť TileLabel pre settlementy
				if (
					tilerepoSettlementCastleBuildingsFrameNames.has(
						tile?.frameName,
					)
				) {
					this.tileLabelsCache.set(
						TileLabel.formatKey(TileLabelSkin.LEVEL, tile.index),
						new TileLabel(
							this,
							{
								x: tile.x,
								y: tile.y,
							},
							true,
							(
								tile as SettlementCastleBuildingTileEntity
							).level.toString(),
							tile.index === MAIN_SETTLEMENT_TILE_INDEX
								? TileLabelType.MAIN_SETTLEMENT
								: !tile.isConnectedToMainSettlement
									? TileLabelType.SETTLEMENT_NOT_CONNECTED
									: TileLabelType.SETTLEMENT,
						),
					);
					this.updateTileLabels();
				}

				// Po zmene herného stavu znovu synchronizujeme vizuálnu reprezentáciu dotknutého tile.
				this.rerenderTile(i);

				const entity = this.tilerepo.getTileEntityByTileIndex(i);
				if (!entity?.frameName) continue;

				// Ak je daný tile samotné mesto ktoré má možnosť tukať a zbierať dane
				// má samostatný progress bar pre tento vyber dani, takze ho treba nastavit
				if (
					tilerepoSettlementCastleBuildingsFrameNames.has(
						entity.frameName,
					)
				) {
					this.updateTileProgressBar({
						x,
						y,
					});
				}

				if (tilerepoFriendlyUnitsFrameNames.has(entity.frameName)) {
					this.updateFriendlyUnitProgressBar({
						x,
						y,
					});
				}

				if (tilerepoFightableEnemiesFrameNames.has(entity.frameName)) {
					this.updateFightableEnemyProgressBar({
						x,
						y,
					});
				}
			}
		}
	}

	/**
	 * Určí, či fog tile leží bezprostredne za aktuálnou hranicou uzamknutého mapového regiónu. Podľa osi
	 * regiónu vráti smer expanznej šípky alebo null, ak sa šípka na tile nemá zobrazovať.
	 *
	 * @param tileIndex Lineárny index tile používaný tile repository.
	 * @returns Smer šípky up, down, left alebo right; null, ak tile nie je hranicou zamknutej expanzie.
	 */
	private getExpansionRegionArrowByTileIndex(
		tileIndex: TileIndex,
	): "up" | "down" | "left" | "right" | null {
		// Ak platí podmienka `this.tilerepo.getParam(tileIndex, TileEntityParam.VISIBILITY) !==
		// TileVisibilityType.FOG`, vykonáme ochrannú vetvu a aktuálna metóda alebo callback sa následne ukončí.
		if (
			this.tilerepo.getParam(tileIndex, TileEntityParam.VISIBILITY) !==
			TileVisibilityType.FOG
		)
			return null;

		const { x, y } = this.tilerepo.getTileCoordinatesByTileIndex(tileIndex);

		const region = this.mapExpansionStore.getMapExpansionByCoordinates({
			x,
			y,
		});
		// Ak platí podmienka `!region || region.isUnlocked`, vykonáme ochrannú vetvu a aktuálna metóda alebo
		// callback sa následne ukončí.
		if (!region || region.isUnlocked) return null;

		const { minX, maxX, minY, maxY } =
			this.mapExpansionStore.getEffectiveMapBorderCoordinates();

		// Ak platí podmienka `region.axis === "x"`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú
		// príslušný stav.
		if (region.axis === "x") {
			// Ak platí podmienka `x === minX - 1`, vykonáme ochrannú vetvu a aktuálna metóda alebo callback sa
			// následne ukončí.
			if (x === minX - 1) return "left";
			// Ak platí podmienka `x === maxX + 1`, vykonáme ochrannú vetvu a aktuálna metóda alebo callback sa
			// následne ukončí.
			if (x === maxX + 1) return "right";
		}

		// Ak platí podmienka `region.axis === "y"`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú
		// príslušný stav.
		if (region.axis === "y") {
			// Ak platí podmienka `y === minY - 1`, vykonáme ochrannú vetvu a aktuálna metóda alebo callback sa
			// následne ukončí.
			if (y === minY - 1) return "up";
			// Ak platí podmienka `y === maxY + 1`, vykonáme ochrannú vetvu a aktuálna metóda alebo callback sa
			// následne ukončí.
			if (y === maxY + 1) return "down";
		}

		return null;
	}

	/**
	 * Prekreslí pásy tiles okolo aktuálnych efektívnych hraníc mapy až po dosah odomknutý príslušnými upgradmi.
	 * Aktualizuje tak expanzné šípky a vizuály po zmene veľkosti dostupnej oblasti.
	 *
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	public rerenderMapExpansionBorderTiles(): void {
		const { minX, maxX, minY, maxY } =
			this.mapExpansionStore.getEffectiveMapBorderCoordinates();
		const maxDistance =
			1 +
			getUpgradeCurrentReplaceValueById(UpgradeIds.cartography) +
			getUpgradeCurrentReplaceValueById(UpgradeIds.espionage);

		// Opakujeme blok s počítadlom `let md = 1`, kým platí `md <= maxDistance`; po iterácii vykonáme `md++`.
		for (let md = 1; md <= maxDistance; md++) {
			// Opakujeme blok s počítadlom `let x = minX`, kým platí `x <= maxX`; po iterácii vykonáme `x++`.
			for (let x = minX; x <= maxX; x++) {
				// Po zmene herného stavu znovu synchronizujeme vizuálnu reprezentáciu dotknutého tile.
				this.rerenderTile(
					this.tilerepo.getTileIndexByTileCoordinates({
						x,
						y: minY - md,
					}),
				);
				// Po zmene herného stavu znovu synchronizujeme vizuálnu reprezentáciu dotknutého tile.
				this.rerenderTile(
					this.tilerepo.getTileIndexByTileCoordinates({
						x,
						y: maxY + md,
					}),
				);
			}

			// Opakujeme blok s počítadlom `let y = minY`, kým platí `y <= maxY`; po iterácii vykonáme `y++`.
			for (let y = minY; y <= maxY; y++) {
				// Po zmene herného stavu znovu synchronizujeme vizuálnu reprezentáciu dotknutého tile.
				this.rerenderTile(
					this.tilerepo.getTileIndexByTileCoordinates({
						x: minX - md,
						y,
					}),
				);
				// Po zmene herného stavu znovu synchronizujeme vizuálnu reprezentáciu dotknutého tile.
				this.rerenderTile(
					this.tilerepo.getTileIndexByTileCoordinates({
						x: maxX + md,
						y,
					}),
				);
			}
		}
	}

	/**
	 * Synchronizuje renderovaciu reprezentáciu jedného tile s aktuálnym stavom repository. Najskôr odstráni
	 * staré base/overlap položky, spracuje expanzné šípky a ochranné stavy, znovu zostaví atlasové frames a
	 * nakoniec upraví progress bar alebo červené zvýraznenie.
	 *
	 * @param tileIndex Lineárny index tile používaný tile repository.
	 * @param onlyDestroy Ak je true, stará renderovacia reprezentácia sa odstráni bez vytvorenia novej.
	 *                    Voliteľný parameter.
	 */
	rerenderTile(tileIndex: TileIndex, onlyDestroy: boolean = false): void {
		const { x, y } = this.tilerepo.getTileCoordinatesByTileIndex(tileIndex);
		// Renderovať môžeme len vtedy ak máme korektné X/Y súradnice a už máme vytorené vrstvy
		// do ktorých môžeme renderovať
		if (
			!this.mapLayer ||
			!this.overlapLayer ||
			x < 0 ||
			y < 0 ||
			x >= this.tilerepo.MAP_SIZE ||
			y >= this.tilerepo.MAP_SIZE
		)
			return;

		// Remove both groups first so a stale square or `_Top.png` cut-out cannot
		// survive an entity/visibility change or a temporary click/fade animation.
		this.removeDirectAtlasTileEntries(x, y);

		const expansionDirection =
			!FLAG_REVEAL_MAP &&
			this.getExpansionRegionArrowByTileIndex(tileIndex);
		if (expansionDirection) {
			const skin =
				`expansionArrow${capitalize(expansionDirection)}` as TileLabelSkin;
			if (!this.hasTileLabel({ x, y }, skin)) {
				// Synchronizujeme sprievodný vizuálny prvok s aktuálnym stavom tile alebo entity.
				this.updateTileLabel({ x, y }, skin, TileLabelAction.CREATE);
				this.updateTileLabels();
			}
			return;
		}

		//oprva určená na správne vykreslenei tile labels pri re-renderingu v súvislosti s rozsirenim mapy
		let expansionLabelsChanged = false;
		const expansionSkins: TileLabelSkin[] = [
			TileLabelSkin.EXPANSION_ARROW_UP,
			TileLabelSkin.EXPANSION_ARROW_DOWN,
			TileLabelSkin.EXPANSION_ARROW_LEFT,
			TileLabelSkin.EXPANSION_ARROW_RIGHT,
		];
		// Nastavenie šípiek pre rozšírenie mapy ak tile má takúto vizuálnu reprezentáciu
		for (const skin of expansionSkins) {
			if (!this.hasTileLabel({ x, y }, skin)) continue;
			this.updateTileLabel({ x, y }, skin, TileLabelAction.DELETE);
			expansionLabelsChanged = true;
		}
		//aktualizuj všetko až po skončení cyklu s nastavením tile Labels pre rozsirenie mapy
		if (expansionLabelsChanged) this.updateTileLabels();

		if (this.mapExpansionStore.isInLockedRegionByCoordinates({ x, y }))
			return;
		// Ak je daný tile nepreskúmaný - čierny tak nerob nič
		if (
			this.tilerepo.getParam(tileIndex, TileEntityParam.VISIBILITY) ===
			TileVisibilityType.BLACK
		)
			return;
		// Tu končí likvidácia grafickej reprezentácie tile, od tohto riadku ďalej sa jedná o znovu vytovrenie
		// nového grafického tile
		if (onlyDestroy) return;

		// získame názvy framenov ktoré chcem aplikovať
		const logicalFrameNames: FrameName[] =
			this.getCorrectFrameNamesByCoordinates({
				x,
				y,
			});
		// nastavíme pre daný tileIndex a x/y koordináty príslušné framy
		this.setDirectAtlasTileEntries(tileIndex, x, y, logicalFrameNames);

		const doNotRemoveProgressBarTileIds = [
			...tilerepoSettlementCastleBuildingsFrameNames,
			...tilerepoFriendlyUnitsFrameNames,
			...tilerepoFightableEnemiesFrameNames,
		]
			.map((frameName: FrameName): TileId | null =>
				this.tilerepo.getTileIdByFrameName(frameName),
			)
			.filter(
				(tileId: TileId | null): boolean => tileId !== null,
			) as Array<TileId>;

		// Ak platí podmienka `this.tilerepo.getParam(tileIndex, TileEntityParam.VISIBILITY) ===
		// TileVisibilityType.EXPLORED && this.tilerepo.getTileIdsByT…`, vykonáme nasledujúcu vetvu a jej
		// operácie aktualizujú príslušný stav.
		if (
			this.tilerepo.getParam(tileIndex, TileEntityParam.VISIBILITY) ===
				TileVisibilityType.EXPLORED &&
			!doNotRemoveProgressBarTileIds.includes(
				this.tilerepo.getTileIdsByTileIndex(tileIndex)[2],
			)
		) {
			const plagueCoordinates: PlagueTile[] | null =
				useUserStore().plagueCoordinates;
			if (plagueCoordinates) {
				// Overíme, či aspoň jedna položka z kolekcie `plagueCoordinates` spĺňa podmienku.
				const isPlagueTile = plagueCoordinates.some(
					(plague: PlagueTile): boolean =>
						plague.coordinates.x === x &&
						plague.coordinates.y === y,
				);
				if (isPlagueTile) return;
			}
			this.removeProgressBar(tileIndex);
		}

		if (tilerepoSettlementEmptyFrameNames.has(logicalFrameNames[0])) {
			const isConnected =
				this.tilerepo.getParam(
					tileIndex,
					TileEntityParam.IS_CONNECTED_TO_MAIN_SETTLEMENT,
				) === 1;
			if (!isConnected) {
				this.highlightTilesRed(
					new Set<TileCoordinatesWithValue>([
						{ x, y, value: isConnected },
					]),
				);
			}
		}
	}

	/**
	 * Pripojí hlavnú Phaser kameru, nastaví jej hranice podľa pixelových rozmerov celej mapy a počiatočne ju
	 * vycentruje na stred mapy.
	 *
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	setupSceneCamera(): void {
		// Aktualizujeme interný stav scény podľa výsledku aktuálnej operácie: `this.camera =
		// this.cameras.main;`
		this.camera = this.cameras.main;

		const screanWidth = this.tilerepo.MAP_SIZE * this.tilerepo.TILE_SIZE;
		const screanHeight = this.tilerepo.MAP_SIZE * this.tilerepo.TILE_SIZE;

		// Set camera bounds according to the dimensions of the tilemap
		this.camera.setBounds(0, 0, screanWidth, screanHeight);

		// Initialize the scroll camera to the center of the map
		const dimensionInPixels =
			(this.tilerepo.MAP_SIZE * this.tilerepo.TILE_SIZE) / 2;
		// Aktualizujeme polohu, zoom alebo hranice kamery podľa aktuálneho herného kontextu.
		this.camera.centerOn(dimensionInPixels, dimensionInPixels);
	}

	/**
	 * Vytvorí rexCameraController s pravidlami pre pan, pinch zoom a mouse-wheel zoom. Po inicializácii obnoví
	 * uloženú pozíciu a zoom kamery z používateľských nastavení.
	 *
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	setCameraController(): void {
		const cameraController = this.plugins.get("rexCameraController");
		// Ak platí podmienka `cameraController`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú
		// príslušný stav.
		if (cameraController) {
			// @ts-expect-error - missing types
			this.cameraController = cameraController.add(this, {
				camera: this.camera,
				minZoom: 0.02,
				maxZoom: 2,

				panScroll: true,
				panScrollEnable: true,

				pinchZoom: true,
				pinchZoomEnable: true,
				pinchZoomMin: 0.02,
				pinchZoomMax: 2,
				pinchZoomFocusEnable: true,

				boundsScroll: true,
				boundsScrollEnable: false,

				mouseWheelZoom: true,
				mouseWheelZoomEnable: true,
				mouseWheelZoomStep: 0.05,
				mouseWheelZoomMin: 0.02,
				mouseWheelZoomMax: 2,
			});
		}

		// Restore the camera position and zoom level from
		const useSettings = useSettingsStore();
		// Aktualizujeme polohu, zoom alebo hranice kamery podľa aktuálneho herného kontextu.
		this.camera?.setScroll(
			useSettings.cameraSave.x,
			useSettings.cameraSave.y,
		);
		// Aktualizujeme polohu, zoom alebo hranice kamery podľa aktuálneho herného kontextu.
		this.camera?.setZoom(useSettings.cameraSave.zoomLevel);
	}

	/**
	 * Ak je aktívny interval automatického klikania, znovu povolí posúvanie kamery, interval zruší a vyčistí
	 * jeho identifikátor.
	 *
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	turnOffAutoTapping(): void {
		// Ak platí podmienka `this.autoTappingInterval`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú
		// príslušný stav.
		if (this.autoTappingInterval) {
			// @ts-expect-error - missing types
			this.cameraController?.panScroll.pan.setEnable(true);
			// Nastavíme alebo zrušíme časovanú operáciu, aby sa príslušná logika vykonala v správnom čase alebo
			// intervale.
			clearInterval(this.autoTappingInterval);
			// Aktualizujeme interný stav scény podľa výsledku aktuálnej operácie: `this.autoTappingInterval =
			// null;`
			this.autoTappingInterval = null;
		}
	}

	/**
	 * Zaregistruje pointerdown, pointermove a pointerup obsluhu pre mapový canvas. Sleduje viac pointerov,
	 * rozlišuje krátky klik od drag gest, riadi auto-tapping, ukladá kameru, aktualizuje ambient a deleguje
	 * platný klik na manageClickTile.
	 *
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	addClickHandler(): void {
		// Threshold for distinguishing dragging from clicking
		const dragDistanceThreshold = 10;
		let pointerDownTime = 0;
		const pointers = new Array<Phaser.Input.Pointer>(
			this.input.mousePointer,
			this.input.pointer1,
			this.input.pointer2,
			this.input.pointer3,
			this.input.pointer4,
			this.input.pointer5,
			this.input.pointer6,
			this.input.pointer7,
			this.input.pointer8,
			this.input.pointer9,
			this.input.pointer10,
		);

		// Processing mouse inputs for panning
		this.input.on("pointerdown", (pointer: Phaser.Input.Pointer): void => {
			// Ak platí podmienka `pointer.downElement?.nodeName !== "CANVAS"`, vykonáme ochrannú vetvu a
			// aktuálna metóda alebo callback sa následne ukončí.
			if (pointer.downElement?.nodeName !== "CANVAS") return;

			const runtimeStore = useRuntimeStore();
			// Zapíšeme výsledok operácie do centrálneho store alebo spustíme nadväzujúce prepočty závislého
			// stavu.
			runtimeStore.setLastClickTimestamp(Date.now()); // Update the last click timestamp in the core store
			// Ak platí podmienka `!runtimeStore.firstClick`, vykonáme ochrannú vetvu a aktuálna metóda alebo
			// callback sa následne ukončí.
			if (!runtimeStore.firstClick) return;
			// Aktualizujeme interný stav scény podľa výsledku aktuálnej operácie: `this.isDragging = false;`
			this.isDragging = false;
			hideAllPoppers(); // hide tooltips from ui
			// Zapíšeme výsledok operácie do centrálneho store alebo spustíme nadväzujúce prepočty závislého
			// stavu.
			runtimeStore.setCameraActive(true);
			this.dragStartPoint.set(pointer.x, pointer.y);
			this.cameraStartPoint.set(
				this.camera?.scrollX ?? 0,
				this.camera?.scrollY ?? 0,
			);
			pointerDownTime = this.time.now; // Save the time of the press

			// Vyfiltrujeme z kolekcie `pointers` iba položky spĺňajúce callback podmienku.
			const pointerDownCount = pointers.filter(
				(pointer: Phaser.Input.Pointer): boolean =>
					pointer?.isDown ?? false,
			).length;
			// Ak platí podmienka `pointerDownCount === 1`, vykonáme nasledujúcu vetvu a jej operácie
			// aktualizujú príslušný stav.
			if (pointerDownCount === 1) {
				const speedMultiplier =
					1 +
					getUpgradeCurrentReplaceValueById(
						UpgradeIds.finger_on_the_pulse,
					) +
					getUpgradeCurrentReplaceValueById(UpgradeIds.lazy_finger);

				// TODO: to 500ms niekde do configu
				const intervalTime = 500 / speedMultiplier;

				// Nastavíme alebo zrušíme časovanú operáciu, aby sa príslušná logika vykonala v správnom čase
				// alebo intervale.
				this.autoTappingInterval = setInterval((): void => {
					this.manageClickTile(pointer, pointers);
				}, intervalTime);
			} else {
				// Ak predchádzajúca podmienka neplatí, vykonáme alternatívnu vetvu.
				this.turnOffAutoTapping();
			}
		});

		// Aktualizujeme interný stav scény podľa výsledku aktuálnej operácie: `this.input.on("pointermove",
		// (pointer: Phaser.Input.Pointer): void => { if (pointer.isDown) { const zoomLevel = this.camera?.zoom
		// ?? 0.2; const deltaX = (pointer.x - this.dragStar…`
		this.input.on("pointermove", (pointer: Phaser.Input.Pointer): void => {
			// Ak platí podmienka `pointer.isDown`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú
			// príslušný stav.
			if (pointer.isDown) {
				const zoomLevel = this.camera?.zoom ?? 0.2;
				const deltaX = (pointer.x - this.dragStartPoint.x) / zoomLevel;
				const deltaY = (pointer.y - this.dragStartPoint.y) / zoomLevel;

				// If the distance exceeds the threshold, we start dragging
				if (
					// @ts-expect-error - missing types
					this.cameraController?.panScroll.isPanning &&
					(Math.abs(deltaX) > dragDistanceThreshold ||
						Math.abs(deltaY) > dragDistanceThreshold)
				) {
					// Aktualizujeme interný stav scény podľa výsledku aktuálnej operácie: `this.isDragging =
					// true;`
					this.isDragging = true;
					this.turnOffAutoTapping();
				}
				// Ak platí podmienka `this.tileLabelPointerDown && (Math.abs(deltaX) > this.tilerepo.TILE_SIZE
				// / 2 || Math.abs(deltaY) > this.tilerepo.TILE_SIZE /…`, vykonáme nasledujúcu vetvu a jej
				// operácie aktualizujú príslušný stav.
				if (
					this.tileLabelPointerDown &&
					(Math.abs(deltaX) > this.tilerepo.TILE_SIZE / 2 ||
						Math.abs(deltaY) > this.tilerepo.TILE_SIZE / 2)
				) {
					this.turnOffAutoTapping();
				}
			}
		});

		// Nastavíme alebo zrušíme časovanú operáciu, aby sa príslušná logika vykonala v správnom čase alebo
		// intervale.
		this.input.on("pointerup", (pointer: Phaser.Input.Pointer): void => {
			// Ak platí podmienka `pointer.downElement?.nodeName !== "CANVAS"`, vykonáme ochrannú vetvu a
			// aktuálna metóda alebo callback sa následne ukončí.
			if (pointer.downElement?.nodeName !== "CANVAS") return;

			if (
				this.cameras.main.zoom !==
				useSettingsStore().cameraSave.zoomLevel
			) {
				this.updateProgressBars();
				this.updateAmbientSounds();
				this.updateTileLabels();
			}
			const clickDuration = this.time.now - pointerDownTime;
			const deltaX = pointer.x - this.dragStartPoint.x;
			const deltaY = pointer.y - this.dragStartPoint.y;
			const dragDistance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
			this.turnOffAutoTapping();
			const cameraPoint = new Phaser.Math.Vector2(
				this.camera?.scrollX ?? 0,
				this.camera?.scrollY ?? 0,
			);
			const runtimeStore = useRuntimeStore();
			// Ak platí podmienka `!cameraPoint.equals(this.cameraLastChangedPoint)`, vykonáme nasledujúcu vetvu
			// a jej operácie aktualizujú príslušný stav.
			if (!cameraPoint.equals(this.cameraLastChangedPoint)) {
				// Aktualizujeme interný stav scény podľa výsledku aktuálnej operácie:
				// `this.cameraLastChangedPoint = cameraPoint.clone();`
				this.cameraLastChangedPoint = cameraPoint.clone();
				// Zapíšeme výsledok operácie do centrálneho store alebo spustíme nadväzujúce prepočty závislého
				// stavu.
				runtimeStore.setLastCameraChangeTimestamp(Date.now()); // Update the last camera change timestamp in the core store
			}
			// Zapíšeme výsledok operácie do centrálneho store alebo spustíme nadväzujúce prepočty závislého
			// stavu.
			useSettingsStore().setCameraSave({
				x: this.cameras.main.scrollX,
				y: this.cameras.main.scrollY,
				zoomLevel: this.cameras.main.zoom ?? 0.2,
			});
			// Zapíšeme výsledok operácie do centrálneho store alebo spustíme nadväzujúce prepočty závislého
			// stavu.
			runtimeStore.setCameraActive(false);

			// Ak platí podmienka `!this.isDragging && dragDistance <= dragDistanceThreshold && clickDuration <
			// 300`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú príslušný stav.
			if (
				!this.isDragging &&
				dragDistance <= dragDistanceThreshold &&
				clickDuration < 300
			) {
				// Ak platí podmienka `!this.tileLabelPointerDown && (this.camera?.zoom ?? 0.2) <
				// ZOOM_NO_TILE_DETAIL`, vykonáme ochrannú vetvu a aktuálna metóda alebo callback sa následne
				// ukončí.
				if (
					!this.tileLabelPointerDown &&
					(this.camera?.zoom ?? 0.2) < ZOOM_NO_TILE_DETAIL
				) {
					const worldPoint = this.camera!.getWorldPoint(
						pointer.x,
						pointer.y,
					);
					const toast = this.toastPool.getForVariant(
						TileToastType.WARNING,
					);
					toast.showToast(
						translate("ui.too_zoomed_out"),
						{
							messageType: TileToastType.WARNING,
							position: {
								x: worldPoint.x / this.tilerepo.TILE_SIZE,
								y: worldPoint.y / this.tilerepo.TILE_SIZE,
							},
						},
						this.cameras.main.zoom,
					);
					// Aktualizujeme interný stav scény podľa výsledku aktuálnej operácie:
					// `this.tileLabelPointerDown = false;`
					this.tileLabelPointerDown = false;
					return;
				}
				this.manageClickTile(pointer, pointers);
			}

			// Ak platí podmienka `useQuestStore().tutorialActive && this.isDragging`, vykonáme nasledujúcu
			// vetvu a jej operácie aktualizujú príslušný stav.
			if (useQuestStore().tutorialActive && this.isDragging) {
				// Use a small delay to let camera settle after manual dragging
				this.time.delayedCall(100, (): void => {
					this.checkCameraPosition();
				});
			}
			// Aktualizujeme interný stav scény podľa výsledku aktuálnej operácie: `this.isDragging = false;`
			this.isDragging = false;
			// Aktualizujeme interný stav scény podľa výsledku aktuálnej operácie: `this.tileLabelPointerDown =
			// false;`
			this.tileLabelPointerDown = false;
		});

		this.input.on("wheel", (): void => {
			this.debounceZoomUpdate();
		});
	}

	private debounceZoomUpdate = useDebounceFn((): void => {
		this.updateProgressBars();
		this.updateAmbientSounds();
		this.updateTileLabels();
	}, 100);

	/**
	 * Spracuje jeden manuálny alebo automaticky opakovaný pointer klik. Prevedie obrazovkové súradnice na tile,
	 * rieši klik na uzamknutú expanziu, vyberie aktuálnu entitu, uplatní obmedzenia zoomu a auto-tappingu a
	 * nakoniec zavolá clickTile.
	 *
	 * @param pointer Aktuálny Phaser pointer obsahujúci obrazovkovú pozíciu a stav stlačenia.
	 * @param pointers Zoznam všetkých sledovaných pointerov používaný na rozlíšenie single-touch a multi-touch
	 *                 vstupu.
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	manageClickTile(
		pointer: Phaser.Input.Pointer,
		pointers: Array<Phaser.Input.Pointer>,
	): void {
		// Vyfiltrujeme z kolekcie `pointers` iba položky spĺňajúce callback podmienku.
		const pointerDownCount = pointers.filter(
			(pointer: Phaser.Input.Pointer): boolean =>
				pointer?.isDown ?? false,
		).length;
		// Ak platí podmienka `pointerDownCount === 0`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú
		// príslušný stav.
		if (pointerDownCount === 0) {
			this.turnOffAutoTapping();
		}
		// Ak platí podmienka `!this.isDragging || this.autoTappingInterval`, vykonáme nasledujúcu vetvu a jej
		// operácie aktualizujú príslušný stav.
		if (!this.isDragging || this.autoTappingInterval) {
			const worldPoint = this.camera!.getWorldPoint(pointer.x, pointer.y);

			const halfTileSize = this.tilerepo.TILE_SIZE / 2;
			const col = Math.trunc(
				(worldPoint.y + halfTileSize) / this.tilerepo.TILE_SIZE,
			);
			const row = Math.trunc(
				(worldPoint.x + halfTileSize) / this.tilerepo.TILE_SIZE,
			);

			const tileIndex = this.tilerepo.getTileIndexByTileCoordinates({
				x: row,
				y: col,
			});

			// Ak platí podmienka `(!clickedTileEntityCache || clickedTileEntityCache.index !== tileIndex) &&
			// !this.tileLabelPointerDown`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú príslušný
			// stav.
			if (
				(!clickedTileEntityCache ||
					clickedTileEntityCache.index !== tileIndex) &&
				!this.tileLabelPointerDown
			) {
				clickedTileEntityCache =
					this.tilerepo.getTileEntityByTileIndex(tileIndex);
			}

			const isExpansionClick =
				!FLAG_REVEAL_MAP &&
				!!this.getExpansionRegionArrowByTileIndex(tileIndex);
			// Ak platí podmienka `isExpansionClick`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú
			// príslušný stav.
			if (
				// isNeighbourExplored&&
				isExpansionClick
			) {
				const expansionConfig =
					this.mapExpansionStore.getMapExpansionByCoordinates(
						this.tilerepo.getTileCoordinatesByTileIndex(tileIndex),
					);
				// Ak platí podmienka `expansionConfig && !expansionConfig.isUnlocked`, vykonáme ochrannú vetvu
				// a aktuálna metóda alebo callback sa následne ukončí.
				if (expansionConfig && !expansionConfig.isUnlocked) {
					clickedTileEntityCache = null;
					// TODO: Toto by bolo lepsie zavesit priamo na label a nie podla suradnic, ze kam klikam na mape.
					usePopupStore().showPopup({
						id: PopupDictionary.LOCKED_MAP_REGION,
						type: PopupType.LOCKED_MAP_REGION,
						heading: translate("ui.map_expansion"),
						body: expansionConfig,
						imgSrc: "popups/map-expansion-header-bg.avif",
						isPaddingless: true,
						maxHeight: 35,
						width: 22.5,
					});
					return;
				}
			}

			// Ak platí podmienka `!clickedTileEntityCache`, vykonáme ochrannú vetvu a aktuálna metóda alebo
			// callback sa následne ukončí.
			if (!clickedTileEntityCache) return;

			// Ak platí podmienka `(!this.tileLabelPointerDown && (this.camera?.zoom ?? 0.2) <
			// ZOOM_NO_TILE_DETAIL) || (!tilerepoSettlementCastleBuildingsFrame…`, vykonáme ochrannú vetvu a
			// aktuálna metóda alebo callback sa následne ukončí.
			if (
				(!this.tileLabelPointerDown &&
					(this.camera?.zoom ?? 0.2) < ZOOM_NO_TILE_DETAIL) ||
				(!tilerepoSettlementCastleBuildingsFrameNames.has(
					clickedTileEntityCache.frameName,
				) &&
					clickedTileEntityCache.getParamByName(
						TileEntityParam.VISIBILITY,
					) !== TileVisibilityType.FOG &&
					this.autoTappingInterval)
			) {
				return;
				// Ak predchádzajúca podmienka neplatí, overíme alternatívu `this.autoTappingInterval`.
			} else if (this.autoTappingInterval) {
				// @ts-expect-error - missing types
				this.cameraController?.panScroll?.setEnable(false);
			}
			this.clickTile(clickedTileEntityCache!);
		}
	}

	/**
	 * Aktivuje quick-tool logiku pre zadanú entitu a podľa viditeľnosti spracuje truhlicu, zobrazí selektor a
	 * prehrá klik. Neviditeľný tile namiesto výberu presmeruje UI.
	 *
	 * @param clickedTile Tile entita, na ktorú hráč klikol alebo ktorú sa pokúša odhaliť.
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	selectTile(clickedTile: TileEntity): void {
		manageQuickTool(this.tilerepo, clickedTile);

		const isVisible = clickedTile.getParamByName(
			TileEntityParam.VISIBILITY,
		);

		// Ak platí podmienka `isVisible === TileVisibilityType.EXPLORED`, vykonáme nasledujúcu vetvu a jej
		// operácie aktualizujú príslušný stav.
		if (isVisible === TileVisibilityType.EXPLORED) {
			this.handleChest(clickedTile.x, clickedTile.y);
		}

		// Ak platí podmienka `isVisible`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú príslušný stav.
		if (isVisible) {
			this.addSelector({
				x: clickedTile.x,
				y: clickedTile.y,
			});

			// Aktualizujeme prehrávanie alebo hlasitosť zvuku podľa výsledku aktuálnej hernej operácie.
			SoundService.getInstance().playGenerickClick();
		} else {
			// Ak predchádzajúca podmienka neplatí, vykonáme alternatívnu vetvu.
			// Emitujeme udalosť do aplikačného event busu, aby sa o zmene dozvedeli ostatné moduly.
			this.emitter.emit(EmitterEvents.PhaserRedirect);
		}
	}

	/**
	 * Zruší aktuálny výber tile odstránením jeho selektora a range zvýraznenia.
	 *
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	deselectTile(): void {
		this.removeSelector();
	}

	/**
	 * Vytvorí jeden alebo viac dočasných efektov získania odmeny alebo liečenia nad tile. Každý efekt náhodne
	 * rozmiestni, animuje smerom nahor a po skončení tweena zničí.
	 *
	 * @param xPos Horizontálna mapová súradnica, na ktorej sa má vytvoriť efekt.
	 * @param yPos Vertikálna mapová súradnica, na ktorej sa má vytvoriť efekt.
	 * @param final Určuje, či ide o finálnu, výraznejšiu verziu claim animácie. Voliteľný parameter.
	 * @param isCoin Určuje, či sa má namiesto heal efektu použiť obrázok mince. Voliteľný parameter.
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	exploreClaimAnimation(
		xPos: number,
		yPos: number,
		final = false,
		isCoin = false,
	): void {
		// Ak platí podmienka `!useSettingsStore().animationsOn`, vykonáme ochrannú vetvu a aktuálna metóda
		// alebo callback sa následne ukončí.
		if (!useSettingsStore().animationsOn) return;

		const numberOfAnimations = final ? 5 : 1;
		const animationLifetime = 800;
		const baseX = xPos * this.tilerepo.TILE_SIZE;
		const baseY = yPos * this.tilerepo.TILE_SIZE;

		// Opakujeme blok s počítadlom `let i = 0`, kým platí `i < numberOfAnimations`; po iterácii vykonáme
		// `i++`.
		for (let i = 0; i < numberOfAnimations; i++) {
			const effect = this.add
				.image(
					baseX +
						(final
							? Phaser.Math.Between(-80, 80)
							: Phaser.Math.Between(-50, 50)),
					baseY + Phaser.Math.Between(-50, 50),
					"animations_01",
					isCoin ? "Coin.png" : `mini-heal-${final ? "2" : "1"}.png`,
				)
				.setDepth(
					this.mapOverlayBaseDepth +
						EXPLORE_ANIMATION_DEPTH_CORRECTION,
				);

			// Vytvoríme tween, ktorý plynulo mení uvedené vlastnosti cieľového objektu a po dokončení vykoná
			// cleanup callback.
			this.tweens.add({
				targets: effect,
				x: effect.x + Phaser.Math.Between(-30, 30),
				y: effect.y + Phaser.Math.Between(-200, -100),
				alpha: 0.5,
				duration: animationLifetime,
				ease: "Linear",
				onComplete: (): void => effect.destroy(),
			});
		}
	}

	/**
	 * Vytvorí krátky vizuálny efekt upgradu sídla na zadanej mapovej súradnici a po dokončení tweena ho
	 * automaticky odstráni.
	 *
	 * @param xPos Horizontálna mapová súradnica, na ktorej sa má vytvoriť efekt.
	 * @param yPos Vertikálna mapová súradnica, na ktorej sa má vytvoriť efekt.
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	upgradeAnimation(xPos: number, yPos: number): void {
		// Ak platí podmienka `!useSettingsStore().animationsOn`, vykonáme ochrannú vetvu a aktuálna metóda
		// alebo callback sa následne ukončí.
		if (!useSettingsStore().animationsOn) return;

		const animationLifetime = 600;
		const effect = this.add
			.image(
				xPos * this.tilerepo.TILE_SIZE,
				yPos * this.tilerepo.TILE_SIZE,
				"animations_01",
				"effect_upgrade_settlement.png",
			)
			.setDepth(
				this.mapOverlayBaseDepth + EXPLORE_ANIMATION_DEPTH_CORRECTION,
			);

		// Vytvoríme tween, ktorý plynulo mení uvedené vlastnosti cieľového objektu a po dokončení vykoná
		// cleanup callback.
		this.tweens.add({
			targets: effect,
			y: effect.y - 200,
			alpha: 0.5,
			duration: animationLifetime,
			ease: "Linear",
			onComplete: (): void => effect.destroy(),
		});
	}

	/**
	 * Vytvorí dvojvrstvovú prachovú animáciu stavby s odlišným scale a alpha priebehom a po skončení uvoľní oba
	 * efekty.
	 *
	 * @param xPos Horizontálna mapová súradnica, na ktorej sa má vytvoriť efekt.
	 * @param yPos Vertikálna mapová súradnica, na ktorej sa má vytvoriť efekt.
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	buildAnimation(xPos: number, yPos: number): void {
		// Ak platí podmienka `!useSettingsStore().animationsOn`, vykonáme ochrannú vetvu a aktuálna metóda
		// alebo callback sa následne ukončí.
		if (!useSettingsStore().animationsOn) return;

		const animationLifetime = 800;
		const baseX = xPos * this.tilerepo.TILE_SIZE;
		const baseY = yPos * this.tilerepo.TILE_SIZE;

		// Opakujeme blok s počítadlom `let i = 0`, kým platí `i < 2`; po iterácii vykonáme `i++`.
		for (let i = 0; i < 2; i++) {
			const scaleAmplitude = i === 0 ? 1.5 : 0.4;
			const baseAlpha = i === 0 ? 0.3 : 1;
			const targetAlpha = i === 0 ? 0.2 : 0;
			const effect = this.add
				.image(baseX, baseY, "animations_01", "Dust.png")
				.setAlpha(baseAlpha)
				.setDepth(
					this.mapOverlayBaseDepth +
						EXPLORE_ANIMATION_DEPTH_CORRECTION,
				);

			// Vytvoríme tween, ktorý plynulo mení uvedené vlastnosti cieľového objektu a po dokončení vykoná
			// cleanup callback.
			this.tweens.add({
				targets: effect,
				scaleX: 1 + scaleAmplitude,
				scaleY: 1 + scaleAmplitude,
				alpha: targetAlpha,
				duration: animationLifetime,
				ease: "Linear",
				onComplete: (): void => effect.destroy(),
			});
		}
	}

	/**
	 * Vytvorí sprite demolácie, prehrá predpripravenú frame animáciu a zabezpečí jeho zničenie po
	 * animationcomplete alebo bezpečnostnom timeoute.
	 *
	 * @param xPos Horizontálna mapová súradnica, na ktorej sa má vytvoriť efekt.
	 * @param yPos Vertikálna mapová súradnica, na ktorej sa má vytvoriť efekt.
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	demolishAnimation(xPos: number, yPos: number): void {
		// Ak platí podmienka `!useSettingsStore().animationsOn || !this.demolishAnim`, vykonáme ochrannú vetvu
		// a aktuálna metóda alebo callback sa následne ukončí.
		if (!useSettingsStore().animationsOn || !this.demolishAnim) return;

		const effect = this.add
			.sprite(
				xPos * this.tilerepo.TILE_SIZE,
				yPos * this.tilerepo.TILE_SIZE,
				"animations_01",
				"demolish-01.png",
			)
			.setDepth(
				this.mapOverlayBaseDepth + EXPLORE_ANIMATION_DEPTH_CORRECTION,
			);

		// Uvoľníme už nepotrebný Phaser objekt alebo zdroj a odstránime súvisiacu referenciu, aby nezostal v
		// pamäti ani render liste.
		effect.once("animationcomplete", (): void => effect.destroy());
		effect.play(this.demolishAnim.key);
		// Nastavíme alebo zrušíme časovanú operáciu, aby sa príslušná logika vykonala v správnom čase alebo
		// intervale.
		this.time.delayedCall(900, (): void => {
			// Ak platí podmienka `effect.active`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú
			// príslušný stav.
			if (effect.active) effect.destroy();
		});
	}

	/**
	 * Vytvorí obmedzený počet veľkých oblakov okolo stredu mapy a animuje ich smerom von so zmenou mierky,
	 * rotácie a priehľadnosti.
	 *
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	cloudIntroAnimation(): void {
		const animationLifetime = 4000;
		const baseX = 512 * this.tilerepo.TILE_SIZE;
		const baseY = 512 * this.tilerepo.TILE_SIZE;

		// Opakujeme blok s počítadlom `let i = 0`, kým platí `i < this.MAX_CLOUD_ANIMATION_COUNT`; po iterácii
		// vykonáme `i++`.
		for (let i = 0; i < this.MAX_CLOUD_ANIMATION_COUNT; i++) {
			const startX =
				baseX +
				Phaser.Math.Between(
					-this.cameras.main.width * 2,
					this.cameras.main.width * 2,
				);
			const startY =
				baseY +
				Phaser.Math.Between(
					-this.cameras.main.height * 2,
					this.cameras.main.height * 2,
				);
			const startScale = Phaser.Math.Between(10, 20);
			const effect = this.add
				.image(
					startX,
					startY,
					"animations_01",
					"cloud-flythrough_256x256.png",
				)
				.setScale(startScale)
				.setAlpha(0.4)
				.setDepth(
					this.mapOverlayBaseDepth +
						EXPLORE_ANIMATION_DEPTH_CORRECTION,
				);

			// Vytvoríme tween, ktorý plynulo mení uvedené vlastnosti cieľového objektu a po dokončení vykoná
			// cleanup callback.
			this.tweens.add({
				targets: effect,
				x: startX + (startX > baseX ? 300 : -300),
				y: startY + (startY > baseY ? 300 : -300),
				scale: startScale + Phaser.Math.Between(5, 10),
				rotation: Phaser.Math.FloatBetween(0.5, 1),
				alpha: 0,
				duration: animationLifetime,
				ease: "Linear",
				onComplete: (): void => effect.destroy(),
			});
		}
	}

	/**
	 * Overí, či možno začať odhaľovanie zadaného tile. Pri pevnine vyžaduje odhaleného suseda, pri mori
	 * kontroluje pobrežie, dostupnosť prieskumu, úroveň lodenice a spojenie s lodenicou; neplatný pokus
	 * vysvetlí toast správou.
	 *
	 * @param clickedTile Tile entita, na ktorú hráč klikol alebo ktorú sa pokúša odhaliť.
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	exploreTile(clickedTile: TileEntity): void {
		const { minX, maxX, minY, maxY } =
			this.mapExpansionStore.getEffectiveMapBorderCoordinates();
		// Ak platí podmienka `clickedTile.x < minX || clickedTile.x > maxX || clickedTile.y < minY ||
		// clickedTile.y > maxY`, vykonáme ochrannú vetvu a aktuálna metóda alebo callback sa následne ukončí.
		if (
			clickedTile.x < minX ||
			clickedTile.x > maxX ||
			clickedTile.y < minY ||
			clickedTile.y > maxY
		) {
			const toast = this.toastPool.getForVariant(TileToastType.WARNING);
			toast.showToast(
				translate("ui.out_of_range"),
				{
					messageType: TileToastType.WARNING,
					position: {
						x: clickedTile.x,
						y: clickedTile.y,
					},
				},
				this.cameras.main.zoom,
			);
			return;
		}

		//ak je odhalovane policko akekolvek more
		if (tilerepoSeasFrameNames.has(clickedTile.frameName)) {
			// 1. Kontrola pobrežia.
			let hasCoast = false;
			const directions =
				this.tilerepo.getDefaulNonDiagonalNeighborCoordinates();

			// Prejdeme všetky hodnoty z `directions`; v každej iterácii spracujeme `const dir`.
			for (const dir of directions) {
				const nx = clickedTile.x + dir.dx;
				const ny = clickedTile.y + dir.dy;

				const neighbourTileIndex =
					this.tilerepo.getTileIndexByTileCoordinates({
						x: nx,
						y: ny,
					});
				// Ak platí podmienka `!neighbourTileIndex`, preskočíme zostávajúce spracovanie aktuálnej
				// iterácie cyklu.
				if (!neighbourTileIndex) continue;

				const [neighbourTileId] =
					this.tilerepo.getTileIdsByTileIndex(neighbourTileIndex);
				// Ak platí podmienka `!neighbourTileId`, preskočíme zostávajúce spracovanie aktuálnej iterácie
				// cyklu.
				if (!neighbourTileId) continue;

				const neighbourFrameName =
					this.tilerepo.getFrameNameByTileId(neighbourTileId);
				// Ak platí podmienka `!neighbourFrameName`, preskočíme zostávajúce spracovanie aktuálnej
				// iterácie cyklu.
				if (!neighbourFrameName) continue;

				const neighborVisibility = this.tilerepo.getParam(
					neighbourTileIndex,
					TileEntityParam.VISIBILITY,
				);
				// Ak platí podmienka `neighborVisibility === TileVisibilityType.EXPLORED &&
				// !tilerepoSeasFrameNames.has(neighbourFrameName)`, ukončíme aktuálny cyklus alebo switch
				// vetvu.
				if (
					neighborVisibility === TileVisibilityType.EXPLORED &&
					!tilerepoSeasFrameNames.has(neighbourFrameName)
				) {
					// Susedí s pobrežím
					hasCoast = true;
					break;
				}
			}

			// Ak platí podmienka `hasCoast`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú príslušný
			// stav.
			if (hasCoast) {
				this.executeExploration(clickedTile);
			} else {
				// Ak predchádzajúca podmienka neplatí, vykonáme alternatívnu vetvu.
				const canExploreSea = this.tilerepo.canExploreSeaTile(
					clickedTile,
					false,
				);
				// Ak platí podmienka `canExploreSea`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú
				// príslušný stav.
				if (canExploreSea) {
					this.executeExploration(clickedTile);
				} else {
					// Ak predchádzajúca podmienka neplatí, vykonáme alternatívnu vetvu.
					const shipyardsInSameSea =
						this.tilerepo.shipyardsInSameSea(clickedTile);
					const settlementStore = useSettlementsStore();
					const shipyardRequiredLevel =
						getBuildingConfig(FrameName.SHIPYARD).requirements.get(
							ResourceType.LEVEL,
						) ?? 13;

					// Ak platí podmienka `settlementStore.mainLevel < shipyardRequiredLevel`, vykonáme
					// nasledujúcu vetvu a jej operácie aktualizujú príslušný stav.
					if (settlementStore.mainLevel < shipyardRequiredLevel) {
						const toast = this.toastPool.getForVariant(
							TileToastType.WARNING,
						);
						toast.showToast(
							translate("ui.shipyard_required").replace(
								"{number}",
								shipyardRequiredLevel.toString(),
							),
							{
								messageType: TileToastType.WARNING,
								customMessageTime: 1800,
								position: {
									x: clickedTile.x,
									y: clickedTile.y,
								},
							},
							this.cameras.main.zoom,
						);
						// Ak predchádzajúca podmienka neplatí, overíme alternatívu `!shipyardsInSameSea.length`.
					} else if (!shipyardsInSameSea.length) {
						const toast = this.toastPool.getForVariant(
							TileToastType.WARNING,
						);
						toast.showToast(
							translate("ui.shipyard_not_connected"),
							{
								messageType: TileToastType.WARNING,
								customMessageTime: 1800,
								position: {
									x: clickedTile.x,
									y: clickedTile.y,
								},
							},
							this.cameras.main.zoom,
						);
					} else {
						// Ak predchádzajúca podmienka neplatí, vykonáme alternatívnu vetvu.
						const toast = this.toastPool.getForVariant(
							TileToastType.WARNING,
						);
						toast.showToast(
							translate("ui.shipyard_far"),
							{
								messageType: TileToastType.WARNING,
								position: {
									x: clickedTile.x,
									y: clickedTile.y,
								},
							},
							this.cameras.main.zoom,
						);
					}

					// TODO: toast ->  Potrebuješ lodenicu (dostupná od úrovne %s)
				}
			}
		} else {
			// Ak to NIE je more, overíme či susedí s odhaleným tile (iba hore/dole/vľavo/vpravo)
			const hasValidNeighbor = this.tilerepo.hasExploredNeighbor(
				clickedTile.x,
				clickedTile.y,
			);
			// Ak platí podmienka `!hasValidNeighbor`, vykonáme ochrannú vetvu a aktuálna metóda alebo callback
			// sa následne ukončí.
			if (!hasValidNeighbor) {
				// Aktualizujeme prehrávanie alebo hlasitosť zvuku podľa výsledku aktuálnej hernej operácie.
				SoundService.getInstance().playOutOfRange();
				const toast = this.toastPool.getForVariant(
					TileToastType.WARNING,
				);
				toast.showToast(
					translate("ui.out_of_range"),
					{
						messageType: TileToastType.WARNING,
						position: {
							x: clickedTile.x,
							y: clickedTile.y,
						},
					},
					this.cameras.main.zoom,
				);

				return; // Neodhalíme tile, lebo nie je pripojený k preskúmanému
			}

			// Ak má platného suseda, pokračujeme normálne
			this.executeExploration(clickedTile);
		}
	}

	/**
	 * Vykoná samotný ekonomický a herný krok odhaľovania tile. Vypočíta cenu a zostávajúcu durability so
	 * všetkými upgradmi a super clickerom, odpočíta zdroje, aktualizuje progres a pri dokončení odhalí tile,
	 * vyplatí efekty, synchronizuje segmenty, questy, labely, tutorial a entity callbacky.
	 *
	 * @param clickedTile Tile entita, na ktorú hráč klikol alebo ktorú sa pokúša odhaliť.
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private executeExploration(clickedTile: TileEntity): void {
		const runtimeStore = useRuntimeStore();
		const resourcesStore = useResourcesStore();

		const { minX, maxX, minY, maxY } =
			this.mapExpansionStore.getEffectiveMapBorderCoordinates();
		// Ak platí podmienka `clickedTile.x < minX || clickedTile.x > maxX || clickedTile.y < minY ||
		// clickedTile.y > maxY`, vykonáme ochrannú vetvu a aktuálna metóda alebo callback sa následne ukončí.
		if (
			clickedTile.x < minX ||
			clickedTile.x > maxX ||
			clickedTile.y < minY ||
			clickedTile.y > maxY
		) {
			const toast = this.toastPool.getForVariant(TileToastType.WARNING);
			toast.showToast(
				translate("ui.out_of_range"),
				{
					messageType: TileToastType.WARNING,
					position: {
						x: clickedTile.x,
						y: clickedTile.y,
					},
				},
				this.cameras.main.zoom,
			);
			return;
		}

		// Zapíšeme výsledok operácie do centrálneho store alebo spustíme nadväzujúce prepočty závislého stavu.
		resourcesStore.increase(
			ResourceType.CLICKS_EXPLORATION_TOTAL,
			1,
			false,
		);

		//if we have coins for exploration we will define variable representing duration after
		//click has been processed successufull - nextTileDurability, and current duration - currentTileDurability
		let nextTileDurability: number = 0;
		let clickPrice: number = 0;
		const currentTileDurability: number =
			clickedTile.getParamByName(TileEntityParam.DURABILITY) ?? 0;

		//we will calculate distance from main settlement and calculate total price for exploration for this tile
		const correctionCoef: number = this.tilerepo.MAP_SIZE / 2;
		const distance =
			Math.abs(clickedTile.x - correctionCoef) +
			Math.abs(clickedTile.y - correctionCoef);
		const baseExplorationPrice =
			150 + (1 + distance / 1) * Math.pow((3 + distance) / 4.5, 2.3);

		const totalExplorationPriceForTile = roundNumber(
			baseExplorationPrice *
				clickedTile.exploreCoefficient! *
				(1 -
					getUpgradeCurrentReplaceValueById(UpgradeIds.compass) -
					getUpgradeCurrentReplaceValueById(UpgradeIds.compass2)),
		);

		let superClickerMultiplier = 1;
		let calucaltedPriceForTile: number;

		// Ak platí podmienka `currentTileDurability === 0`, vykonáme nasledujúcu vetvu a jej operácie
		// aktualizujú príslušný stav.
		if (currentTileDurability === 0) {
			//if this is the first time player clicked on tile which is under visibilityType.FOG
			//we will calcualte what will be the durability after finishing this operation(method)
			calucaltedPriceForTile = totalExplorationPriceForTile;
		} else {
			//if this is not the first time player clicked on this tile, we will simply decrease the durability
			//of this tile by current exploration speed. Durability represents number of coins needed to get to 0 value
			calucaltedPriceForTile = currentTileDurability;
		}

		let superClickerTypeUsed = 0;
		// Ak platí podmienka `runtimeStore.superClickerActive`, vykonáme nasledujúcu vetvu a jej operácie
		// aktualizujú príslušný stav.
		if (runtimeStore.superClickerActive) {
			// Zapíšeme výsledok operácie do centrálneho store alebo spustíme nadväzujúce prepočty závislého
			// stavu.
			runtimeStore.setShowUseSuperClickerTooltip(false);

			const userStore = useUserStore();
			// Zapíšeme výsledok operácie do centrálneho store alebo spustíme nadväzujúce prepočty závislého
			// stavu.
			userStore.setSuperClickersUsed(userStore.superClickersUsed + 1);
			superClickerMultiplier = runtimeStore.superClickerType;

			// Ak platí podmienka `useRuntimeStore().superClickerType === SuperClickerEnum.TEN`, vykonáme
			// nasledujúcu vetvu a jej operácie aktualizujú príslušný stav.
			if (useRuntimeStore().superClickerType === SuperClickerEnum.TEN) {
				superClickerMultiplier += getUpgradeCurrentReplaceValueById(
					UpgradeIds.butterfingers,
				);
			}
			nextTileDurability = Math.max(
				0,
				roundNumber(
					calucaltedPriceForTile -
						this.explorationSpeed * superClickerMultiplier,
				),
			);
			// Ak platí podmienka `calucaltedPriceForTile > this.explorationSpeed`, vykonáme nasledujúcu vetvu a
			// jej operácie aktualizujú príslušný stav.
			if (calucaltedPriceForTile > this.explorationSpeed) {
				superClickerTypeUsed = superClickerMultiplier;
			}
		} else {
			// Ak predchádzajúca podmienka neplatí, vykonáme alternatívnu vetvu.
			// Ak platí podmienka `useResourcesStore().superClicker && this.explorationSpeed *
			// SUPER_CLICKER_TOOLTIP_THRESHOLD < calucaltedPriceForTile`, vykonáme nasledujúcu vetvu a jej
			// operácie aktualizujú príslušný stav.
			if (
				useResourcesStore().superClicker &&
				this.explorationSpeed * SUPER_CLICKER_TOOLTIP_THRESHOLD <
					calucaltedPriceForTile
			) {
				// Zapíšeme výsledok operácie do centrálneho store alebo spustíme nadväzujúce prepočty závislého
				// stavu.
				runtimeStore.setShowUseSuperClickerTooltip(true);
			}
			nextTileDurability = Math.max(
				0,
				roundNumber(calucaltedPriceForTile - this.explorationSpeed),
			);
		}

		// Aktualizujeme prehrávanie alebo hlasitosť zvuku podľa výsledku aktuálnej hernej operácie.
		SoundService.getInstance().playSoundCleaningTile();

		// Ak platí podmienka `nextTileDurability === 0 && currentTileDurability !== 0`, vykonáme nasledujúcu
		// vetvu a jej operácie aktualizujú príslušný stav.
		if (nextTileDurability === 0 && currentTileDurability !== 0) {
			clickPrice = currentTileDurability;
			// Aktualizujeme prehrávanie alebo hlasitosť zvuku podľa výsledku aktuálnej hernej operácie.
			SoundService.getInstance().playSoundTileCleaned();
			// Ak predchádzajúca podmienka neplatí, overíme alternatívu `nextTileDurability === 0 &&
			// currentTileDurability === 0`.
		} else if (nextTileDurability === 0 && currentTileDurability === 0) {
			clickPrice = totalExplorationPriceForTile;
			// Aktualizujeme prehrávanie alebo hlasitosť zvuku podľa výsledku aktuálnej hernej operácie.
			SoundService.getInstance().playSoundTileCleaned();
		} else {
			// Ak predchádzajúca podmienka neplatí, vykonáme alternatívnu vetvu.
			clickPrice = this.explorationSpeed * superClickerMultiplier;
		}

		//identification if we have enough coins for this exploration
		if (useResourcesStore().coins < clickPrice) {
			//if not we will show warning message and end this operation
			const toast = this.toastPool.getForVariant(TileToastType.WARNING);
			toast.showToast(
				translate("ui.not_enough_coins_taxes"),
				{
					messageType: TileToastType.WARNING,
					position: {
						x: clickedTile.x,
						y: clickedTile.y,
					},
				},
				this.cameras.main.zoom,
			);
			// Aktualizujeme prehrávanie alebo hlasitosť zvuku podľa výsledku aktuálnej hernej operácie.
			SoundService.getInstance().playGenerickClick();

			return;
		} else {
			// Ak predchádzajúca podmienka neplatí, vykonáme alternatívnu vetvu.
			// Ak platí podmienka `superClickerTypeUsed > 0`, vykonáme nasledujúcu vetvu a jej operácie
			// aktualizujú príslušný stav.
			if (superClickerTypeUsed > 0) {
				this.decreaseSuperClickerResources(superClickerTypeUsed);
			}
		}

		// Ak platí podmienka `currentTileDurability === 0`, vykonáme nasledujúcu vetvu a jej operácie
		// aktualizujú príslušný stav.
		if (currentTileDurability === 0) {
			//if this is the first time we click on this tilem set the durability to calculated value
			clickedTile.setParamByName(
				TileEntityParam.DURABILITY,
				nextTileDurability,
			);
			// Ak predchádzajúca podmienka neplatí, overíme alternatívu `currentTileDurability !== 0 &&
			// nextTileDurability === 0`.
		} else if (currentTileDurability !== 0 && nextTileDurability === 0) {
			//this is the last click, so we will set durability to specific number - 4294967294, we assume
			//this number will be never actual value of coins needed for exploration.
			clickedTile.setParamByName(
				TileEntityParam.DURABILITY,
				DURABILITY_EXPLORED,
			);
			// Ak predchádzajúca podmienka neplatí, overíme alternatívu `currentTileDurability ===
			// DURABILITY_EXPLORED`.
		} else if (currentTileDurability === DURABILITY_EXPLORED) {
			//this is information that the tile has already been exposed, it is more error handling than real logic
			console.error("Unable to explore already explored tile");
			return;
		} else {
			//this is nrmal click, nt first or last one. So we jsut set the durability to already calculated value
			clickedTile.setParamByName(
				TileEntityParam.DURABILITY,
				nextTileDurability,
			);
		}

		// Ak platí podmienka `nextTileDurability >= 1`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú
		// príslušný stav.
		if (nextTileDurability >= 1) {
			//if next durability is more then 0, we will decrease coins and show message to player
			useResourcesStore().decrease(
				ResourceType.COINS,
				clickPrice,
				"map: exploration",
			);
			// Ak platí podmienka `!tilerepoSettlementCastleBuildingsFrameNames.has( clickedTile.frameName, )`,
			// vykonáme nasledujúcu vetvu a jej operácie aktualizujú príslušný stav.
			if (
				!tilerepoSettlementCastleBuildingsFrameNames.has(
					clickedTile.frameName,
				)
			) {
				// Ak platí podmienka `totalExplorationPriceForTile`, vykonáme nasledujúcu vetvu a jej operácie
				// aktualizujú príslušný stav.
				if (totalExplorationPriceForTile) {
					const toast = this.toastPool.getForVariant(
						TileToastType.EXPLORE,
					);
					toast.showToast(
						clickPrice * -1,
						{
							messageType: TileToastType.EXPLORE,
							position: {
								x: clickedTile.x,
								y: clickedTile.y,
							},

							iconKey: "icon-coins",
							textTwo: useSettingsStore().showExplorationProgress
								? `${translate("ui.progress")}: ${formatNumber(totalExplorationPriceForTile - nextTileDurability, true, true)}/${formatNumber(totalExplorationPriceForTile, true, true)}`
								: "",
							topCorrection: 150,
						},
						this.cameras.main.zoom,
					);
				} else {
					// Ak predchádzajúca podmienka neplatí, vykonáme alternatívnu vetvu.
					const toast = this.toastPool.getForVariant(
						TileToastType.SUCCESS,
					);
					toast.showToast(
						translate("ui.free"),
						{
							messageType: TileToastType.SUCCESS,
							position: {
								x: clickedTile.x,
								y: clickedTile.y,
							},
						},
						this.cameras.main.zoom,
					); // TODO dynamic value from user
				}
			}
			// Synchronizujeme sprievodný vizuálny prvok s aktuálnym stavom tile alebo entity.
			this.updateProgressBar(
				clickedTile.x,
				clickedTile.y,
				totalExplorationPriceForTile,
				nextTileDurability,
			);
			this.exploreClaimAnimation(clickedTile.x, clickedTile.y);
		} else {
			// Ak predchádzajúca podmienka neplatí, vykonáme alternatívnu vetvu.
			let lastClickCost: number;
			//Tento if repreyentuje ak na klik dokazem odhalit cely tile
			if (currentTileDurability === 0 && nextTileDurability === 0) {
				lastClickCost = clickPrice;
			} else {
				//Tento else repreyentuje posledny klik pri odhalovani tile ak ale bolo treba viac ako 1 klik
				lastClickCost = currentTileDurability; // Koľko ešte zostávalo do preskúmania?
			}

			//zníž coiny o to, čo stálo odhalenie tile posledným klikom
			useResourcesStore().decrease(
				ResourceType.COINS,
				lastClickCost,
				"map: exploration",
			);

			//zisti či sú to chestky a ak hej daj toast message ze si dostal odmenu
			//POZOR odmena samotna sa pripocitava inde
			const chest = this.getChestOnLocation(clickedTile.x, clickedTile.y);

			// Ak platí podmienka `chest`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú príslušný stav.
			if (chest) {
				const toast = this.toastPool.getForVariant(
					TileToastType.INCOME,
				);
				const upgradeBonus =
					1 +
					getUpgradeCurrentReplaceValueById(UpgradeIds.inflation) +
					getUpgradeCurrentReplaceValueById(UpgradeIds.geyser);

				toast.showToast(
					ceilNumber(chest.reward.coins * upgradeBonus),
					{
						messageType: TileToastType.INCOME,
						position: {
							x: chest.x,
							y: chest.y,
						},
						messageScale: true,
						topCorrection: 425,
						customMessageTime: 2000,

						iconKey: "icon-coins",
					},
					this.cameras.main.zoom,
				);
			} else {
				//ak sa na danom tile ktorý práve ideme odhaliť chestka nenachádza tak ukážeme štadardný toest message
				if (
					!tilerepoSettlementCastleBuildingsFrameNames.has(
						clickedTile.frameName,
					)
				) {
					// Ak platí podmienka `totalExplorationPriceForTile &&
					// !tilerepoSettlementCastleBuildingsFrameNames.has( clickedTile.frameName, )`, vykonáme
					// nasledujúcu vetvu a jej operácie aktualizujú príslušný stav.
					if (
						totalExplorationPriceForTile &&
						!tilerepoSettlementCastleBuildingsFrameNames.has(
							clickedTile.frameName,
						)
					) {
						const toast = this.toastPool.getForVariant(
							TileToastType.EXPLORE,
						);
						toast.showToast(
							lastClickCost * -1,
							{
								messageType: TileToastType.EXPLORE,
								position: {
									x: clickedTile.x,
									y: clickedTile.y,
								},

								iconKey: "icon-coins",
								textTwo: useSettingsStore()
									.showExplorationProgress
									? `${translate("ui.progress")}: ${formatNumber(totalExplorationPriceForTile - nextTileDurability, true, true)}/${formatNumber(totalExplorationPriceForTile, true, true)}`
									: "",
								topCorrection: 150,
							},
							this.cameras.main.zoom,
						);
					} else {
						// Ak predchádzajúca podmienka neplatí, vykonáme alternatívnu vetvu.
						const toast = this.toastPool.getForVariant(
							TileToastType.SUCCESS,
						);
						toast.showToast(
							translate("ui.free"),
							{
								messageType: TileToastType.SUCCESS,
								position: {
									x: clickedTile.x,
									y: clickedTile.y,
								},
							},
							this.cameras.main.zoom,
						); // TODO dynamic value from user
					}
				}

				// zobrazenie toastu po odhaleni tilu - mimo settlementov a poi
				if (
					!tilerepoPoiFrameNames.has(clickedTile?.frameName) &&
					!tilerepoSettlementEmptyFrameNames.has(
						clickedTile.frameName,
					) &&
					!tilerepoSettlementCastleBuildingsFrameNames.has(
						clickedTile.frameName,
					)
				) {
					setTimeout((): void => {
						const toast = this.toastPool.getForVariant(
							TileToastType.REVEAL,
						);
						toast.showToast(
							1,
							{
								messageType: TileToastType.REVEAL,
								position: {
									x: clickedTile.x,
									y: clickedTile.y,
								},
								topCorrection: 425,
								messageScale: true,
								iconKey: "icon-coins",
								textTwo: "/10s",
							},
							this.cameras.main.zoom,
						);
					}, 250);
				}
			}

			// Ak je uz tile odhalený, nastavíme jeho durabilitu na DURABILITY_EXPLORED visibilitu na EXPLORED a o kusok neskor zbehne re-render funkcia.
			if (nextTileDurability === 0) {
				clickedTile.setParamByName(
					TileEntityParam.DURABILITY,
					DURABILITY_EXPLORED,
				);
				clickedTile.setParamByName(
					TileEntityParam.VISIBILITY,
					TileVisibilityType.EXPLORED,
				);
				// pridaj 1 coin za kazde odhalene policko do celkových príjmov
				useResourcesStore().increase(
					ResourceType.COINS_INCOME,
					1,
					"map: exploration",
				);
				// Zapíšeme výsledok operácie do centrálneho store alebo spustíme nadväzujúce prepočty závislého
				// stavu.
				useResourcesStore().increase(
					ResourceType.COINS_UNCAPPED_INCOME,
					1,
					"map: exploration",
				);

				const sm = useSegmentManager();
				sm.markSegmentAsDirtyByTileIndex(clickedTile.index);
				sm.run(true);

				if (clickedTile.isBlockedByEnemy)
					useSiegeSystemRepository().rerenderBattlefieldLabelByTileIndex(
						clickedTile.index,
					);
			}

			this.exploreClaimAnimation(clickedTile.x, clickedTile.y, true);

			// Po zmene herného stavu znovu synchronizujeme vizuálnu reprezentáciu dotknutého tile.
			this.rerenderTile(clickedTile.index);

			this.removeProgressBar(clickedTile.index);

			this.tilerepo.exploreNewTiles(clickedTile);

			const questStore = useQuestStore();
			// Vyhľadáme prvú položku z kolekcie `questStore.getActiveQuests`, ktorá spĺňa callback podmienku.
			const activePlagueQuest = questStore.getActiveQuests.find(
				(q: QuestActive): boolean =>
					q.objective.action === QuestObjectiveActions.CLEAR_PLAGUE,
			);
			// Ak platí podmienka `activePlagueQuest`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú
			// príslušný stav.
			if (activePlagueQuest) {
				plagueRepository.changeSkin(clickedTile.index);
			}

			// Synchronizujeme sprievodný vizuálny prvok s aktuálnym stavom tile alebo entity.
			this.updateTileLabel(
				{ x: clickedTile.x, y: clickedTile.y },
				TileLabelSkin.SETTLEMENT_HIGHLIGHT,
				TileLabelAction.DELETE,
			);
			// Synchronizujeme sprievodný vizuálny prvok s aktuálnym stavom tile alebo entity.
			this.updateTileLabel(
				{ x: clickedTile.x, y: clickedTile.y },
				TileLabelSkin.SPECIAL_LOCATION,
				TileLabelAction.DELETE,
			);
			this.updateTileLabels();

			const revealedSettlement =
				useUserStore().revealedSettlementFromOfferCoordinates;
			// Ak platí podmienka `revealedSettlement && revealedSettlement.x === clickedTile.x &&
			// revealedSettlement.y === clickedTile.y`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú
			// príslušný stav.
			if (
				revealedSettlement &&
				revealedSettlement.x === clickedTile.x &&
				revealedSettlement.y === clickedTile.y
			) {
				// Zapíšeme výsledok operácie do centrálneho store alebo spustíme nadväzujúce prepočty závislého
				// stavu.
				useUserStore().setRevealedSettlementFromOfferCoordinates(null);
			}
			// Ak platí podmienka `useRuntimeStore().searchToolActiveFrameName?.includes( clickedTile.frameName,
			// )`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú príslušný stav.
			if (
				useRuntimeStore().searchToolActiveFrameName?.includes(
					clickedTile.frameName,
				)
			) {
				// Emitujeme udalosť do aplikačného event busu, aby sa o zmene dozvedeli ostatné moduly.
				this.emitter.emit(EmitterEvents.PhaserUpdateTileLabel, {
					coordinates: { x: clickedTile.x, y: clickedTile.y },
					skin: TileLabelSkin.SEARCH_TOOL,
					action: TileLabelAction.CREATE,
				});
			}

			//tutorial krok 2-3-4. vyfarbickuj pulzujucimi kruhmi cestu k odhaleniu noveho mesta
			//+ daj Xka tam kde je to nove mesto
			if (useQuestStore().tutorialActive) {
				// Ak platí podmienka `this.tutorialRevealQueue.length === 0 && this.tutorialConvertXsToPulses
				// === false`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú príslušný stav.
				if (
					this.tutorialRevealQueue.length === 0 &&
					this.tutorialConvertXsToPulses === false
				) {
					this.handleTutorialSteps(2, clickedTile);
					// Ak predchádzajúca podmienka neplatí, overíme alternatívu `this.tutorialConvertXsToPulses`.
				} else if (this.tutorialConvertXsToPulses) {
					this.handleTutorialSteps(4, clickedTile);
				} else {
					// Ak predchádzajúca podmienka neplatí, vykonáme alternatívnu vetvu.
					this.handleTutorialSteps(3, clickedTile);
				}
			}

			// Ak platí podmienka `"onReveal" in clickedTile`, vykonáme nasledujúcu vetvu a jej operácie
			// aktualizujú príslušný stav.
			if ("onReveal" in clickedTile)
				(clickedTile as TileEntityWithOnRevealMethod).onReveal();
			// Ak platí podmienka `"onActivate" in clickedTile`, vykonáme nasledujúcu vetvu a jej operácie
			// aktualizujú príslušný stav.
			if ("onActivate" in clickedTile)
				(clickedTile as TileEntityWithOnActivateMethod).onActivate();

			// Ak platí podmienka `tilerepoSettlementCastleBuildingsFrameNames.has( clickedTile?.frameName, )`,
			// vykonáme nasledujúcu vetvu a jej operácie aktualizujú príslušný stav.
			if (
				tilerepoSettlementCastleBuildingsFrameNames.has(
					clickedTile?.frameName,
				)
			) {
				const newSettlementLabel = new TileLabel(
					this,
					{
						x: clickedTile.x,
						y: clickedTile.y,
					},
					true,
					(
						clickedTile as SettlementCastleBuildingTileEntity
					).level.toString(),
					clickedTile.index === MAIN_SETTLEMENT_TILE_INDEX
						? TileLabelType.MAIN_SETTLEMENT
						: !clickedTile.isConnectedToMainSettlement
							? TileLabelType.SETTLEMENT_NOT_CONNECTED
							: TileLabelType.SETTLEMENT,
				);
				// Synchronizujeme príslušnú cache so zmeneným stavom, aby ďalšie lookupy a rendering používali
				// aktuálne údaje.
				this.tileLabelsCache.set(
					TileLabel.formatKey(TileLabelSkin.LEVEL, clickedTile.index),
					newSettlementLabel,
				);
			}

			// Oznámime quest systému vykonanú hernú akciu, aby mohol aktualizovať aktívne ciele.
			QuestRepository.getInstance().checkQuestsAfterTileReveal(
				clickedTile,
			);
			// Tu sa pouziva metoda z tilerepa, lebo ona resolvuje aj dependencies tych tiles.
			this.tilerepo.markSegmentAsDirty({
				tileIndex: clickedTile.index,
				nextFrameName: clickedTile.frameName,
			});
			useSegmentManager().run(true);
		}
	}

	/**
	 * Zmeria vzdialenosť kamery od stredu mapy a pri prekročení povoleného tutorialového limitu ju presunie
	 * späť ku kapitálu.
	 *
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	checkCameraPosition(): void {
		const { TILE_SIZE } = this.tilerepo;
		const centerX = (this.tilerepo.MAP_SIZE / 2) * TILE_SIZE;
		const centerY = (this.tilerepo.MAP_SIZE / 2) * TILE_SIZE;
		const distanceThreshold = 20 * TILE_SIZE; // The threshold distance from the center of the map
		const cameraX = this.camera?.scrollX ?? 0;
		const cameraY = this.camera?.scrollY ?? 0;
		const distanceFromCenter = Phaser.Math.Distance.Between(
			cameraX,
			cameraY,
			centerX,
			centerY,
		);
		// Ak platí podmienka `distanceFromCenter > distanceThreshold`, vykonáme nasledujúcu vetvu a jej
		// operácie aktualizujú príslušný stav.
		if (distanceFromCenter > distanceThreshold) {
			this.goToCapital();
		}
	}

	/**
	 * Rozdelí klik na tile podľa jeho visibility stavu. Explored tile otvorí detail alebo aktivuje entitu, fog
	 * tile pošle do exploration flow a black tile zobrazí varovanie; zároveň uplatní tutorialové obmedzenia,
	 * gems-mine zber a click animáciu.
	 *
	 * @param clickedTile Tile entita, na ktorú hráč klikol alebo ktorú sa pokúša odhaliť.
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	clickTile(clickedTile: TileEntity): void {
		// Ak platí podmienka `!clickedTile`, vykonáme ochrannú vetvu a aktuálna metóda alebo callback sa
		// následne ukončí.
		if (!clickedTile) return;

		// Ak platí podmienka `clickedTile.exploredTimestamp`, vykonáme nasledujúcu vetvu a jej operácie
		// aktualizujú príslušný stav.
		if (clickedTile.exploredTimestamp) {
			const secondsElapsed =
				(new Date().getTime() - clickedTile.exploredTimestamp) / 1_000;
			// Ak platí podmienka `secondsElapsed < 0.5`, vykonáme ochrannú vetvu a aktuálna metóda alebo
			// callback sa následne ukončí.
			if (secondsElapsed < 0.5) return;
			clickedTile.exploredTimestamp = null;
		}
		const tileVisibility = clickedTile.getParamByName(
			TileEntityParam.VISIBILITY,
		);

		//TUTORIAL KONTROLA – povolený klik len na tile s grafickým markerom
		if (
			useQuestStore().tutorialActive &&
			useQuestStore().tutorialStep < 8
		) {
			const key = `${clickedTile.x},${clickedTile.y}`;
			// Ak platí podmienka `!this.graphicsObjects.has(key)`, vykonáme ochrannú vetvu a aktuálna metóda
			// alebo callback sa následne ukončí.
			if (!this.graphicsObjects.has(key)) return;
		}

		const chest = this.getChestOnLocation(clickedTile.x, clickedTile.y);

		// Ak platí podmienka `clickedTile.frameName === FrameName.GEMS_MINE &&
		// useUserStore().gemMinesClaimable`, vykonáme ochrannú vetvu a aktuálna metóda alebo callback sa
		// následne ukončí.
		if (
			clickedTile.frameName === FrameName.GEMS_MINE &&
			useUserStore().gemMinesClaimable
		) {
			this.tilerepo.collectAllGemsFromGemMines({
				x: clickedTile.x,
				y: clickedTile.y,
			});
			return;
		}
		// Rozvetvíme spracovanie podľa hodnoty `tileVisibility`; každý case rieši samostatný variant.
		switch (tileVisibility) {
			// Spracujeme hodnotu `TileVisibilityType.EXPLORED` a po vykonaní príslušných operácií vetvu
			// ukončíme alebo necháme pokračovať podľa kódu.
			case TileVisibilityType.EXPLORED:
				// Ak platí podmienka `!useQuickToolStore().active && !chest`, vykonáme nasledujúcu vetvu a jej
				// operácie aktualizujú príslušný stav.
				if (!useQuickToolStore().active && !chest) {
					// Emitujeme udalosť do aplikačného event busu, aby sa o zmene dozvedeli ostatné moduly.
					this.emitter.emit(
						EmitterEvents.PhaserTileDetailOpen,
						clickedTile,
					);
				}
				this.selectTile(clickedTile);

				// Ak platí podmienka `(tilerepoSettlementCastleBuildingsFrameNames.has( clickedTile.frameName,
				// ) || tilerepoGemsMinesFrameNames.has( clickedTile.f…`, vykonáme nasledujúcu vetvu a jej
				// operácie aktualizujú príslušný stav.
				if (
					(tilerepoSettlementCastleBuildingsFrameNames.has(
						clickedTile.frameName,
					) ||
						tilerepoGemsMinesFrameNames.has(
							clickedTile.frameName,
						)) &&
					"onClick" in clickedTile
				) {
					(clickedTile as TileEntityWithOnClickMethod).onClick();
				}

				//tutorial krok 6. urob "tutorialove" cesty aby si userovi ukazal kde ma stavat cestu
				//+ daj pulzujuci kruh nad prvou tutorialovou cestou aby vedel ze tam má kliknúť
				if (useQuestStore().tutorialActive) {
					this.handleTutorialSteps(5, clickedTile);
				}
				break;
			// Spracujeme hodnotu `TileVisibilityType.FOG` a po vykonaní príslušných operácií vetvu ukončíme
			// alebo necháme pokračovať podľa kódu.
			case TileVisibilityType.FOG:
				this.deselectAndRedirect();
				this.exploreTile(clickedTile);
				break;
			// Spracujeme hodnotu `TileVisibilityType.BLACK` a po vykonaní príslušných operácií vetvu ukončíme
			// alebo necháme pokračovať podľa kódu.
			case TileVisibilityType.BLACK: {
				this.deselectAndRedirect();
				// Aktualizujeme prehrávanie alebo hlasitosť zvuku podľa výsledku aktuálnej hernej operácie.
				SoundService.getInstance().playOutOfRange();
				const toast = this.toastPool.getForVariant(
					TileToastType.WARNING,
				);
				toast.showToast(
					translate("ui.out_of_range"),
					{
						messageType: TileToastType.WARNING,
						duration: 0,
						position: {
							x: clickedTile.x,
							y: clickedTile.y,
						},
					},
					this.cameras.main.zoom,
				);
				break;
			}
			// Spracujeme defaultný variant a po vykonaní príslušných operácií vetvu ukončíme alebo necháme
			// pokračovať podľa kódu.
			default:
				break;
		}

		// Ak platí podmienka `tileVisibility !== TileVisibilityType.BLACK`, vykonáme nasledujúcu vetvu a jej
		// operácie aktualizujú príslušný stav.
		if (tileVisibility !== TileVisibilityType.BLACK) {
			// TODO: not while in init
			this.animateClick({
				x: clickedTile.x,
				y: clickedTile.y,
			});
		}
	}

	/**
	 * Oznámi UI, aby zavrelo detail alebo sa vrátilo na základnú route, a následne odstráni aktuálny mapový
	 * výber.
	 *
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	deselectAndRedirect(): void {
		// this closes the modal window, reset to base url
		this.emitter.emit(EmitterEvents.PhaserRedirect);
		// but we still need to remove the last selected tile from the scene
		this.deselectTile();
	}

	/**
	 * Prepočíta aktuálnu silu exploration kliku zo základnej hodnoty a hodnoty uloženej v resources store. Pri
	 * chýbajúcej store hodnote použije konfiguračný násobiteľ.
	 *
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private updateExplorationSpeed(): void {
		const { explorationSpeed: currentValue } = useResourcesStore();

		// Aktualizujeme interný stav scény podľa výsledku aktuálnej operácie: `this.explorationSpeed =
		// this.explorationSpeedDefault * (!currentValue ? this.explorationSpeedConf : currentValue);`
		this.explorationSpeed =
			this.explorationSpeedDefault *
			(!currentValue ? this.explorationSpeedConf : currentValue);
	}

	/**
	 * Vytvorí alebo prekreslí progress bar odhaľovania či plague stavu na zadanej súradnici. Prispôsobí jeho
	 * mierku zoomu pri sídlach, vypočíta percento, polohu a farebný stupeň a vykreslí rám aj vyplnenú časť.
	 *
	 * @param x Horizontálna mapová súradnica tile.
	 * @param y Vertikálna mapová súradnica tile.
	 * @param total Celková referenčná hodnota progressu alebo maximálne HP.
	 * @param current Aktuálna zostávajúca hodnota durability alebo aktuálne HP.
	 * @param plague Ak je true, progress a farebná škála sa interpretujú ako plague/HP indikátor. Voliteľný
	 *               parameter.
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private updateProgressBar(
		x: number,
		y: number,
		total: number,
		current: number,
		{ plague, siege }: { plague?: boolean; siege?: boolean } = {},
	): void {
		const { TILE_SIZE } = this.tilerepo;
		let scale = 1;
		let yOffset = 0;
		const cameraZoom = this.camera?.zoom || 1;
		const dpr = window.devicePixelRatio;
		const SCALE_COEFFICIENT = 0.17 * dpr;
		const entity = this.tilerepo.getTileEntityByTileCoordinates({
			x,
			y,
		});

		// Ak platí podmienka `entity?.frameName &&
		// tilerepoSettlementCastleBuildingsFrameNames.has(entity.frameName)`, vykonáme nasledujúcu vetvu a jej
		// operácie aktualizujú príslušný stav.
		if (
			entity?.frameName &&
			tilerepoSettlementCastleBuildingsFrameNames.has(entity.frameName)
		) {
			// Ak platí podmienka `cameraZoom < tileLabelZoomConfig[3]`, vykonáme nasledujúcu vetvu a jej
			// operácie aktualizujú príslušný stav.
			if (cameraZoom < tileLabelZoomConfig[3]) {
				scale = Math.max(
					tileLabelZoomConfig[0],
					(1 / Math.max(cameraZoom, tileLabelZoomConfig[3])) *
						SCALE_COEFFICIENT,
				);
				yOffset = (TILE_SIZE / 2) * scale - TILE_SIZE / 2 + 32 * scale;
				// Ak predchádzajúca podmienka neplatí, overíme alternatívu `cameraZoom < tileLabelZoomConfig[2]`.
			} else if (cameraZoom < tileLabelZoomConfig[2]) {
				scale = (1 / cameraZoom) * SCALE_COEFFICIENT;
				yOffset = (TILE_SIZE / 2) * scale - TILE_SIZE / 2 + 32 * scale;
			}
		}

		const tileIndex = this.tilerepo.getTileIndexByTileCoordinates({
			x,
			y,
		});

		// Ak platí podmienka `!this.progressBars.has(tileIndex)`, vykonáme nasledujúcu vetvu a jej operácie
		// aktualizujú príslušný stav.
		if (!this.progressBars.has(tileIndex)) {
			const progressBar = this.add
				.graphics()
				.setDepth(
					this.mapOverlayBaseDepth + PROGRESS_BAR_DEPTH_CORRECTION,
				)
				.setName("progress-bar");
			this.progressBars.set(tileIndex, {
				bar: progressBar,
				progress: -1,
			});
		}
		const progressBar = this.progressBars.get(tileIndex)!;
		progressBar.bar.clear();

		const barWidth = TILE_SIZE * scale;
		const barHeight = 16 * scale;
		const barBorderWidth = 2 * scale; // kvoli tomuto su nizsie tie divoke pocty, lebo polka borderu ide von a polka dnu
		const progressReverse =
			total > 0 ? Math.min(1, Math.max(0, current / total)) : 0;
		const progress = plague
			? progressReverse
			: Math.max(0, (total - current) / total);
		const worldX = x * TILE_SIZE;
		const worldY = y * TILE_SIZE - TILE_SIZE * 0.5 - yOffset;
		if (siege) {
			progressBar.bar.lineStyle(barBorderWidth, 0xf4d278, 1);
		} else {
			progressBar.bar.lineStyle(barBorderWidth, 0x000000, 0.8);
		}

		progressBar.bar.strokeRect(
			worldX - barWidth / 2 + barBorderWidth,
			worldY + barBorderWidth,
			barWidth - barBorderWidth * 2,
			barHeight,
		);

		if (siege) {
			let progressColorTop = 0x000000;
			let progressColorBottom = 0x000000;

			if (progress < 0.25) {
				progressColorTop = 0xcf0032;
				progressColorBottom = 0x9a0025;
			} else if (progress < 0.5) {
				progressColorTop = 0xcf5d00;
				progressColorBottom = 0x954504;
			} else if (progress < 0.75) {
				progressColorTop = 0xa9b30a;
				progressColorBottom = 0x676d03;
			} else {
				progressColorTop = 0x19b345;
				progressColorBottom = 0x0d6c27;
			}

			progressBar.bar.fillGradientStyle(
				progressColorTop, // top
				progressColorTop, // top
				progressColorBottom, // bottom
				progressColorBottom, // bottom
				1,
				1,
				1,
				1,
			);
		} else {
			let progressColor = 0xdeb751;

			if (plague) {
				if (progress < 0.2) {
					progressColor = 0x555555;
				} else if (progress < 0.4) {
					progressColor = 0x777777;
				} else if (progress < 0.6) {
					progressColor = 0x999999;
				} else if (progress < 0.8) {
					progressColor = 0x8ba889;
				} else {
					progressColor = 0x76a673;
				}
			} else {
				// Ak predchádzajúca podmienka neplatí, vykonáme alternatívnu vetvu.
				// Ak platí podmienka `progress < 0.2`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú
				// príslušný stav.
				if (progress < 0.2) {
					progressColor = 0xb84034;
					// Ak predchádzajúca podmienka neplatí, overíme alternatívu `progress < 0.4`.
				} else if (progress < 0.4) {
					progressColor = 0xc57d38;
					// Ak predchádzajúca podmienka neplatí, overíme alternatívu `progress < 0.6`.
				} else if (progress < 0.6) {
					progressColor = 0xd6b860;
					// Ak predchádzajúca podmienka neplatí, overíme alternatívu `progress < 0.8`.
				} else if (progress < 0.8) {
					progressColor = 0x9ac872;
				} else {
					// Ak predchádzajúca podmienka neplatí, vykonáme alternatívnu vetvu.
					progressColor = 0x49a147;
				}
			}

			progressBar.bar.fillStyle(progressColor, 1);
		}

		progressBar.bar.fillRect(
			worldX - barWidth / 2 + barBorderWidth * 1.5,
			worldY + barBorderWidth * 1.5,
			(barWidth - barBorderWidth * 2 * 1.5) * progress,
			barHeight - barBorderWidth,
		);

		if (siege) {
			// Toto nevyzera dobre, ma to z nejakeho dovodu inu hrubku ako border okolo celeho hp boxu.
			// progressBar.lineStyle(barBorderWidth, 0xf4d278, 1);
			// progressBar.strokeRect(
			// 	worldX - barWidth / 2 + barBorderWidth * 1.5,
			// 	worldY + barBorderWidth * 1.5,
			// 	(barWidth - barBorderWidth * 2 * 1.5) * progress,
			// 	barHeight - barBorderWidth,
			// );

			progressBar.bar.fillStyle(0x000000, 1);
			progressBar.bar.fillRect(
				worldX -
					barWidth / 2 +
					barBorderWidth * 1.5 +
					(barWidth - barBorderWidth * 2 * 1.5) * progress,
				worldY + barBorderWidth * 1.5,
				(barWidth - barBorderWidth * 2 * 1.5) * (1 - progress),
				barHeight - barBorderWidth,
			);
		}
	}

	/**
	 * Vyhľadá progress bar podľa indexu tile, zničí jeho Graphics objekt a odstráni ho z cache.
	 *
	 * @param tileIndex Lineárny index tile používaný tile repository.
	 */
	private removeProgressBar(tileIndex: TileIndex): void {
		const progressBar = this.progressBars.get(tileIndex) as
			| {
					bar: Phaser.GameObjects.Graphics;
					progress: number;
			  }
			| undefined;
		if (progressBar) {
			progressBar.bar.destroy();
			this.progressBars.delete(tileIndex);
		}
	}

	/**
	 * Vyhľadá aktívnu castle entitu na zadanej súradnici a synchronizuje jej progress bar s hodnotou settlement
	 * progress.
	 *
	 * @param coordinates Objekt s mapovými súradnicami settlement castle tile.
	 */
	private updateTileProgressBar({ x, y }: TileCoordinates): void {
		const index = this.tilerepo.getTileIndexByTileCoordinates({
			x,
			y,
		});
		const entity = this.tilerepo.getTileEntityFactoriesByTileIndex(
			index,
		)[2]() as SettlementCastleBuildingTileEntity | null;
		// Ak platí podmienka `!entity || !entity.isActive`, vykonáme ochrannú vetvu a aktuálna metóda alebo
		// callback sa následne ukončí.
		if (
			!entity ||
			(!entity.isActive &&
				!(
					tilerepoFightableEnemiesFrameNames.has(entity.frameName) ||
					tilerepoFriendlyUnitsFrameNames.has(entity.frameName)
				))
		)
			return;

		if (this.progressBars.get(index)?.progress ?? 0 < 0) {
			this.progressBars.get(index)!.progress = entity.progress;
		}
		const progress = this.progressBars.get(index)?.progress ?? 0;
		this.updateProgressBar(entity.x, entity.y, 1, 1 - progress);
	}

	private updateFriendlyUnitProgressBar({ x, y }: TileCoordinates): void {
		const index = this.tilerepo.getTileIndexByTileCoordinates({
			x,
			y,
		});

		const isVisible =
			this.tilerepo.getParam(index, TileEntityParam.VISIBILITY) ===
			TileVisibilityType.EXPLORED;
		if (!isVisible) {
			this.removeProgressBar(index);
			return;
		}

		if (!useSiegeSystemRepository().isActive) {
			this.removeProgressBar(index);
			return;
		}

		const entity = this.tilerepo.getTileEntityFactoriesByTileIndex(
			index,
		)[2]() as TileEntity | null;
		if (!entity) {
			this.removeProgressBar(index);
			return;
		}
		if (entity.progress === null || isNaN(entity.progress)) {
			this.removeProgressBar(index);
			return;
		}

		this.updateProgressBar(entity.x, entity.y, 1, 1 - entity.progress, {
			siege: true,
		});
	}

	private updateFightableEnemyProgressBar({ x, y }: TileCoordinates): void {
		this.updateFriendlyUnitProgressBar({ x, y });
	}

	/**
	 * Odstráni predchádzajúci selektor, vytvorí nový obrázkový selektor nad zadaným tile a voliteľne ho krátko
	 * zmenší tweenom. Následne zobrazí range zvýraznenie vybranej entity.
	 *
	 * @param coordinates Objekt s mapovými súradnicami tile, ktorý sa má označiť.
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private addSelector({ x, y }: TileCoordinates): void {
		const { TILE_SIZE } = this.tilerepo;

		this.removeSelector();

		const START_SCALE = 1.1;

		const TARGET_SCALE = 1.0;

		const ANIMATION_DURATION = 200;

		// Aktualizujeme interný stav scény podľa výsledku aktuálnej operácie: `this.clickSelector = this.add
		// .image(x * TILE_SIZE, y * TILE_SIZE, "tile-selector") .setOrigin(0.5)
		// .setDepth(this.mapOverlayBaseDepth + SELECTOR_DEPTH_CORRECTION) .setName("tile-…`
		this.clickSelector = this.add
			.image(x * TILE_SIZE, y * TILE_SIZE, "tile-selector")
			.setOrigin(0.5)
			.setDepth(this.mapOverlayBaseDepth + SELECTOR_DEPTH_CORRECTION)
			.setName("tile-selector")
			.setScale(START_SCALE);

		// Ak platí podmienka `useSettingsStore().animationsOn`, vykonáme nasledujúcu vetvu a jej operácie
		// aktualizujú príslušný stav.
		if (useSettingsStore().animationsOn) {
			// Vytvoríme tween, ktorý plynulo mení uvedené vlastnosti cieľového objektu a po dokončení vykoná
			// cleanup callback.
			this.tweens.add({
				targets: this.clickSelector,
				scale: TARGET_SCALE,
				ease: "Power1",
				duration: ANIMATION_DURATION,
			});
		}

		this.addRangeSelector(x, y);
	}

	// TODO: mozme len dat alpha = 0/1 a menit x/y
	/**
	 * Zničí aktuálny click selektor, vyčistí referenciu a odstráni aj všetky range zvýraznenia.
	 *
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private removeSelector(): void {
		// Uvoľníme už nepotrebný Phaser objekt alebo zdroj a odstránime súvisiacu referenciu, aby nezostal v
		// pamäti ani render liste.
		this.clickSelector?.destroy();
		// Aktualizujeme interný stav scény podľa výsledku aktuálnej operácie: `this.clickSelector = null;`
		this.clickSelector = null;

		this.removeRangeSelector();
	}

	/**
	 * Prejde všetky Graphics objekty range zvýraznenia, zničí ich a resetuje pracovný zoznam.
	 *
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private removeRangeSelector(): void {
		// Opakujeme blok s počítadlom `let i = 0`, kým platí `i < this.rangeHighlights.length`; po iterácii
		// vykonáme `i++`.
		for (let i = 0; i < this.rangeHighlights.length; i++) {
			const rangeHighlight: Phaser.GameObjects.Graphics =
				this.rangeHighlights[i];
			// Uvoľníme už nepotrebný Phaser objekt alebo zdroj a odstránime súvisiacu referenciu, aby nezostal
			// v pamäti ani render liste.
			rangeHighlight.destroy();
		}
		// Aktualizujeme interný stav scény podľa výsledku aktuálnej operácie: `this.rangeHighlights = [];`
		this.rangeHighlights = [];
	}

	/**
	 * Zostaví presný zoznam atlasových frame names, ktoré majú vizuálne tvoriť zadaný tile. Rieši vrstvy tile
	 * ID, varianty hradov, palisád a pobrežia, FOW prípony a automaticky dopĺňa existujúce _Top.png presahy.
	 *
	 * @param coordinates Objekt s mapovými súradnicami, pre ktoré sa zostavuje výsledný zoznam framov.
	 * @returns Usporiadané pole base aj _Top.png frame names, ktoré majú tvoriť výsledný tile.
	 */
	private getCorrectFrameNamesByCoordinates({
		x,
		y,
	}: TileCoordinates): Array<FrameName> {
		const tileIndex = this.tilerepo.getTileIndexByTileCoordinates({ x, y });
		const tileIds = this.tilerepo.getTileIdsByTileIndex(tileIndex);
		const isFogFrame =
			this.tilerepo.getParam(tileIndex, TileEntityParam.VISIBILITY) ===
			TileVisibilityType.FOG;

		const SHALLOW_SEA_TILE_ID = this.tilerepo.getTileIdByFrameName(
			FrameName.SHALLOW_SEA,
		);

		const frameNames = new Array<FrameName>();
		// Opakujeme blok s počítadlom `let i = 0`, kým platí `i < tileIds.length`; po iterácii vykonáme `i++`.
		for (let i = 0; i < tileIds.length; i++) {
			const tileId = tileIds[i];

			let frameName = this.tilerepo.getFrameNameByTileId(tileId);
			// Ak platí podmienka `!frameName`, preskočíme zostávajúce spracovanie aktuálnej iterácie cyklu.
			if (!frameName) continue;

			// Specialne pripady tilov s transparentnym pozadim.
			const isCastle =
				i === 2 &&
				tilerepoSettlementCastleBuildingsFrameNames.has(frameName);
			const isPalisade =
				(i === 1 || i === 3) &&
				(tilerepoPalisades1FrameNames.has(frameName) ||
					tilerepoPalisades3FrameNames.has(frameName));
			const isPossibleBeach =
				i === 1 && tileIds[0] === SHALLOW_SEA_TILE_ID;

			// Ak platí podmienka `isCastle`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú príslušný
			// stav.
			if (isCastle) {
				frameName = getSettlementCastleFrameNameByCoordinates({ x, y });
			}

			// Ak platí podmienka `isPalisade`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú príslušný
			// stav.
			if (isPalisade) {
				frameName = getSettlementPalisadeFrameNameByCoordinates({
					x,
					y,
				});
			}

			// Ak platí podmienka `isPossibleBeach`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú
			// príslušný stav.
			if (isPossibleBeach) {
				frameName = this.tilerepo.getCorrectBeachFrameName({ x, y })!;
			}

			// Ak platí podmienka `!frameName || frameName === FrameName.NOT_SET`, preskočíme zostávajúce
			// spracovanie aktuálnej iterácie cyklu.
			if (!frameName || frameName === FrameName.NOT_SET) continue;

			// Ak platí podmienka `isFogFrame`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú príslušný
			// stav.
			if (isFogFrame) {
				frameName = frameName.replace(".png", "_FOW.png") as FrameName;
			}

			frameNames.push(frameName);

			const overlapFrameName = frameName.replace(
				".png",
				"_Top.png",
			) as FrameName;
			// Ak platí podmienka `this.overlapFrameNamesCache.has(overlapFrameName)`, vykonáme nasledujúcu
			// vetvu a jej operácie aktualizujú príslušný stav.
			if (this.overlapFrameNamesCache.has(overlapFrameName)) {
				frameNames.push(overlapFrameName);
			}
		}

		return frameNames;
	}

	/**
	 * Pripraví dočasnú obrazovú kópiu aktuálneho tile a relevantného presahu tile pod ním pre click alebo fade
	 * animáciu. Odstráni staré animácie, vytvorí vnorené kontajnery, zachová depth a tint jednotlivých framov a
	 * vráti všetky tri kontajnery.
	 *
	 * @param coordinates Objekt s mapovými súradnicami tile pripravovaného na dočasnú animáciu.
	 * @returns Objekt s wrapper, current-tile a bottom-overlap kontajnerom alebo null, ak tile nemá žiadne
	 *          renderovateľné frames.
	 */
	private getAnimationContainers({ x, y }: TileCoordinates): {
		container: Phaser.GameObjects.Container | null;
		tileWithOverlapContainer: Phaser.GameObjects.Container | null;
		bottomTileOverlapContainer: Phaser.GameObjects.Container | null;
	} | null {
		const tileIndex: TileIndex =
			this.tilerepo.getTileIndexByTileCoordinates({ x, y });

		// Ak platí podmienka `this.animationContainersCache.has(tileIndex)`, vykonáme nasledujúcu vetvu a jej
		// operácie aktualizujú príslušný stav.
		if (this.animationContainersCache.has(tileIndex)) {
			// Uvoľníme už nepotrebný Phaser objekt alebo zdroj a odstránime súvisiacu referenciu, aby nezostal
			// v pamäti ani render liste.
			this.animationContainersCache.get(tileIndex)!.destroy();
			this.animationContainersCache.delete(tileIndex);
		}

		// Ak platí podmienka `this.animationTweensCache.has(tileIndex)`, vykonáme nasledujúcu vetvu a jej
		// operácie aktualizujú príslušný stav.
		if (this.animationTweensCache.has(tileIndex)) {
			const tween = this.animationTweensCache.get(tileIndex)!;
			tween.stop();
			// Uvoľníme už nepotrebný Phaser objekt alebo zdroj a odstránime súvisiacu referenciu, aby nezostal
			// v pamäti ani render liste.
			tween.destroy();
			this.animationTweensCache.delete(tileIndex);
		}

		const { TILE_SIZE, OVERLAP_TILE_SIZE } = this.tilerepo;
		const frameNames = this.getCorrectFrameNamesByCoordinates({ x, y });
		// Ak platí podmienka `!frameNames.length`, vykonáme ochrannú vetvu a aktuálna metóda alebo callback sa
		// následne ukončí.
		if (!frameNames.length) return null;

		const bottomCoordinates = { x, y: y + 1 };
		const hasBottomTile = bottomCoordinates.y < this.tilerepo.MAP_SIZE;
		const bottomTileIndex = hasBottomTile
			? this.tilerepo.getTileIndexByTileCoordinates(bottomCoordinates)
			: null;
		const isBottomOverlapVisible =
			bottomTileIndex !== null &&
			this.tilerepo.getParam(
				bottomTileIndex,
				TileEntityParam.VISIBILITY,
			) !== TileVisibilityType.BLACK;
		// Vyfiltrujeme z kolekcie `this.getCorrectFrameNamesByCoordinates(bottomCoordinates)` iba položky
		// spĺňajúce callback podmienku.
		const bottomOverlapFrameNames = isBottomOverlapVisible
			? this.getCorrectFrameNamesByCoordinates(bottomCoordinates).filter(
					(frameName: FrameName): boolean =>
						this.overlapFrameNamesCache.has(frameName),
				)
			: [];

		// Overíme, či aspoň jedna položka z kolekcie `frameNames` spĺňa callback podmienku.
		const hasOverlapFrameName = frameNames.some(
			(frameName: FrameName): boolean =>
				this.overlapFrameNamesCache.has(frameName),
		);

		// Vytvoríme dočasný alebo trvalý Phaser Game Object a následnými volaniami nastavíme jeho polohu, depth
		// a vizuálne vlastnosti.
		const container = this.add.container(
			x * TILE_SIZE,
			y * TILE_SIZE - (hasOverlapFrameName ? OVERLAP_TILE_SIZE / 2 : 0),
		);
		container.setName("click-animation-container-wrapper");
		container.setDepth(
			this.mapOverlayBaseDepth + CLICK_ANIMATION_DEPTH_CORRECTION,
		);

		// Vytvoríme dočasný alebo trvalý Phaser Game Object a následnými volaniami nastavíme jeho polohu, depth
		// a vizuálne vlastnosti.
		const tileWithOverlapContainer = this.add.container(0, 0);
		tileWithOverlapContainer.setName(
			"click-animation-container-tile-with-overlap",
		);

		// Vytvoríme dočasný alebo trvalý Phaser Game Object a následnými volaniami nastavíme jeho polohu, depth
		// a vizuálne vlastnosti.
		const bottomTileOverlapContainer = this.add.container(0, 0);
		bottomTileOverlapContainer.setName(
			"click-animation-container-bottom-tile-overlap",
		);

		const currentTintState = this.tileTintCache.get(tileIndex);
		// Prejdeme všetky hodnoty z `frameNames`; v každej iterácii spracujeme `const frameName`.
		for (const frameName of frameNames) {
			const atlas = this.frameNameToAtlasNameCache.get(frameName);
			const depth = this.frameNameToLayerIndexCache.get(frameName);
			// Ak platí podmienka `!atlas || depth === undefined`, preskočíme zostávajúce spracovanie aktuálnej
			// iterácie cyklu.
			if (!atlas || depth === undefined) continue;

			const isOverlapFrameName =
				this.overlapFrameNamesCache.has(frameName);
			const image = this.add
				.image(
					0,
					(isOverlapFrameName ? -TILE_SIZE / 2 : 0) +
						(hasOverlapFrameName && !isOverlapFrameName
							? OVERLAP_TILE_SIZE / 2
							: 0),
					atlas,
					frameName,
				)
				.setOrigin(0.5)
				.setDepth(depth);
			this.applyTintStateToImage(image, currentTintState);
			tileWithOverlapContainer.add(image);
		}

		const bottomTintState =
			bottomTileIndex === null
				? undefined
				: this.tileTintCache.get(bottomTileIndex);
		// Prejdeme všetky hodnoty z `bottomOverlapFrameNames`; v každej iterácii spracujeme `const frameName`.
		for (const frameName of bottomOverlapFrameNames) {
			const atlas = this.frameNameToAtlasNameCache.get(frameName);
			const depth = this.frameNameToLayerIndexCache.get(frameName);
			// Ak platí podmienka `!atlas || depth === undefined`, preskočíme zostávajúce spracovanie aktuálnej
			// iterácie cyklu.
			if (!atlas || depth === undefined) continue;

			const image = this.add
				.image(
					0,
					(TILE_SIZE - OVERLAP_TILE_SIZE) / 2 +
						(hasOverlapFrameName ? OVERLAP_TILE_SIZE / 2 : 0),
					atlas,
					frameName,
				)
				.setOrigin(0.5)
				.setDepth(depth);
			this.applyTintStateToImage(image, bottomTintState);
			bottomTileOverlapContainer.add(image);
		}

		container.add(tileWithOverlapContainer);
		container.add(bottomTileOverlapContainer);

		return {
			container,
			tileWithOverlapContainer,
			bottomTileOverlapContainer,
		};
	}

	/**
	 * Dočasne skryje direct-atlas položky kliknutého tile, vytvorí ich kontajnerovú kópiu, aplikuje scale click
	 * tween a po skončení kontajner zničí a tile znovu vykreslí.
	 *
	 * @param coordinates Objekt s mapovými súradnicami kliknutého tile.
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private animateClick({ x, y }: TileCoordinates): void {
		// Ak platí podmienka `!useSettingsStore().animationsOn`, vykonáme ochrannú vetvu a aktuálna metóda
		// alebo callback sa následne ukončí.
		if (!useSettingsStore().animationsOn) return;

		const isInLockedRegion =
			this.mapExpansionStore.isInLockedRegionByCoordinates({
				x,
				y,
			});
		// Ak platí podmienka `isInLockedRegion`, vykonáme ochrannú vetvu a aktuálna metóda alebo callback sa
		// následne ukončí.
		if (isInLockedRegion) return;

		const containers = this.getAnimationContainers({ x, y });
		// Ak platí podmienka `!containers`, vykonáme ochrannú vetvu a aktuálna metóda alebo callback sa
		// následne ukončí.
		if (!containers) return;

		const { container, tileWithOverlapContainer } = containers;
		// Ak platí podmienka `!container || !tileWithOverlapContainer`, vykonáme ochrannú vetvu a aktuálna
		// metóda alebo callback sa následne ukončí.
		if (!container || !tileWithOverlapContainer) return;

		const tileIndex: TileIndex =
			this.tilerepo.getTileIndexByTileCoordinates({
				x,
				y,
			});
		this.animationContainersCache.set(tileIndex, container);

		tileWithOverlapContainer.setScale(CLICK_SCALE);

		// Po zmene herného stavu znovu synchronizujeme vizuálnu reprezentáciu dotknutého tile.
		this.rerenderTile(tileIndex, true);

		// Vytvoríme tween, ktorý plynulo mení uvedené vlastnosti cieľového objektu a po dokončení vykoná
		// cleanup callback.
		const tween = this.tweens.add({
			targets: tileWithOverlapContainer,
			scale: 1,
			duration: CLICK_ANIMATION_DURATION,
			ease: "Sine.easeOut",
			onComplete: (): void => {
				// Uvoľníme už nepotrebný Phaser objekt alebo zdroj a odstránime súvisiacu referenciu, aby
				// nezostal v pamäti ani render liste.
				this.animationContainersCache.get(tileIndex)!.destroy();
				this.animationContainersCache.delete(tileIndex);
				this.animationTweensCache.delete(tileIndex);

				// Po zmene herného stavu znovu synchronizujeme vizuálnu reprezentáciu dotknutého tile.
				this.rerenderTile(tileIndex);
			},
		});
		this.animationTweensCache.set(tileIndex, tween);
	}

	/**
	 * Dočasne nahradí tile kontajnerovou kópiou, animuje jej priehľadnosť a po dokončení vyčistí animáciu a
	 * obnoví štandardný direct-atlas render tile.
	 *
	 * @param coordinates Objekt s mapovými súradnicami tile, ktorý má plynulo zmiznúť alebo sa obnoviť.
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private animateFade({ x, y }: TileCoordinates): void {
		const isInLockedRegion =
			this.mapExpansionStore.isInLockedRegionByCoordinates({
				x,
				y,
			});
		// Ak platí podmienka `isInLockedRegion`, vykonáme ochrannú vetvu a aktuálna metóda alebo callback sa
		// následne ukončí.
		if (isInLockedRegion) return;

		const containers = this.getAnimationContainers({ x, y });
		// Ak platí podmienka `!containers`, vykonáme ochrannú vetvu a aktuálna metóda alebo callback sa
		// následne ukončí.
		if (!containers) return;

		const { container, tileWithOverlapContainer } = containers;
		// Ak platí podmienka `!container || !tileWithOverlapContainer`, vykonáme ochrannú vetvu a aktuálna
		// metóda alebo callback sa následne ukončí.
		if (!container || !tileWithOverlapContainer) return;

		const tileIndex: TileIndex =
			this.tilerepo.getTileIndexByTileCoordinates({
				x,
				y,
			});
		this.animationContainersCache.set(tileIndex, container);

		tileWithOverlapContainer.setAlpha(0);

		// Po zmene herného stavu znovu synchronizujeme vizuálnu reprezentáciu dotknutého tile.
		this.rerenderTile(tileIndex, true);

		// Vytvoríme tween, ktorý plynulo mení uvedené vlastnosti cieľového objektu a po dokončení vykoná
		// cleanup callback.
		const tween = this.tweens.add({
			targets: tileWithOverlapContainer,
			alpha: 1,
			duration: FADE_ANIMATION_DURATION,
			ease: "Sine.easeOut",
			onComplete: (): void => {
				// Uvoľníme už nepotrebný Phaser objekt alebo zdroj a odstránime súvisiacu referenciu, aby
				// nezostal v pamäti ani render liste.
				this.animationContainersCache.get(tileIndex)!.destroy();
				this.animationContainersCache.delete(tileIndex);
				this.animationTweensCache.delete(tileIndex);

				// Po zmene herného stavu znovu synchronizujeme vizuálnu reprezentáciu dotknutého tile.
				this.rerenderTile(tileIndex);
			},
		});
		this.animationTweensCache.set(tileIndex, tween);
	}

	/**
	 * Vypočíta range vybranej entity a pri platnom dosahu vytvorí polopriehľadný štvorec s obrysom nad
	 * príslušnou mapovou oblasťou. Vynecháva entity bez range, zakázané frames a základný range hradu.
	 *
	 * @param x Horizontálna mapová súradnica tile.
	 * @param y Vertikálna mapová súradnica tile.
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private addRangeSelector(x: number, y: number): void {
		const entity = this.tilerepo.getTileEntityByTileCoordinates({ x, y });
		// Ak platí podmienka `!entity || tilerepoHideRangeIndicatorsFrameNames.has(entity.frameName)`, vykonáme
		// ochrannú vetvu a aktuálna metóda alebo callback sa následne ukončí.
		if (
			!entity ||
			tilerepoHideRangeIndicatorsFrameNames.has(entity.frameName)
		)
			return;

		const range: number =
			(entity as SettlementCastleBuildingTileEntity).activeQuestRange ??
			entity.range ??
			0;
		// Ak platí podmienka `range <= 0`, vykonáme ochrannú vetvu a aktuálna metóda alebo callback sa následne
		// ukončí.
		if (range <= 0) return;

		// Nechceme ukazovat range hradu ako takeho, ale iba range pre settlement questy.
		if (
			tilerepoSettlementCastleBuildingsFrameNames.has(entity.frameName) &&
			range === 1
		)
			return;

		const { TILE_SIZE } = this.tilerepo;

		const borderWidth = 8;
		const startX = (x - range) * TILE_SIZE - TILE_SIZE / 2;
		const startY = (y - range) * TILE_SIZE - TILE_SIZE / 2;
		const boxSize = range * 2 * TILE_SIZE + TILE_SIZE;

		// Vytvoríme dočasný alebo trvalý Phaser Game Object a následnými volaniami nastavíme jeho polohu, depth
		// a vizuálne vlastnosti.
		const box = this.add.graphics();
		box.fillStyle(0x9fff88, 0.2);
		box.fillRect(startX, startY, boxSize, boxSize);
		box.lineStyle(borderWidth, 0x9fff88, 0.6);
		box.strokeRect(
			startX - borderWidth / 2,
			startY - borderWidth / 2,
			boxSize + borderWidth,
			boxSize + borderWidth,
		);
		box.setDepth(this.mapOverlayBaseDepth + SELECTOR_DEPTH_CORRECTION + 1);
		this.rangeHighlights.push(box);
	}

	/**
	 * Vypočíta efektívne hranice odomknutej mapy a hrúbku čiary kompenzovanú zoomom. Existujúci Graphics objekt
	 * prekreslí, alebo pri prvom volaní vytvorí nový hlavný rám mapy.
	 *
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private addOrModifyMapBorder(): void {
		const { TILE_SIZE } = this.tilerepo;

		const zoom = this.camera?.zoom || 0.2;
		const borderWidth = 2 / zoom;

		const { minX, maxX, minY, maxY } =
			this.mapExpansionStore.getEffectiveMapBorderCoordinates();
		const startX = minX * TILE_SIZE - TILE_SIZE / 2;
		const startY = minY * TILE_SIZE - TILE_SIZE / 2;
		const width = (maxX - minX) * TILE_SIZE + TILE_SIZE;
		const height = (maxY - minY) * TILE_SIZE + TILE_SIZE;

		// Ak platí podmienka `this.mapBorder`, vykonáme ochrannú vetvu a aktuálna metóda alebo callback sa
		// následne ukončí.
		if (this.mapBorder) {
			this.mapBorder.clear();
			this.mapBorder.lineStyle(borderWidth, 0xfff888, 0.6);
			this.mapBorder.strokeRect(
				startX - borderWidth / 2,
				startY - borderWidth / 2,
				width + borderWidth / 2,
				height + borderWidth / 2,
			);
			return;
		}

		// Vytvoríme dočasný alebo trvalý Phaser Game Object a následnými volaniami nastavíme jeho polohu, depth
		// a vizuálne vlastnosti.
		this.mapBorder = this.add.graphics();
		this.mapBorder.lineStyle(borderWidth, 0xfff888, 0.6);
		this.mapBorder.strokeRect(
			startX - borderWidth / 2,
			startY - borderWidth / 2,
			width + borderWidth / 2,
			height + borderWidth / 2,
		);
		this.mapBorder.setDepth(
			this.mapOverlayBaseDepth + SELECTOR_DEPTH_CORRECTION + 1,
		);
	}

	/**
	 * Prejde herné segmenty a vytvorí alebo prekreslí ich diagnostické hranice s hrúbkou nezávislou od zoomu.
	 * Graphics objekty recykluje podľa ID segmentu.
	 *
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private addOrModifySegmentMapBorders(): void {
		const { TILE_SIZE } = this.tilerepo;

		const zoom = this.camera?.zoom || 0.2;
		const borderWidth = 2 / zoom;

		const { segments } = useSegmentManager();

		// Prejdeme všetky hodnoty z `segments`; v každej iterácii spracujeme `const [segmentId, segmentData]`.
		for (const [segmentId, segmentData] of segments) {
			const startX = segmentData.minX * TILE_SIZE - TILE_SIZE / 2;
			const startY = segmentData.minY * TILE_SIZE - TILE_SIZE / 2;
			const width =
				(segmentData.maxX - segmentData.minX) * TILE_SIZE + TILE_SIZE;
			const height =
				(segmentData.maxY - segmentData.minY) * TILE_SIZE + TILE_SIZE;

			// Ak platí podmienka `this.segmentMapBorders.has(segmentId)`, preskočíme zostávajúce spracovanie
			// aktuálnej iterácie cyklu.
			if (this.segmentMapBorders.has(segmentId)) {
				const border = this.segmentMapBorders.get(segmentId)!;
				border.clear();
				border.lineStyle(borderWidth, 0x8888ff, 0.8);
				border.strokeRect(
					startX - borderWidth / 2,
					startY - borderWidth / 2,
					width + borderWidth / 2,
					height + borderWidth / 2,
				);
				continue;
			}

			// Vytvoríme dočasný alebo trvalý Phaser Game Object a následnými volaniami nastavíme jeho polohu,
			// depth a vizuálne vlastnosti.
			const border = this.add.graphics();
			border.lineStyle(borderWidth, 0x8888ff, 0.6);
			border.strokeRect(
				startX - borderWidth / 2,
				startY - borderWidth / 2,
				width + borderWidth / 2,
				height + borderWidth / 2,
			);
			border.setDepth(
				this.mapOverlayBaseDepth + SELECTOR_DEPTH_CORRECTION + 1,
			);
			this.segmentMapBorders.set(segmentId, border);
		}
	}

	/**
	 * Prejde konfigurácie mapových expanzií. Pre región pripravený na odomknutie vytvorí alebo prekreslí ružový
	 * rám; pri odomknutom alebo ešte nepripravenom regióne existujúci Graphics objekt odstráni z renderovania
	 * aj cache.
	 *
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private addOrModifyMapExpansionBorders(): void {
		const { TILE_SIZE } = this.tilerepo;

		const zoom = this.camera?.zoom || 0.2;
		const borderWidth = 2 / zoom;

		// Prejdeme všetky hodnoty z `this.mapExpansionStore.keys`; v každej iterácii spracujeme `const key`.
		for (const key of this.mapExpansionStore.keys) {
			const region = this.mapExpansionStore[key];
			// Ak platí podmienka `!region`, preskočíme zostávajúce spracovanie aktuálnej iterácie cyklu.
			if (!region) continue;

			// Ak platí podmienka `!region.isReadyToUnlockByRegions() || region.isUnlocked`, preskočíme
			// zostávajúce spracovanie aktuálnej iterácie cyklu.
			if (!region.isReadyToUnlockByRegions() || region.isUnlocked) {
				// Ak platí podmienka `this.regionMapBorders.has(key)`, vykonáme nasledujúcu vetvu a jej
				// operácie aktualizujú príslušný stav.
				if (this.regionMapBorders.has(key)) {
					this.regionMapBorders.get(key)!.clear();
					// Uvoľníme už nepotrebný Phaser objekt alebo zdroj a odstránime súvisiacu referenciu, aby
					// nezostal v pamäti ani render liste.
					this.regionMapBorders.get(key)!.destroy();
					this.regionMapBorders.delete(key);
				}

				continue;
			}

			const startX = region.minX * TILE_SIZE - TILE_SIZE / 2;
			const startY = region.minY * TILE_SIZE - TILE_SIZE / 2;
			const width = (region.maxX - region.minX) * TILE_SIZE + TILE_SIZE;
			const height = (region.maxY - region.minY) * TILE_SIZE + TILE_SIZE;

			// Ak platí podmienka `this.regionMapBorders.has(key)`, preskočíme zostávajúce spracovanie aktuálnej
			// iterácie cyklu.
			if (this.regionMapBorders.has(key)) {
				const border = this.regionMapBorders.get(key)!;
				border.clear();
				border.lineStyle(borderWidth, 0xff4488, 0.8);
				border.strokeRect(
					startX - borderWidth / 2,
					startY - borderWidth / 2,
					width + borderWidth / 2,
					height + borderWidth / 2,
				);
				continue;
			}

			// Vytvoríme dočasný alebo trvalý Phaser Game Object a následnými volaniami nastavíme jeho polohu,
			// depth a vizuálne vlastnosti.
			const border = this.add.graphics();
			border.lineStyle(borderWidth, 0xff4488, 0.6);
			border.strokeRect(
				startX - borderWidth / 2,
				startY - borderWidth / 2,
				width + borderWidth / 2,
				height + borderWidth / 2,
			);
			border.setDepth(
				this.mapOverlayBaseDepth + SELECTOR_DEPTH_CORRECTION + 1,
			);
			this.regionMapBorders.set(key, border);
		}
	}

	/**
	 * Nájde mapovú expanziu podľa ID a prejde iba tiles na jej vonkajšom obvode. Každý hraničný tile znovu
	 * vykreslí, aby sa po odomknutí regiónu aktualizovali jeho mapové frames a expanzné značky; samotný stav
	 * store táto metóda nemení.
	 *
	 * @param id Identifikátor mapovej expanzie, ktorá sa má odomknúť.
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private unlockMapExpansionById(id: MapExpansionKey): void {
		const region = this.mapExpansionStore[id];
		// Ak platí podmienka `!region`, vykonáme ochrannú vetvu a aktuálna metóda alebo callback sa následne
		// ukončí.
		if (!region) return;

		// loop only border tiles
		for (let x = region.minX; x <= region.maxX; x++) {
			// Opakujeme blok s počítadlom `let y = region.minY`, kým platí `y <= region.maxY`; po iterácii
			// vykonáme `y++`.
			for (let y = region.minY; y <= region.maxY; y++) {
				// Ak platí podmienka `x !== region.minX && x !== region.maxX && y !== region.minY && y !==
				// region.maxY`, preskočíme zostávajúce spracovanie aktuálnej iterácie cyklu.
				if (
					x !== region.minX &&
					x !== region.maxX &&
					y !== region.minY &&
					y !== region.maxY
				)
					continue;

				const tileIndex = this.tilerepo.getTileIndexByTileCoordinates({
					x,
					y,
				});
				// Po zmene herného stavu znovu synchronizujeme vizuálnu reprezentáciu dotknutého tile.
				this.rerenderTile(tileIndex);
			}
		}
	}

	/**
	 * Aplikuje uloženú dvojfarebnú tint konfiguráciu na ľahký direct-atlas tile. Pri chýbajúcom
	 * stave obnoví predvolené farby a multiply režim.
	 *
	 * @param tile Ľahký direct-atlas tile, na ktorý sa má aplikovať vizuálny stav.
	 * @param tintState Voliteľná uložená konfigurácia primárneho a sekundárneho tintu a tint mode.
	 */
	private applyTintStateToTile(
		tile: DirectAtlasRenderTile | null,
		tintState?: TileTintState,
	): void {
		if (!tile) return;

		tile.tint = tintState?.tint ?? 0xffffff;
		tile.tint2 = tintState?.tint2 ?? 0x000000;
		tile.tintMode =
			tintState?.tintMode ?? (Phaser.TintModes.MULTIPLY as number);
	}

	/**
	 * Aplikuje rovnaký tint stav na dočasný Phaser Image používaný animáciami. Pri chýbajúcom stave tint z
	 * obrázka odstráni.
	 *
	 * @param image Dočasný Phaser Image, na ktorý sa má aplikovať alebo z ktorého sa má odstrániť tint.
	 * @param tintState Voliteľná uložená konfigurácia primárneho a sekundárneho tintu a tint mode.
	 */
	private applyTintStateToImage(
		image: Phaser.GameObjects.Image,
		tintState?: TileTintState,
	): void {
		if (!tintState) return;

		image.tint = tintState.tint;
		image.tintMode = tintState.tintMode;
	}

	/**
	 * Vyhľadá všetky základné aj overlap deskriptory na zadanej súradnici a aplikuje na ne rovnaký tint stav.
	 *
	 * @param x Horizontálna mapová súradnica tile.
	 * @param y Vertikálna mapová súradnica tile.
	 * @param tintState Voliteľná uložená konfigurácia primárneho a sekundárneho tintu a tint mode.
	 */
	private applyTintStateAtCoordinates(
		x: number,
		y: number,
		tintState?: TileTintState,
	): void {
		const coordinateKey = y * this.tilerepo.MAP_SIZE + x;
		const baseEntries =
			this.directBaseTilesByCoordinateKey.get(coordinateKey);
		const overlapEntries =
			this.directOverlapTilesByCoordinateKey.get(coordinateKey);

		if (baseEntries) {
			for (const tile of baseEntries) {
				this.applyTintStateToTile(tile, tintState);
			}
		}

		if (overlapEntries) {
			for (const tile of overlapEntries) {
				this.applyTintStateToTile(tile, tintState);
			}
		}
	}

	/**
	 * Aktualizuje červené zvýraznenie množiny tiles podľa ich boolean hodnoty. Stav uloží do tint cache a
	 * okamžite ho aplikuje na existujúce base aj overlap renderovacie položky.
	 *
	 * @param tiles Množina súradníc a boolean hodnôt určujúcich, ktoré tiles majú byť zvýraznené.
	 */
	private highlightTilesRed(tiles: Set<TileCoordinatesWithValue>): void {
		// Zvýrazniť vieme len vtedy ak máme vrstvy
		if (!this.mapLayer || !this.overlapLayer) return;

		// Prechdzame vsetky tiles, poskztnute ako argument metody a vy koname prislusne operacie
		for (const { x, y, value } of tiles) {
			const tileIndex: TileIndex =
				this.tilerepo.getTileIndexByTileCoordinates({
					x,
					y,
				});

			// Zistíme, či už má táto dlaždica aplikovaný červený tint (uložený v cache)
			const hasTint = this.tileTintCache.has(tileIndex);
			// Ak nie, znamena to ze nepresla dobre initom v createMap a ignorujeme ju
			if (value === !hasTint) continue;

			if (value) {
				// vymaz zanam o titnte a nastav defaultny tint - nic
				this.tileTintCache.delete(tileIndex);
				this.applyTintStateAtCoordinates(x, y);
			} else {
				// Use the single-color multiply mode available throughout Phaser 4.
				// It is deliberately stronger than the old 0.3 blend so a disconnected
				// road remains clearly visible across all direct atlas frame entries.
				const tintState: TileTintState = {
					tint: 0xff8080,
					tint2: 0x000000,
					tintMode: Phaser.TintModes.MULTIPLY as number,
				};
				// nastavime novy tint do cache pre dany tile
				this.tileTintCache.set(tileIndex, tintState);
				// Synchronizujeme sprievodný vizuálny prvok s aktuálnym stavom tile alebo entity.
				this.applyTintStateAtCoordinates(x, y, tintState);
			}

			this.tilerepo.markSegmentAsDirty({ tileIndex });
		}
	}

	/**
	 * Pridá Stats.js panel do document.body a pripojí begin/end meranie na preupdate a postupdate udalosti
	 * scény. Metóda aktuálne panel iba aktivuje; neprepína Tilemap debug rendering ani neodstraňuje už
	 * registrované listenery.
	 *
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private activeDebug(): void {
		this.stats.showPanel(0); // 0 = FPS, 1 = MS/frame, 2 = MB pamäť
		document.body.appendChild(this.stats.dom);

		// Hookni sa na hernú slučku
		this.events.on("preupdate", (): void => {
			this.stats.begin();
		});

		this.events.on("postupdate", (): void => {
			this.stats.end();
		});
	}

	/**
	 * Vytvorí pulzujúci kruhový Graphics marker na mapovej súradnici. Nastaví farbu, počiatočnú a cieľovú
	 * mierku, alpha smer a nekonečný yoyo tween a objekt uloží do cache.
	 *
	 * @param x Horizontálna mapová súradnica tile.
	 * @param y Vertikálna mapová súradnica tile.
	 * @param color Farba pulzujúceho markeru vo formáte 0xRRGGBB. Voliteľný parameter.
	 * @param scaleFrom Počiatočná mierka pulzujúceho markeru. Voliteľný parameter.
	 * @param scaleTo Cieľová mierka pulzujúceho markeru. Voliteľný parameter.
	 * @param reverseAlpha Ak je true, alpha animácia prebieha opačným smerom. Voliteľný parameter.
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private showClickPulse(
		x: number,
		y: number,
		color: number = 0xfeb942,
		scaleFrom: number = 0.5,
		scaleTo: number = 1.8,
		reverseAlpha: boolean = false,
	): void {
		const key = `${x},${y}`;
		// Ak platí podmienka `this.graphicsObjects.has(key)`, vykonáme ochrannú vetvu a aktuálna metóda alebo
		// callback sa následne ukončí.
		if (this.graphicsObjects.has(key)) return;

		const { TILE_SIZE } = this.tilerepo;
		const centerX = x * TILE_SIZE + TILE_SIZE / 2 - 125;
		const centerY = y * TILE_SIZE + TILE_SIZE / 2 - 125;

		const pulses: Phaser.GameObjects.Graphics[] = [];

		// Opakujeme blok s počítadlom `let i = 0`, kým platí `i < 3`; po iterácii vykonáme `i++`.
		for (let i = 0; i < 3; i++) {
			// Vytvoríme dočasný alebo trvalý Phaser Game Object a následnými volaniami nastavíme jeho polohu,
			// depth a vizuálne vlastnosti.
			const pulse = this.add.graphics().setDepth(9999);
			pulse.lineStyle(7, color, 1);
			pulse.strokeCircle(0, 0, TILE_SIZE / 3);
			pulse.setPosition(centerX, centerY);
			pulse.setScale(scaleFrom);
			pulse.setAlpha(0);

			pulses.push(pulse);

			const alphaFrom = reverseAlpha ? 1 : 0;
			const alphaTo = reverseAlpha ? 0 : 1;
			// Vytvoríme tween, ktorý plynulo mení uvedené vlastnosti cieľového objektu a po dokončení vykoná
			// cleanup callback.
			this.tweens.add({
				targets: pulse,
				scale: { from: scaleFrom, to: scaleTo },
				alpha: { from: alphaFrom, to: alphaTo },
				duration: 3000,
				delay: i * 2000,
				repeat: -1,
				ease: "Sine.easeOut",
				onRepeat: (): void => {
					// Reset before next repeat
					pulse.setScale(scaleFrom);
					pulse.setAlpha(0);
				},
			});
		}

		// Synchronizujeme príslušnú cache so zmeneným stavom, aby ďalšie lookupy a rendering používali aktuálne
		// údaje.
		this.graphicsObjects.set(key, pulses);
	}

	/**
	 * Vytvorí tutorialový marker v tvare X na zadanej mapovej súradnici a uloží ho medzi pomocné Graphics
	 * objekty danej lokality.
	 *
	 * @param x Horizontálna mapová súradnica tile.
	 * @param y Vertikálna mapová súradnica tile.
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private showClickX(x: number, y: number): void {
		const key = `${x},${y}`;
		// Ak platí podmienka `this.graphicsObjects.has(key)`, vykonáme ochrannú vetvu a aktuálna metóda alebo
		// callback sa následne ukončí.
		if (this.graphicsObjects.has(key)) return;

		const { TILE_SIZE } = this.tilerepo;
		const centerX = x * TILE_SIZE + TILE_SIZE / 2 - 125;
		const centerY = y * TILE_SIZE + TILE_SIZE / 2 - 125;

		// Vytvoríme dočasný alebo trvalý Phaser Game Object a následnými volaniami nastavíme jeho polohu, depth
		// a vizuálne vlastnosti.
		const graphics = this.add.graphics().setDepth(9999);
		graphics.lineStyle(6, 0xfeb942, 1); // rovnaká farba ako v pulse

		const size = TILE_SIZE * 0.6;

		// diagonály do kríža
		graphics.beginPath();
		graphics.moveTo(centerX - size / 2, centerY - size / 2);
		graphics.lineTo(centerX + size / 2, centerY + size / 2);
		graphics.moveTo(centerX + size / 2, centerY - size / 2);
		graphics.lineTo(centerX - size / 2, centerY + size / 2);
		graphics.strokePath();

		// Synchronizujeme príslušnú cache so zmeneným stavom, aby ďalšie lookupy a rendering používali aktuálne
		// údaje.
		this.graphicsObjects.set(key, [graphics]);
	}

	/**
	 * Vyhľadá všetky pomocné Graphics objekty na zadanej súradnici, zničí ich a odstráni cache záznam.
	 *
	 * @param x Horizontálna mapová súradnica tile.
	 * @param y Vertikálna mapová súradnica tile.
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private deleteGraphicsObjects(x: number, y: number): void {
		const key = `${x},${y}`;
		const graphicsObjects: Phaser.GameObjects.Graphics[] | undefined =
			this.graphicsObjects.get(key);
		// Ak platí podmienka `!graphicsObjects`, vykonáme ochrannú vetvu a aktuálna metóda alebo callback sa
		// následne ukončí.
		if (!graphicsObjects) return;

		// Prejdeme všetky hodnoty z `graphicsObjects`; v každej iterácii spracujeme `const graphicsObject`.
		for (const graphicsObject of graphicsObjects) {
			// Uvoľníme už nepotrebný Phaser objekt alebo zdroj a odstránime súvisiacu referenciu, aby nezostal
			// v pamäti ani render liste.
			graphicsObject.destroy();
		}
		// Synchronizujeme príslušnú cache so zmeneným stavom, aby ďalšie lookupy a rendering používali aktuálne
		// údaje.
		this.graphicsObjects.delete(key);
	}
	/**
	 * Prevedie aktuálny world viewport kamery na orezaný rozsah mapových buniek a overí, či každá kontrolovaná
	 * bunka má BLACK visibility. Vracia false hneď po nájdení viditeľnejšieho tile.
	 *
	 * @returns True, ak celý kontrolovaný viewport obsahuje iba BLACK tiles; inak false.
	 */
	private isViewportShowingOnlyBlackTiles(): boolean {
		// Ak platí podmienka `!this.camera`, vykonáme ochrannú vetvu a aktuálna metóda alebo callback sa
		// následne ukončí.
		if (!this.camera) {
			return false;
		}

		const { TILE_SIZE } = this.tilerepo;

		// Get the viewport bounds in world coordinates
		const viewportLeft = this.camera.worldView.x;
		const viewportTop = this.camera.worldView.y;
		const viewportRight = viewportLeft + this.camera.worldView.width;
		const viewportBottom = viewportTop + this.camera.worldView.height;

		// Convert to tile coordinates with some margin for partial tiles
		const tileLeft = floorNumber(viewportLeft / TILE_SIZE) - 1;
		const tileTop = floorNumber(viewportTop / TILE_SIZE) - 1;
		const tileRight = ceilNumber(viewportRight / TILE_SIZE) + 1;
		const tileBottom = ceilNumber(viewportBottom / TILE_SIZE) + 1;

		// Check all tiles in viewport - early exit if any tile is not black
		let totalTiles = 0;

		// Opakujeme blok s počítadlom `let x = tileLeft`, kým platí `x <= tileRight`; po iterácii vykonáme
		// `x++`.
		for (let x = tileLeft; x <= tileRight; x++) {
			// Opakujeme blok s počítadlom `let y = tileTop`, kým platí `y <= tileBottom`; po iterácii vykonáme
			// `y++`.
			for (let y = tileTop; y <= tileBottom; y++) {
				// Skip tiles outside the map bounds
				if (
					x < 0 ||
					y < 0 ||
					x >= this.tilerepo.MAP_SIZE ||
					y >= this.tilerepo.MAP_SIZE
				) {
					continue;
				}

				totalTiles++;
				const tileIndex = this.tilerepo.getTileIndexByTileCoordinates({
					x,
					y,
				});
				const visibility = this.tilerepo.getParam(
					tileIndex,
					TileEntityParam.VISIBILITY,
				);

				// Early exit if any tile is not black
				if (visibility !== TileVisibilityType.BLACK) {
					return false;
				}
			}
		}

		// Return true only if we have tiles and all of them are black
		return totalTiles > 0;
	}

	/**
	 * Sleduje, ako dlho kamera zobrazuje výhradne čierne tiles. Po prekročení timeoutu emituje udalosť na
	 * zobrazenie tlačidla resetu kamery; po návrate nad inú oblasť emituje jeho skrytie a časovač resetuje.
	 *
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private checkBlackTileTimeout(): void {
		const currentlyViewingBlackTiles =
			this.isViewportShowingOnlyBlackTiles();
		const currentTime = Date.now();

		// Ak platí podmienka `currentlyViewingBlackTiles`, vykonáme nasledujúcu vetvu a jej operácie
		// aktualizujú príslušný stav.
		if (currentlyViewingBlackTiles) {
			// Ak platí podmienka `!this.isViewingOnlyBlackTiles`, vykonáme nasledujúcu vetvu a jej operácie
			// aktualizujú príslušný stav.
			if (!this.isViewingOnlyBlackTiles) {
				// Started viewing black tiles
				this.isViewingOnlyBlackTiles = true;
				// Aktualizujeme interný stav scény podľa výsledku aktuálnej operácie: `this.blackTileStartTime
				// = currentTime;`
				this.blackTileStartTime = currentTime;
			} else {
				// Still viewing black tiles, check if timeout reached
				const timeElapsed = currentTime - this.blackTileStartTime;
				// Ak platí podmienka `timeElapsed >= this.BLACK_TILE_TIMEOUT`, vykonáme nasledujúcu vetvu a jej
				// operácie aktualizujú príslušný stav.
				if (timeElapsed >= this.BLACK_TILE_TIMEOUT) {
					// Show camera reset button after 5 seconds of viewing only black tiles
					this.emitter.emit(
						EmitterEvents.PhaserShowCameraResetButton,
					);
				}
			}
		} else {
			// Not viewing only black tiles anymore, hide button and reset the timer
			if (this.isViewingOnlyBlackTiles) {
				// Emitujeme udalosť do aplikačného event busu, aby sa o zmene dozvedeli ostatné moduly.
				this.emitter.emit(EmitterEvents.PhaserHideCameraResetButton);
			}
			// Aktualizujeme interný stav scény podľa výsledku aktuálnej operácie: `this.isViewingOnlyBlackTiles
			// = false;`
			this.isViewingOnlyBlackTiles = false;
			// Aktualizujeme interný stav scény podľa výsledku aktuálnej operácie: `this.blackTileStartTime =
			// 0;`
			this.blackTileStartTime = 0;
		}
	}

	/**
	 * Animovane presunie a priblíži kameru na počiatočný cieľ druhého tutorialového kroku.
	 *
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private animateTutorialStep2Camera(): void {
		// Aktualizujeme polohu, zoom alebo hranice kamery podľa aktuálneho herného kontextu.
		this.cameras.main.zoomTo(0.5, 4000, "Sine.easeInOut");
		const capital = settlementNames.get(1);
		// Ak platí podmienka `!capital`, vykonáme ochrannú vetvu a aktuálna metóda alebo callback sa následne
		// ukončí.
		if (!capital) return;
		const { x, y } = capital;
		const { TILE_SIZE } = this.tilerepo;
		// Aktualizujeme polohu, zoom alebo hranice kamery podľa aktuálneho herného kontextu.
		this.cameras.main.pan(
			x * TILE_SIZE,
			y * TILE_SIZE - 1050,
			4000,
			"Sine.easeInOut",
		);
	}

	/**
	 * Dočasne deaktivuje tile labely, vycentruje kameru na stred mapy, nastaví počiatočný zoom, spustí fade-in
	 * a cloud efekt a po krátkom oneskorení kameru priblíži. Po dokončení zoomu labely znovu aktivuje a mimo
	 * tutorialu vyvolá analýzu quest stavu.
	 *
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private animateIntroCamera(): void {
		// Prejdeme každú položku z kolekcie `this.tileLabelsCache` a nad každou vykonáme callback.
		this.tileLabelsCache.forEach((label: TileLabel): void => {
			label.active = false;
		});
		this.cameras.main.on(
			"camerazoomcomplete",
			(): void => {
				// Prejdeme každú položku z kolekcie `this.tileLabelsCache` a nad každou vykonáme callback.
				this.tileLabelsCache.forEach((label: TileLabel): void => {
					label.active = true;
				});
				this.cameras.main.off("camerazoomcomplete");
			},
			this,
		);
		const questStore = useQuestStore();
		const { TILE_SIZE } = this.tilerepo;
		// Aktualizujeme polohu, zoom alebo hranice kamery podľa aktuálneho herného kontextu.
		this.cameras.main.setZoom(0.2);
		// Aktualizujeme polohu, zoom alebo hranice kamery podľa aktuálneho herného kontextu.
		this.cameras.main.centerOn(512 * TILE_SIZE, 512 * TILE_SIZE); // Keep camera centered at (0, 0) during zoom
		this.cameras.main.fadeIn(2500, 0, 0, 0);
		this.cloudIntroAnimation();

		// Aktualizujeme polohu, zoom alebo hranice kamery podľa aktuálneho herného kontextu.
		setTimeout((): void => {
			// Aktualizujeme polohu, zoom alebo hranice kamery podľa aktuálneho herného kontextu.
			this.cameras.main.zoomTo(0.8, 3000, "Sine.easeInOut");

			// Ak platí podmienka `!questStore.tutorialActive`, vykonáme nasledujúcu vetvu a jej operácie
			// aktualizujú príslušný stav.
			if (!questStore.tutorialActive) {
				questStore.analyzeQuestState();
			}
		}, 1200);
	}

	/**
	 * Odstráni neaktívne tutorialové labely zo scény a vráti kľúče tiles, pre ktoré treba po zmene prekladu
	 * znovu vytvoriť upgrade-town label.
	 *
	 * @returns Pole kľúčov tiles, pre ktoré treba obnoviť tutorialové upgrade labely.
	 */
	private handleInactiveTutorialTileLabels(): string[] {
		// Aktualizujeme interný stav scény podľa výsledku aktuálnej operácie: `this.tutorialLabel = null;`
		this.tutorialLabel = null;
		// Remove all tutorial tile labels
		const keys: string[] = [];
		// Prejdeme všetky hodnoty z `this.tileLabelsCache`; v každej iterácii spracujeme `const [key, label]`.
		for (const [key, label] of this.tileLabelsCache) {
			// Ak platí podmienka `label.skin === TileLabelSkin.TUTORIAL`, vykonáme nasledujúcu vetvu a jej
			// operácie aktualizujú príslušný stav.
			if (label.skin === TileLabelSkin.TUTORIAL) {
				// Remove label from scene
				label.delete();

				// Remove label from cache
				this.tileLabelsCache.delete(key);
				keys.push(key);
			}
		}
		return keys;
	}

	/**
	 * Riadi grafické markery, kameru, labely, front odhaľovania a prechody jednotlivých tutorialových krokov.
	 * Podľa čísla kroku a voliteľne kliknutého tile vytvára alebo odstraňuje pulzy a X značky a synchronizuje
	 * tutorialový stav.
	 *
	 * @param step Číslo tutorialového kroku, ktorého grafické a stavové pravidlá sa majú vykonať.
	 * @param clickedTile Voliteľný tile, ktorý vyvolal posun tutorialu a poskytuje kontext aktuálneho kroku.
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private handleTutorialSteps(step: number, clickedTile?: TileEntity): void {
		const questStore = useQuestStore();

		// Rozvetvíme spracovanie podľa hodnoty `step`; každý case rieši samostatný variant.
		switch (step) {
			// Spracujeme hodnotu `1` a po vykonaní príslušných operácií vetvu ukončíme alebo necháme pokračovať
			// podľa kódu.
			case 1: {
				// Step 1: Pulse on main settlement
				if (questStore.tutorialStep > 1) break; // Already done

				const [xStr, yStr] = this.tutorialStartingFocusKey.split(",");
				const x = parseInt(xStr, 10);
				const y = parseInt(yStr, 10);

				// If already revealed, skip to next step
				const tileIndex = this.tilerepo.getTileIndexByTileCoordinates({
					x,
					y,
				});
				const visibility = this.tilerepo.getParam(
					tileIndex,
					TileEntityParam.VISIBILITY,
				);
				// Ak platí podmienka `visibility === TileVisibilityType.EXPLORED`, ukončíme aktuálny cyklus
				// alebo switch vetvu.
				if (visibility === TileVisibilityType.EXPLORED) {
					// Zapíšeme výsledok operácie do centrálneho store alebo spustíme nadväzujúce prepočty
					// závislého stavu.
					questStore.setTutorialStep(2);
					this.handleTutorialSteps(2);
					break;
				}

				this.showClickPulse(x, y);
				// Zapíšeme výsledok operácie do centrálneho store alebo spustíme nadväzujúce prepočty závislého
				// stavu.
				questStore.setTutorialStep(1);
				break;
			}
			//tutorial krok  2-3-4. vyfarbickuj pulzujucimi kruhmi cestu k odhaleniu noveho mesta
			//+ daj Xka tam kde je to nove mesto
			case 2: {
				// Ak platí podmienka `questStore.tutorialStep > 2`, ukončíme aktuálny cyklus alebo switch
				// vetvu.
				if (questStore.tutorialStep > 2) break; // Already done

				this.animateTutorialStep2Camera();
				// Step 2: Reveal path to next settlement
				const [xStr, yStr] = this.tutorialStartingFocusKey.split(",");
				const x = parseInt(xStr, 10);
				const y = parseInt(yStr, 10);

				// Ak platí podmienka `this.graphicsObjects.has(this.tutorialStartingFocusKey)`, vykonáme
				// nasledujúcu vetvu a jej operácie aktualizujú príslušný stav.
				if (this.graphicsObjects.has(this.tutorialStartingFocusKey)) {
					this.deleteGraphicsObjects(x, y);
					// Zapíšeme výsledok operácie do centrálneho store alebo spustíme nadväzujúce prepočty
					// závislého stavu.
					questStore.setTutorialStep(2);
				}

				// Vytvor frontu pre postupné odkrytie cesty
				this.tutorialRevealQueue = [
					{ x, y: y - 1 },
					{ x, y: y - 2 },
					{ x, y: y - 3 },
				];

				let nextTile: TileCoordinates | undefined = undefined;
				// Opakujeme blok s počítadlom `let i = 0`, kým platí `i < this.tutorialRevealQueue.length`; po
				// iterácii vykonáme `i++`.
				for (let i = 0; i < this.tutorialRevealQueue.length; i++) {
					const tile = this.tutorialRevealQueue[i];
					const tileIndex =
						this.tilerepo.getTileIndexByTileCoordinates(tile);
					const visibility = this.tilerepo.getParam(
						tileIndex,
						TileEntityParam.VISIBILITY,
					);
					// Ak platí podmienka `visibility !== TileVisibilityType.EXPLORED`, ukončíme aktuálny cyklus
					// alebo switch vetvu.
					if (visibility !== TileVisibilityType.EXPLORED) {
						nextTile = tile;
						// Aktualizujeme interný stav scény podľa výsledku aktuálnej operácie:
						// `this.tutorialRevealQueue = this.tutorialRevealQueue.slice(i);`
						this.tutorialRevealQueue =
							this.tutorialRevealQueue.slice(i);
						break;
					}
				}
				// Ak platí podmienka `!nextTile`, ukončíme aktuálny cyklus alebo switch vetvu.
				if (!nextTile) {
					// All tiles are revealed, skip to next step
					questStore.setTutorialStep(3);
					this.handleTutorialSteps(3);
					break;
				}
				this.showClickPulse(nextTile.x, nextTile.y);

				const nextSettlement: SettlementItem | undefined =
					settlementNames.get(4);
				// Ak platí podmienka `nextSettlement`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú
				// príslušný stav.
				if (nextSettlement) {
					this.showClickX(nextSettlement.x, nextSettlement.y);
					this.showClickX(nextSettlement.x, nextSettlement.y + 1);
				}
				break;
			}
			// Spracujeme hodnotu `3` a po vykonaní príslušných operácií vetvu ukončíme alebo necháme pokračovať
			// podľa kódu.
			case 3: {
				// Ak platí podmienka `questStore.tutorialStep > 3`, ukončíme aktuálny cyklus alebo switch
				// vetvu.
				if (questStore.tutorialStep > 3) break; // Already done

				// Step 3: Reveal each tile in the queue
				// If the queue is empty, reconstruct it based on unexplored tiles
				if (!this.tutorialRevealQueue.length) {
					const [xStr, yStr] =
						this.tutorialStartingFocusKey.split(",");
					const x = parseInt(xStr, 10);
					const y = parseInt(yStr, 10);

					const possibleQueue = [
						{ x, y: y - 2 }, // middle tile
						{ x, y: y - 3 },
						{ x, y: y - 4 },
					];

					// Only include tiles that are not yet explored
					this.tutorialRevealQueue = possibleQueue.filter(
						(tile: TileCoordinates): boolean => {
							const tileIndex =
								this.tilerepo.getTileIndexByTileCoordinates(
									tile,
								);
							const visibility = this.tilerepo.getParam(
								tileIndex,
								TileEntityParam.VISIBILITY,
							);
							return visibility !== TileVisibilityType.EXPLORED;
						},
					);

					// Ak platí podmienka `this.tutorialRevealQueue.length === 0`, vykonáme ochrannú vetvu a
					// aktuálna metóda alebo callback sa následne ukončí.
					if (this.tutorialRevealQueue.length === 0) {
						// All tiles are revealed, skip to next step
						questStore.setTutorialStep(4);
						this.handleTutorialSteps(4);
						return;
					}
				}
				// Ak platí podmienka `clickedTile`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú
				// príslušný stav.
				if (clickedTile) {
					// Zapíšeme výsledok operácie do centrálneho store alebo spustíme nadväzujúce prepočty
					// závislého stavu.
					questStore.setTutorialStep(3);
					const { x, y } = clickedTile;
					const { x: targetX, y: targetY } =
						this.tutorialRevealQueue[0];

					// Ak platí podmienka `x === targetX && y === targetY`, vykonáme nasledujúcu vetvu a jej
					// operácie aktualizujú príslušný stav.
					if (x === targetX && y === targetY) {
						this.deleteGraphicsObjects(x, y);
						this.tutorialRevealQueue.shift();

						// Ak platí podmienka `this.tutorialRevealQueue.length > 0`, vykonáme nasledujúcu vetvu
						// a jej operácie aktualizujú príslušný stav.
						if (this.tutorialRevealQueue.length > 0) {
							const next: TileCoordinates =
								this.tutorialRevealQueue[0];
							this.showClickPulse(next.x, next.y);
						} else {
							// Ak predchádzajúca podmienka neplatí, vykonáme alternatívnu vetvu.
							// Aktualizujeme interný stav scény podľa výsledku aktuálnej operácie:
							// `this.tutorialConvertXsToPulses = true;`
							this.tutorialConvertXsToPulses = true;
							this.deleteGraphicsObjects(x, y - 1);
							this.showClickPulse(x, y - 1);
						}
					}
				} else {
					// If tutorial was interrupted, check which tile should be pulsed
					if (this.tutorialRevealQueue.length > 0) {
						const next: TileCoordinates =
							this.tutorialRevealQueue[0];
						this.showClickPulse(next.x, next.y);
					}
				}
				break;
			}
			// Spracujeme hodnotu `4` a po vykonaní príslušných operácií vetvu ukončíme alebo necháme pokračovať
			// podľa kódu.
			case 4: {
				// Ak platí podmienka `questStore.tutorialStep > 4`, ukončíme aktuálny cyklus alebo switch
				// vetvu.
				if (questStore.tutorialStep > 4) break; // Already done

				// Step 4: Convert Xs to pulses around the new settlement
				questStore.setTutorialStep(4);
				const settlement: SettlementItem | undefined =
					settlementNames.get(4);
				// Ak platí podmienka `settlement && clickedTile`, vykonáme nasledujúcu vetvu a jej operácie
				// aktualizujú príslušný stav.
				if (settlement && clickedTile) {
					const settlementGraphicsKey = `${clickedTile.x},${clickedTile.y}`;

					// Ak platí podmienka `this.graphicsObjects.has(settlementGraphicsKey)`, vykonáme
					// nasledujúcu vetvu a jej operácie aktualizujú príslušný stav.
					if (this.graphicsObjects.has(settlementGraphicsKey)) {
						this.deleteGraphicsObjects(
							clickedTile.x,
							clickedTile.y,
						);
					}

					// Vyfiltrujeme z výsledku predchádzajúceho reťazca operácií iba položky spĺňajúce callback
					// podmienku.
					const keysToConvert = [
						...this.graphicsObjects.keys(),
					].filter((key: string): boolean => {
						// eslint-disable-next-line no-restricted-syntax
						const [kx, ky] = key.split(",").map(Number);
						const dx = Math.abs(settlement.x - kx);
						const dy = Math.abs(settlement.y - ky);
						return dx <= 1 && dy <= 1;
					});
					const key = keysToConvert[0];
					// Ak platí podmienka `key`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú príslušný
					// stav.
					if (key) {
						// eslint-disable-next-line no-restricted-syntax
						const [x, y] = key.split(",").map(Number);
						this.deleteGraphicsObjects(x, y);
						this.showClickPulse(x, y);
					} else {
						// Potom už končíme tento režim
						this.tutorialConvertXsToPulses = false;
						// Zapíšeme výsledok operácie do centrálneho store alebo spustíme nadväzujúce prepočty
						// závislého stavu.
						questStore.setTutorialStep(5);
						this.handleTutorialSteps(5);
					}
				} else {
					// If tutorial was interrupted, check which tile should be pulsed
					const nextSettlement: SettlementItem | undefined =
						settlementNames.get(4);
					// Ak platí podmienka `nextSettlement`, vykonáme nasledujúcu vetvu a jej operácie
					// aktualizujú príslušný stav.
					if (nextSettlement) {
						this.showClickPulse(nextSettlement.x, nextSettlement.y);
					}
				}
				break;
			}
			//tutorial krok 6. urob "tutorialove" cesty aby si userovi ukazal kde ma stavat cestu
			//+ daj pulzujuci kruh nad prvou tutorialovou cestou aby vedel ze tam má kliknúť
			case 5: {
				// Ak platí podmienka `questStore.tutorialStep > 5`, ukončíme aktuálny cyklus alebo switch
				// vetvu.
				if (questStore.tutorialStep > 5) break; // Already done

				// Zapíšeme výsledok operácie do centrálneho store alebo spustíme nadväzujúce prepočty závislého
				// stavu.
				questStore.setTutorialStep(5);
				const nextSettlement: SettlementItem | undefined =
					settlementNames.get(4);

				// NOTE: prevents duplicate tutorial labels in the cache before they are rendered on the scene
				if (
					nextSettlement &&
					!Array.from(this.tileLabelsCache.values()).some(
						(label: TileLabel): boolean =>
							label.skin === TileLabelSkin.TUTORIAL,
					)
				) {
					// Tutorial step: connect the road from the main city to the adjacent settlement
					const tileIndex =
						this.tilerepo.getTileIndexByTileCoordinates({
							x: nextSettlement.x,
							y: nextSettlement.y,
						});

					// Synchronizujeme príslušnú cache so zmeneným stavom, aby ďalšie lookupy a rendering
					// používali aktuálne údaje.
					this.tileLabelsCache.set(
						TileLabel.formatKey(TileLabelSkin.TUTORIAL, tileIndex),
						new TileLabel(
							this,
							{
								x: nextSettlement.x,
								y: nextSettlement.y,
							},
							true,
							translate("ui.connect_road_town"),
							TileLabelType.SETTLEMENT,
							TileLabelSkin.TUTORIAL,
						),
					);
					this.updateTileLabels();
				}

				// Ak platí podmienka `nextSettlement && clickedTile`, vykonáme nasledujúcu vetvu a jej operácie
				// aktualizujú príslušný stav.
				if (nextSettlement && clickedTile) {
					// Ak platí podmienka `nextSettlement.x === clickedTile.x && nextSettlement.y ===
					// clickedTile.y`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú príslušný stav.
					if (
						nextSettlement.x === clickedTile.x &&
						nextSettlement.y === clickedTile.y
					) {
						this.deleteGraphicsObjects(
							nextSettlement.x,
							nextSettlement.y,
						);

						const mainSettlement: SettlementItem | undefined =
							settlementNames.get(1);
						// Ak platí podmienka `mainSettlement`, vykonáme nasledujúcu vetvu a jej operácie
						// aktualizujú príslušný stav.
						if (mainSettlement) {
							const mainSettlementX = mainSettlement.x;
							const mainSettlementY = mainSettlement.y;
							this.showClickPulse(
								mainSettlementX,
								mainSettlementY - 2,
							);

							const roadYs = [
								mainSettlementY - 2,
								mainSettlementY - 3,
								mainSettlementY - 4,
								mainSettlementY - 5,
							];
							// Prejdeme každú položku z kolekcie `roadYs` a nad každou vykonáme callback.
							roadYs.forEach((y: number): void =>
								this.tilerepo.setTileByTileCoordinates(
									{ x: mainSettlementX, y },
									FrameName.DIRT_ROAD_TUTORIAL,
									true,
								),
							);
						}
					}
				} else {
					// If tutorial was interrupted, check which tile should be pulsed based on the tiles between the settlements and ad the pulse
					const mainSettlement: SettlementItem | undefined =
						settlementNames.get(1);
					// Ak platí podmienka `mainSettlement`, vykonáme nasledujúcu vetvu a jej operácie
					// aktualizujú príslušný stav.
					if (mainSettlement) {
						const mainSettlementX = mainSettlement.x;
						const mainSettlementY = mainSettlement.y;
						this.showClickPulse(
							mainSettlementX,
							mainSettlementY - 2,
						);

						const roadYs = [
							mainSettlementY - 2,
							mainSettlementY - 3,
							mainSettlementY - 4,
							mainSettlementY - 5,
						];
						// Prejdeme každú položku z kolekcie `roadYs` a nad každou vykonáme callback.
						roadYs.forEach((y: number): void =>
							this.tilerepo.setTileByTileCoordinates(
								{ x: mainSettlementX, y },
								FrameName.DIRT_ROAD_TUTORIAL,
								true,
							),
						);
					}
				}
				break;
			}
			//tutorial krok 7. postav cesty a spoj dve mesta
			case 6: {
				// Ak platí podmienka `questStore.tutorialStep > 6`, ukončíme aktuálny cyklus alebo switch
				// vetvu.
				if (questStore.tutorialStep > 6) break; // Already done

				// TOTO JE MRTVY KOD, POTOM VYHODIT A NECHAT PRETESTOVAT TUTORIAL
				// Ak platí podmienka `questStore.tutorialStep === 6 && this.tutorialRoadBuilderCounter === 0`,
				// vykonáme nasledujúcu vetvu a jej operácie aktualizujú príslušný stav.
				if (
					questStore.tutorialStep === 6 &&
					this.tutorialRoadBuilderCounter === 0
				) {
					// if the tutorial was interrupted, we need to showClickPulse on road - We need to calculate the tutorialRoadBuilderCounter based on DIRT_ROAD and set the showClickPulse to the next road
					const mainSettlement: SettlementItem | undefined =
						settlementNames.get(1);
					// Ak platí podmienka `mainSettlement`, vykonáme nasledujúcu vetvu a jej operácie
					// aktualizujú príslušný stav.
					if (mainSettlement) {
						const mainSettlementX = mainSettlement.x;
						const mainSettlementY = mainSettlement.y;
						// Opakujeme blok s počítadlom `let i = 0`, kým platí `i < 3`; po iterácii vykonáme
						// `i++`.
						for (let i = 0; i < 3; i++) {
							const roadTile =
								this.tilerepo.getTileEntityByTileCoordinates({
									x: mainSettlementX,
									y: mainSettlementY - 2 - i,
								});
							// Ak platí podmienka `roadTile?.frameName === FrameName.DIRT_ROAD_8`, vykonáme
							// nasledujúcu vetvu a jej operácie aktualizujú príslušný stav.
							if (roadTile?.frameName === FrameName.DIRT_ROAD_8) {
								this.tutorialRoadBuilderCounter++;
							}
						}
						// Ak platí podmienka `this.tutorialRoadBuilderCounter < 4`, vykonáme nasledujúcu vetvu
						// a jej operácie aktualizujú príslušný stav.
						if (this.tutorialRoadBuilderCounter < 4) {
							this.showClickPulse(
								mainSettlementX,
								mainSettlementY -
									this.tutorialRoadBuilderCounter -
									2,
							);
						}
					}
				}

				// Registrujeme obsluhu udalosti; callback prepojí event bus s príslušnou logikou a stavom
				// scény.
				this.emitter.on(
					EmitterEvents.PhaserTutorialTileUpgrade,
					({
						coordinates,
						frameName,
					}: TileRepositoryUpgradePayload): void => {
						// Ak platí podmienka `useQuestStore().tutorialStep > 6`, vykonáme ochrannú vetvu a
						// aktuálna metóda alebo callback sa následne ukončí.
						if (useQuestStore().tutorialStep > 6) return;
						// Zapíšeme výsledok operácie do centrálneho store alebo spustíme nadväzujúce prepočty
						// závislého stavu.
						useQuestStore().setTutorialStep(6);
						// Ak platí podmienka `frameName !== FrameName.DIRT_ROAD`, vykonáme ochrannú vetvu a
						// aktuálna metóda alebo callback sa následne ukončí.
						if (frameName !== FrameName.DIRT_ROAD) return;

						const x = coordinates.x;
						const y = coordinates.y;
						const graphicsKey = `${x},${y}`;

						// Ak platí podmienka `this.graphicsObjects.has(graphicsKey)`, vykonáme nasledujúcu
						// vetvu a jej operácie aktualizujú príslušný stav.
						if (this.graphicsObjects.has(graphicsKey)) {
							this.tutorialRoadBuilderCounter++;
						}

						this.deleteGraphicsObjects(x, y);
						// Ak platí podmienka `this.tutorialRoadBuilderCounter === 4`, vykonáme ochrannú vetvu a
						// aktuálna metóda alebo callback sa následne ukončí.
						if (this.tutorialRoadBuilderCounter === 4) {
							this.emitter.off(
								EmitterEvents.PhaserTutorialSettlementRevealed,
							);
							this.emitter.off(
								EmitterEvents.PhaserTutorialTileUpgrade,
							);

							this.handleInactiveTutorialTileLabels();
							// Zapíšeme výsledok operácie do centrálneho store alebo spustíme nadväzujúce
							// prepočty závislého stavu.
							useQuestStore().setTutorialStep(7);
							return;
						}

						this.showClickPulse(x, y - 1);
					},
				);
				break;
			}
			// tutorial krok 8 - vo vue zobraz vvpravo tlaidlo
			case 7: {
				break;
			}
			// tutorial krok 9 - aktivuj quest
			case 8: {
				break;
			}
		}
	}

	/**
	 * Pridá na mapu truhlicu zobrazovanú tmavým skinom, vytvorí jej TileLabel a uloží rozšírený ChestTile do
	 * zoznamu.
	 *
	 * @param chest Dátový objekt truhlice vrátane súradníc, typu a odmeny.
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private addDarkChestToMap(chest: Chest): void {
		const chestImage = new TileLabel(
			this,
			{ x: chest.x, y: chest.y },
			true,
			"",
			TileLabelType.MISC,
			TileLabelSkin.CHEST_DARK,
		);

		const saveChest = {
			...chest,
			img: chestImage,
			coordinates: { x: chest.x, y: chest.y },
		};

		this.chests.push(saveChest);
	}

	/**
	 * Pridá na mapu štandardnú truhlicu, vytvorí jej TileLabel a uloží ju do lokálneho zoznamu truhlíc.
	 *
	 * @param chest Dátový objekt truhlice vrátane súradníc, typu a odmeny.
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private addChestToMap(chest: Chest): void {
		const chestImage = new TileLabel(
			this,
			{ x: chest.x, y: chest.y },
			true,
			"",
			TileLabelType.MISC,
			TileLabelSkin.CHEST,
		);

		const saveChest = {
			...chest,
			img: chestImage,
			coordinates: { x: chest.x, y: chest.y },
		};

		this.chests.push(saveChest);
	}

	/**
	 * Ak nie je aktívny tutorial, zabezpečí názvový label hlavného sídla a pri úrovni sídla najviac 5 aj
	 * tutorialový upgrade-town label. Podľa parametra môže najskôr odstrániť staré tutorialové labely; pri
	 * úrovni nad 5 upgrade výzvu odstráni a skončí.
	 *
	 * @param keepLabel Ak je true, existujúci relevantný label sa podľa možnosti zachová namiesto
	 *                  bezpodmienečného odstránenia. Voliteľný parameter.
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private handleSettlementCtaMapLabel(keepLabel: boolean = false): void {
		// Ak platí podmienka `!keepLabel`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú príslušný
		// stav.
		if (!keepLabel) {
			this.handleInactiveTutorialTileLabels();
		}
		// LK-814: If the tutorial is active
		if (useQuestStore().tutorialActive) return;

		const mainSettlement: SettlementItem | undefined =
			settlementNames.get(1);

		// Ak platí podmienka `!mainSettlement`, vykonáme ochrannú vetvu a aktuálna metóda alebo callback sa
		// následne ukončí.
		if (!mainSettlement) return;

		const tileIndex = this.tilerepo.getTileIndexByTileCoordinates({
			x: mainSettlement.x,
			y: mainSettlement.y,
		});

		this.createMainSettlementNameLabel(tileIndex);

		const tile = this.tilerepo.getTileEntityByTileIndex(
			tileIndex,
		) as SettlementCastleBuildingTileEntity;

		// Ak platí podmienka `tile.level > 5`, vykonáme ochrannú vetvu a aktuálna metóda alebo callback sa
		// následne ukončí.
		if (tile.level > 5) {
			// Ak platí podmienka `this.tutorialLabel`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú
			// príslušný stav.
			if (this.tutorialLabel) {
				this.handleInactiveTutorialTileLabels();
			}
			return;
		}

		this.createTutorialUpgradeTownLabel(tileIndex);
	}

	/**
	 * Vytvorí názvový TileLabel hlavného sídla pre zadaný tile index a uloží ho do cache s príslušným skinom.
	 *
	 * @param tileIndex Lineárny index tile používaný tile repository.
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private createMainSettlementNameLabel(tileIndex: number): void {
		const mainSettlement: SettlementItem | undefined =
			settlementNames.get(1);
		// Ak platí podmienka `!mainSettlement`, vykonáme ochrannú vetvu a aktuálna metóda alebo callback sa
		// následne ukončí.
		if (!mainSettlement) return;

		// Ak platí podmienka `!Array.from(this.tileLabelsCache.values()).some( (label: TileLabel): boolean =>
		// label.skin === TileLabelSkin.MAIN_SETTLEMENT…`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú
		// príslušný stav.
		// Overíme, či aspoň jedna položka z kolekcie `Array.from(this.tileLabelsCache.values())` spĺňa callback
		// podmienku.
		if (
			!Array.from(this.tileLabelsCache.values()).some(
				(label: TileLabel): boolean =>
					label.skin === TileLabelSkin.MAIN_SETTLEMENT_NAME,
			)
		) {
			const entity = this.tilerepo.getTileEntityByTileCoordinates({
				x: mainSettlement.x,
				y: mainSettlement.y,
			}) as SettlementCastleBuildingTileEntity | null;

			const text = entity!.settlementName;

			// Synchronizujeme príslušnú cache so zmeneným stavom, aby ďalšie lookupy a rendering používali
			// aktuálne údaje.
			this.tileLabelsCache.set(
				TileLabel.formatKey(
					TileLabelSkin.MAIN_SETTLEMENT_NAME,
					tileIndex,
				),
				new TileLabel(
					this,
					{
						x: mainSettlement.x,
						y: mainSettlement.y,
					},
					true,
					text,
					TileLabelType.MAIN_SETTLEMENT_NAME,
					TileLabelSkin.MAIN_SETTLEMENT_NAME,
				),
			);
		}
	}

	/**
	 * Vytvorí alebo obnoví tutorialový label vyzývajúci na upgrade mesta. Zo vstupného indexu alebo kľúča
	 * odvodí tile, text a typ, odstráni konfliktný label a uloží nový objekt.
	 *
	 * @param tileIndex Lineárny index tile alebo existujúci formátovaný cache kľúč tutorialového labelu.
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private createTutorialUpgradeTownLabel(tileIndex: number | string): void {
		const mainSettlement: SettlementItem | undefined =
			settlementNames.get(1);
		// Ak platí podmienka `!mainSettlement`, vykonáme ochrannú vetvu a aktuálna metóda alebo callback sa
		// následne ukončí.
		if (!mainSettlement) return;
		const entity = this.tilerepo.getTileEntityByTileCoordinates({
			x: mainSettlement.x,
			y: mainSettlement.y,
		}) as SettlementCastleBuildingTileEntity | null;
		// Ak platí podmienka `!entity`, vykonáme ochrannú vetvu a aktuálna metóda alebo callback sa následne
		// ukončí.
		if (!entity) return;

		const level5Notice = translate("tutorial.click_town_for_upgrade").split(
			" ",
		);
		const halfLevel5Notice = ceilNumber(level5Notice.length / 2);

		const firstLevel5Notice = level5Notice
			.slice(0, halfLevel5Notice)
			.join(" ");
		const secondLevel5Notice = level5Notice
			.slice(halfLevel5Notice)
			.join(" ");

		const labelText =
			useSettlementsStore().mainLevel === 5
				? firstLevel5Notice
				: translate("tutorial.upgrade_town");

		const firstText =
			entity.isQuestCompleted || useSettlementsStore().mainLevel === 5
				? labelText
				: entity.questDescriptionHtml;

		const secondText =
			!entity.isQuestCompleted && useSettlementsStore().mainLevel < 5
				? `${entity.questProgressValues?.currentValue} / ${entity.questProgressValues?.targetValue} `
				: useSettlementsStore().mainLevel === 5
					? secondLevel5Notice
					: undefined;

		// Ak platí podmienka `this.tutorialLabel`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú
		// príslušný stav.
		if (this.tutorialLabel) {
			// Ak platí podmienka `this.tutorialLabel?.secondText?.text === secondText &&
			// this.tutorialLabel?.rawText === firstText`, vykonáme ochrannú vetvu a aktuálna metóda alebo
			// callback sa následne ukončí.
			if (
				this.tutorialLabel?.secondText?.text === secondText &&
				this.tutorialLabel?.rawText === firstText
			) {
				return;
			}

			// Ak platí podmienka `this.tutorialLabel?.secondText?.text !== secondText &&
			// this.tutorialLabel?.rawText === firstText`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú
			// príslušný stav.
			if (
				this.tutorialLabel?.secondText?.text !== secondText &&
				this.tutorialLabel?.rawText === firstText
			) {
				this.tutorialLabel.updateLabelTexts(undefined, secondText);
			} else {
				// Ak predchádzajúca podmienka neplatí, vykonáme alternatívnu vetvu.
				this.handleInactiveTutorialTileLabels();
			}
		}
		// Ak platí podmienka `!Array.from(this.tileLabelsCache.values()).some( (label: TileLabel): boolean =>
		// label.skin === TileLabelSkin.TUTORIAL, )`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú
		// príslušný stav.
		// Overíme, či aspoň jedna položka z kolekcie `Array.from(this.tileLabelsCache.values())` spĺňa callback
		// podmienku.
		if (
			!Array.from(this.tileLabelsCache.values()).some(
				(label: TileLabel): boolean =>
					label.skin === TileLabelSkin.TUTORIAL,
			)
		) {
			// Ak platí podmienka `typeof tileIndex === "string"`, vykonáme nasledujúcu vetvu a jej operácie
			// aktualizujú príslušný stav.
			if (typeof tileIndex === "string") {
				// Synchronizujeme príslušnú cache so zmeneným stavom, aby ďalšie lookupy a rendering používali
				// aktuálne údaje.
				this.tileLabelsCache.set(
					tileIndex,
					(this.tutorialLabel = new TileLabel(
						this,
						{
							x: mainSettlement.x,
							y: mainSettlement.y,
						},
						true,
						firstText,
						TileLabelType.SETTLEMENT,
						TileLabelSkin.TUTORIAL,
						secondText,
					)),
				);
			} else {
				// Ak predchádzajúca podmienka neplatí, vykonáme alternatívnu vetvu.
				// Synchronizujeme príslušnú cache so zmeneným stavom, aby ďalšie lookupy a rendering používali
				// aktuálne údaje.
				this.tileLabelsCache.set(
					TileLabel.formatKey(TileLabelSkin.TUTORIAL, tileIndex),
					(this.tutorialLabel = new TileLabel(
						this,
						{
							x: mainSettlement.x,
							y: mainSettlement.y,
						},
						true,
						firstText,
						TileLabelType.SETTLEMENT,
						TileLabelSkin.TUTORIAL,
						secondText,
					)),
				);
			}
		}
	}

	/**
	 * Animovane presunie kameru na hlavné sídlo a nastaví vhodnú úroveň priblíženia.
	 *
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private goToCapital(): void {
		const capital = settlementNames.get(1);
		// Ak platí podmienka `!capital`, vykonáme ochrannú vetvu a aktuálna metóda alebo callback sa následne
		// ukončí.
		if (!capital) return;
		const { x, y } = capital;
		const { TILE_SIZE } = this.tilerepo;
		// Aktualizujeme polohu, zoom alebo hranice kamery podľa aktuálneho herného kontextu.
		this.cameras.main.pan(x * TILE_SIZE, y * TILE_SIZE, 1_000, "Power2");
		// Aktualizujeme polohu, zoom alebo hranice kamery podľa aktuálneho herného kontextu.
		this.cameras.main.zoomTo(0.8, 1_000, "Power2");
	}

	/**
	 * Rezervované miesto pre budúcu implementáciu screenshotu mapy. Aktuálne telo obsahuje iba TODO komentár,
	 * nevykonáva žiadnu operáciu a nevytvára snapshot.
	 *
	 * @returns Žiadna návratová hodnota; aktuálna placeholder implementácia je no-op.
	 */
	private makeScreenshot(): void {
		// TODO Spravit screenshot
	}

	/**
	 * Vyhľadá lokálnu truhlicu na zadanej súradnici a overí, či ešte existuje v ChestRepository. Neaktuálnu
	 * lokálnu truhlicu odstráni; pri platnej truhlici bez specialReward zobrazí coin toast a nakoniec deleguje
	 * samotný claim na ChestRepository.
	 *
	 * @param x Horizontálna mapová súradnica tile.
	 * @param y Vertikálna mapová súradnica tile.
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private handleChest(x: number, y: number): void {
		// Vyhľadáme index prvej položky z kolekcie `this.chests`, ktorá spĺňa callback podmienku.
		const index = this.chests.findIndex(
			(chest: ChestTile): boolean => chest.x === x && chest.y === y,
		);
		// Ak platí podmienka `index !== -1`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú príslušný
		// stav.
		if (index !== -1) {
			const chest = this.chests[index];

			// Check if this specific chest still exists in ChestRepository
			const chestrepo = ChestRepository.getInstance();
			const activeChest = chestrepo.getChestByCoordinates({ x, y });

			// Ak platí podmienka `!activeChest`, vykonáme ochrannú vetvu a aktuálna metóda alebo callback sa
			// následne ukončí.
			if (!activeChest) {
				// Chest was already claimed but UI wasn't updated - remove from scene only
				this.removeChest(chest);
				return;
			}

			// Chest exists, process normally
			if (!chest.specialReward) {
				const toast = this.toastPool.getForVariant(
					TileToastType.INCOME,
				);
				const upgradeBonus =
					1 +
					getUpgradeCurrentReplaceValueById(UpgradeIds.inflation) +
					getUpgradeCurrentReplaceValueById(UpgradeIds.geyser);

				toast.showToast(
					ceilNumber(chest.reward.coins * upgradeBonus),
					{
						messageType: TileToastType.INCOME,
						position: {
							x: chest.x,
							y: chest.y,
						},
						iconKey: "icon-coins",
					},
					this.cameras.main.zoom,
				);
			}

			// Then claim in repository
			chestrepo.claimChestByCoordinates(x, y);
		}
	}

	/**
	 * Vyhľadá truhlicu na zadanej súradnici a prepne jej label z tmavého na štandardný chest skin.
	 *
	 * @param x Horizontálna mapová súradnica tile.
	 * @param y Vertikálna mapová súradnica tile.
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private swapChest(x: number, y: number): void {
		// Vyhľadáme index prvej položky z kolekcie `this.chests`, ktorá spĺňa callback podmienku.
		const index = this.chests.findIndex(
			(chest: ChestTile): boolean => chest.x === x && chest.y === y,
		);
		// Ak platí podmienka `index !== -1`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú príslušný
		// stav.
		if (index !== -1) {
			this.chests[index].img.swapSkin(TileLabelSkin.CHEST);
		}
	}

	/**
	 * Vyhľadá truhlicu podľa súradníc, odstráni jej TileLabel a vyberie ju z lokálneho zoznamu.
	 *
	 * @param chest Dátový objekt truhlice vrátane súradníc, typu a odmeny.
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private removeChest(chest: Chest): void {
		// Vyhľadáme index prvej položky z kolekcie `this.chests`, ktorá spĺňa callback podmienku.
		const index = this.chests.findIndex(
			(c: ChestTile): boolean => c.x === chest.x && c.y === chest.y,
		);
		// Ak platí podmienka `index !== -1`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú príslušný
		// stav.
		if (index !== -1) {
			this.chests[index].img.delete();
			this.chests.splice(index, 1);
		}
	}

	/**
	 * Vyhľadá a vráti prvú truhlicu umiestnenú na zadaných mapových súradniciach.
	 *
	 * @param x Horizontálna mapová súradnica tile.
	 * @param y Vertikálna mapová súradnica tile.
	 * @returns Nájdený ChestTile alebo undefined, ak na súradnici truhlica nie je.
	 */
	private getChestOnLocation(x: number, y: number): ChestTile | undefined {
		// Vyhľadáme prvú položku z kolekcie `this.chests`, ktorá spĺňa callback podmienku.
		return this.chests.find(
			(chest: ChestTile): boolean => chest.x === x && chest.y === y,
		);
	}

	/**
	 * Aktualizuje mierku a polohu všetkých bežných tile labelov, chest labelov a labelov špeciálnych lokácií
	 * podľa aktuálneho zoomu kamery.
	 *
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private updateTileLabels(): void {
		//ak je nastavená táto flagy tak sa deje úvodné načítanie mapy a teda nie je ptorebné stále aktualizovať tileLabels
		if (this.deferInitialTileLabelUpdates) return;
		// Prejdeme každú položku z kolekcie `this.tileLabelsCache` a nad každou vykonáme callback.
		this.tileLabelsCache.forEach((label: TileLabel): void => {
			label.update(this.camera?.zoom || 0.2);
		});
		this.updateChestTileLabels();
		// Prejdeme každú položku z kolekcie `this.specialLocationsCache` a nad každou vykonáme callback.
		this.specialLocationsCache.forEach(
			(specialLocation: SpecialLocationRepository | null): void => {
				specialLocation?.tileLabel.update(this.camera?.zoom || 0.2);
			},
		);
	}

	/**
	 * Prejde všetky truhlice a aktualizuje ich TileLabel reprezentáciu podľa aktuálneho zoomu.
	 *
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private updateChestTileLabels(): void {
		// Prejdeme každú položku z kolekcie `this.chests` a nad každou vykonáme callback.
		this.chests.forEach((chest: ChestTile): void => {
			chest.img.update(this.camera?.zoom || 0.2);
		});
	}

	/**
	 * Prejde cache progress barov a znovu vypočíta geometriu barov patriacich settlement castle entitám, aby
	 * reagovali na zoom a aktuálny progress.
	 *
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	public updateProgressBars(): void {
		// Prejdeme každú položku z kolekcie `this.progressBars` a nad každou vykonáme callback.
		this.progressBars.forEach(
			(
				_barInfo: {
					bar: Phaser.GameObjects.Graphics;
					progress: number;
				},
				tileIndex: number,
			): void => {
				// console.warn(bar.scale);
				// bar.setScale(1 / (this.camera?.zoom || 1));
				// console.warn(bar.scale);
				const entity = this.tilerepo.getTileEntityByTileIndex(
					tileIndex,
				) as SettlementCastleBuildingTileEntity;
				if (!entity) return this.removeProgressBar(tileIndex);
				// Ak platí podmienka `!tilerepoSettlementCastleBuildingsFrameNames.has( entity.frameName, )`,
				// vykonáme ochrannú vetvu a aktuálna metóda alebo callback sa následne ukončí.
				if (
					tilerepoSettlementCastleBuildingsFrameNames.has(
						entity.frameName,
					)
				) {
					// Synchronizujeme sprievodný vizuálny prvok s aktuálnym stavom tile alebo entity.
					this.updateProgressBar(
						entity.x,
						entity.y,
						1,
						1 - entity.progress,
					);
				}

				if (
					(tilerepoFriendlyUnitsFrameNames.has(entity.frameName) ||
						tilerepoFightableEnemiesFrameNames.has(
							entity.frameName,
						)) &&
					entity.progress === null
				) {
					const isVisible =
						this.tilerepo.getParam(
							entity.index,
							TileEntityParam.VISIBILITY,
						) === TileVisibilityType.EXPLORED;
					if (!isVisible) return;

					this.removeProgressBar(entity.index);
					return;
				}

				if (tilerepoFriendlyUnitsFrameNames.has(entity.frameName)) {
					this.updateFriendlyUnitProgressBar(entity);
				}

				if (tilerepoFightableEnemiesFrameNames.has(entity.frameName)) {
					this.updateFightableEnemyProgressBar(entity);
				}

				if (_barInfo.progress <= 0) {
					_barInfo.progress = entity.progress;
				}
				// Synchronizujeme sprievodný vizuálny prvok s aktuálnym stavom tile alebo entity.
				this.updateProgressBar(
					entity.x,
					entity.y,
					1,
					1 - _barInfo.progress,
				);
			},
		);
	}

	/**
	 * Podľa použitého násobiteľa odpočíta správny typ clicker zdroja a zaznamená príslušný quest objective.
	 * Neznáme alebo všeobecné násobitele spracuje ako super clicker.
	 *
	 * @param superClickerMultiplier Použitý násobiteľ, podľa ktorého sa vyberie typ clicker zdroja na
	 *                               odpočítanie.
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private decreaseSuperClickerResources(
		superClickerMultiplier: number,
	): void {
		// Rozvetvíme spracovanie podľa hodnoty `superClickerMultiplier`; každý case rieši samostatný variant.
		switch (superClickerMultiplier) {
			// Spracujeme hodnotu `2` a po vykonaní príslušných operácií vetvu ukončíme alebo necháme pokračovať
			// podľa kódu.
			case 2:
				// Zapíšeme výsledok operácie do centrálneho store alebo spustíme nadväzujúce prepočty závislého
				// stavu.
				useResourcesStore().decrease(
					ResourceType.CLICKER_2,
					1,
					"map: Clicker 2 used",
				);
				// Oznámime quest systému vykonanú hernú akciu, aby mohol aktualizovať aktívne ciele.
				QuestRepository.getInstance().manageQuestState(
					QuestObjectiveActions.USE_CLICK_MULTIPLIER,
				);
				break;
			// Spracujeme hodnotu `3` a po vykonaní príslušných operácií vetvu ukončíme alebo necháme pokračovať
			// podľa kódu.
			case 3:
				// Zapíšeme výsledok operácie do centrálneho store alebo spustíme nadväzujúce prepočty závislého
				// stavu.
				useResourcesStore().decrease(
					ResourceType.CLICKER_3,
					1,
					"map: Clicker 3 used",
				);
				// Oznámime quest systému vykonanú hernú akciu, aby mohol aktualizovať aktívne ciele.
				QuestRepository.getInstance().manageQuestState(
					QuestObjectiveActions.USE_CLICK_MULTIPLIER,
				);
				break;
			// Spracujeme hodnotu `5` a po vykonaní príslušných operácií vetvu ukončíme alebo necháme pokračovať
			// podľa kódu.
			case 5:
				// Zapíšeme výsledok operácie do centrálneho store alebo spustíme nadväzujúce prepočty závislého
				// stavu.
				useResourcesStore().decrease(
					ResourceType.CLICKER_5,
					1,
					"map: Clicker 5 used",
				);
				// Oznámime quest systému vykonanú hernú akciu, aby mohol aktualizovať aktívne ciele.
				QuestRepository.getInstance().manageQuestState(
					QuestObjectiveActions.USE_CLICK_MULTIPLIER,
				);
				break;
			// Spracujeme defaultný variant a po vykonaní príslušných operácií vetvu ukončíme alebo necháme
			// pokračovať podľa kódu.
			default:
				// Zapíšeme výsledok operácie do centrálneho store alebo spustíme nadväzujúce prepočty závislého
				// stavu.
				useResourcesStore().decrease(
					ResourceType.SUPER_CLICKER,
					1,
					"map: super clicker used",
				);
				// Oznámime quest systému vykonanú hernú akciu, aby mohol aktualizovať aktívne ciele.
				QuestRepository.getInstance().manageQuestState(
					QuestObjectiveActions.USE_SUPER_CLICKER,
				);
				break;
		}
	}

	/**
	 * Vytvorí štandardný cache kľúč zo skinu a súradníc a overí, či už zodpovedajúci TileLabel existuje.
	 *
	 * @param coordinates Mapové súradnice, na ktorých sa kontroluje existencia labelu.
	 * @param skin Vizuálny skin TileLabelu použitý pri tvorbe cache kľúča alebo labelovej operácii.
	 * @returns True, ak cache obsahuje label so zadaným skinom na danej súradnici; inak false.
	 */
	private hasTileLabel(
		coordinates: TileCoordinates,
		skin: TileLabelSkin,
	): boolean {
		return this.tileLabelsCache.has(
			TileLabel.formatKey(
				skin,
				this.tilerepo.getTileIndexByTileCoordinates(coordinates),
			),
		);
	}

	/**
	 * Centralizovane vykoná CREATE, UPDATE, DELETE alebo DELETE_ALL operáciu nad tile labelmi. Spravuje cache,
	 * zdieľanie priestoru s level labelom, sprievodné grafické pulzy a špeciálne lokácie a nakoniec
	 * synchronizuje všetky labely so zoomom.
	 *
	 * @param coordinates Mapové súradnice cieľového labelu.
	 * @param skin Vizuálny skin TileLabelu použitý pri tvorbe cache kľúča alebo labelovej operácii.
	 * @param action Požadovaná TileLabel operácia CREATE, UPDATE, DELETE alebo DELETE_ALL.
	 * @param type Logický typ TileLabelu ovplyvňujúci jeho správanie a vizuál. Voliteľný parameter.
	 * @param text Textový obsah zobrazovaný v labeli. Voliteľný parameter.
	 * @param scaleToZoom Určuje, či sa má label automaticky škálovať podľa zoomu kamery. Voliteľný parameter.
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private updateTileLabel(
		coordinates: TileCoordinates,
		skin: TileLabelSkin,
		action: TileLabelAction,
		type: TileLabelType = TileLabelType.MISC,
		text: string = "",
		scaleToZoom: boolean = true,
	): void {
		if (action === TileLabelAction.DELETE_MULTIPLE_SKINS_AT_COORS) {
			const skins = skin.split("|") as Array<TileLabelSkin>;
			for (const s of skins) {
				const key = TileLabel.formatKey(
					s,
					this.tilerepo.getTileIndexByTileCoordinates(coordinates),
				);
				const label = this.tileLabelsCache.get(key);
				label?.delete();
				this.tileLabelsCache.delete(key);
			}
		}

		// Ak platí podmienka `action === TileLabelAction.DELETE_ALL`, vykonáme nasledujúcu vetvu a jej operácie
		// aktualizujú príslušný stav.
		if (action === TileLabelAction.DELETE_ALL) {
			// Prejdeme každú položku z kolekcie `this.tileLabelsCache` a nad každou vykonáme callback.
			this.tileLabelsCache.forEach(
				(label: TileLabel, key: string): void => {
					// Ak platí podmienka `label.skin === skin`, vykonáme nasledujúcu vetvu a jej operácie
					// aktualizujú príslušný stav.
					if (label.skin === skin) {
						label.delete();
						// Synchronizujeme príslušnú cache so zmeneným stavom, aby ďalšie lookupy a rendering
						// používali aktuálne údaje.
						this.tileLabelsCache.delete(key);
					}
				},
			);
		}
		// Ak platí podmienka `action === TileLabelAction.DELETE`, vykonáme nasledujúcu vetvu a jej operácie
		// aktualizujú príslušný stav.
		if (action === TileLabelAction.DELETE) {
			const key = TileLabel.formatKey(
				skin,
				this.tilerepo.getTileIndexByTileCoordinates(coordinates),
			);
			const label = this.tileLabelsCache.get(key);
			label?.delete();
			// Synchronizujeme príslušnú cache so zmeneným stavom, aby ďalšie lookupy a rendering používali
			// aktuálne údaje.
			this.tileLabelsCache.delete(key);
			const levelKey = TileLabel.formatKey(
				TileLabelSkin.LEVEL,
				this.tilerepo.getTileIndexByTileCoordinates(coordinates),
			);
			const levelLabel = this.tileLabelsCache.get(levelKey);
			// Ak platí podmienka `levelLabel`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú príslušný
			// stav.
			if (levelLabel) {
				levelLabel.sharingTile = false;
			}
			// Ak platí podmienka `skin === TileLabelSkin.SETTLEMENT_HIGHLIGHT`, vykonáme nasledujúcu vetvu a
			// jej operácie aktualizujú príslušný stav.
			if (skin === TileLabelSkin.SETTLEMENT_HIGHLIGHT) {
				this.deleteGraphicsObjects(coordinates.x, coordinates.y - 0.05);
			}
			// Ak platí podmienka `skin === TileLabelSkin.SPECIAL_LOCATION`, vykonáme nasledujúcu vetvu a jej
			// operácie aktualizujú príslušný stav.
			if (skin === TileLabelSkin.SPECIAL_LOCATION) {
				this.deleteSpecialLocation(coordinates);
			}
		}
		// Ak platí podmienka `action === TileLabelAction.CREATE`, vykonáme nasledujúcu vetvu a jej operácie
		// aktualizujú príslušný stav.
		if (action === TileLabelAction.CREATE) {
			// Ak platí podmienka `this.hasTileLabel(coordinates, skin)`, vykonáme ochrannú vetvu a aktuálna
			// metóda alebo callback sa následne ukončí.
			if (this.hasTileLabel(coordinates, skin)) {
				// console.warn(
				// 	`Tile label with key ${key} already exists in cache.`,
				// );
				return;
			}
			const label = new TileLabel(
				this,
				coordinates,
				scaleToZoom,
				text,
				type,
				skin,
			);
			const levelKey = TileLabel.formatKey(
				TileLabelSkin.LEVEL,
				this.tilerepo.getTileIndexByTileCoordinates(coordinates),
			);
			const levelLabel = this.tileLabelsCache.get(levelKey);
			// Ak platí podmienka `levelLabel`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú príslušný
			// stav.
			if (levelLabel) {
				levelLabel.sharingTile = true;
			}
			const key = TileLabel.formatKey(
				skin,
				this.tilerepo.getTileIndexByTileCoordinates(coordinates),
			);
			// Synchronizujeme príslušnú cache so zmeneným stavom, aby ďalšie lookupy a rendering používali
			// aktuálne údaje.
			this.tileLabelsCache.set(key, label);
			// Ak platí podmienka `skin === TileLabelSkin.SETTLEMENT_HIGHLIGHT`, vykonáme nasledujúcu vetvu a
			// jej operácie aktualizujú príslušný stav.
			if (skin === TileLabelSkin.SETTLEMENT_HIGHLIGHT) {
				this.showClickPulse(
					coordinates.x,
					coordinates.y - 0.05,
					0xc1a473,
					2.5,
					3.8,
					true,
				);
			}
		}
		// Ak platí podmienka `action === TileLabelAction.UPDATE`, vykonáme nasledujúcu vetvu a jej operácie
		// aktualizujú príslušný stav.
		if (action === TileLabelAction.UPDATE) {
			const key = TileLabel.formatKey(
				skin,
				this.tilerepo.getTileIndexByTileCoordinates(coordinates),
			);
			const label = this.tileLabelsCache.get(key);
			// Ak platí podmienka `!label`, vykonáme ochrannú vetvu a aktuálna metóda alebo callback sa následne
			// ukončí.
			if (!label) return;

			// Synchronizujeme sprievodný vizuálny prvok s aktuálnym stavom tile alebo entity.
			this.updateTileLabel(coordinates, skin, TileLabelAction.DELETE);
			// Synchronizujeme sprievodný vizuálny prvok s aktuálnym stavom tile alebo entity.
			this.updateTileLabel(
				coordinates,
				skin,
				TileLabelAction.CREATE,
				type,
				label.rawText,
			);
		}
		//POZOR!!! Tu bolo kedysi volanie this.updateTileLabels(). To spôsobovalo
		//že pri každom update 1 labelu sa aktualizovali všetky a to niekedy aj rekurzívne
		//teraz je potrebné manuálne aktualizovať jednotlivé TileLabels a nespolihať sa nato, že to táto metóda
		//spraví automaticky
	}

	/**
	 * Zaregistruje window resize listener, ktorý pri zmene viewportu aktualizuje rozmery parent elementu aj
	 * fyzickú veľkosť Phaser canvasu s ohľadom na device pixel ratio.
	 *
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private addResizeListener(): void {
		window.addEventListener(
			"resize",
			(): void => {
				const width: number = window.innerWidth;
				const height: number = window.innerHeight;

				// Sets the size of the parent element
				this.scale.setParentSize(width, height);

				// Sets the physical size of the canvas
				this.scale.setGameSize(sizeDpr().width, sizeDpr().height);
			},
			true,
		);
	}

	/**
	 * Odvodí cache kľúč špeciálnej lokácie zo súradníc, zničí jej objekt a odstráni ho z cache.
	 *
	 * @param coordinates Mapové súradnice špeciálnej lokácie určenej na odstránenie.
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private deleteSpecialLocation(coordinates: TileCoordinates): void {
		const key = TileLabel.formatKey(
			TileLabelSkin.SPECIAL_LOCATION,
			this.tilerepo.getTileIndexByTileCoordinates(coordinates),
		);
		const label = this.specialLocationsCache.get(key);
		label?.delete();
		// Synchronizujeme príslušnú cache so zmeneným stavom, aby ďalšie lookupy a rendering používali aktuálne
		// údaje.
		this.specialLocationsCache.delete(key);
	}

	/**
	 * Pokúsi sa vytvoriť novú špeciálnu lokáciu, pokiaľ hráč neprekročil limit aktívnych lokácií. Ak lokácia
	 * nie je dostupná, inicializuje chest repository a skúsi ako náhradu vygenerovať exploration truhlicu.
	 *
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private generateNewSpecialLocation(): void {
		let generateChest = false;
		// Ak platí podmienka `useUserStore().activeSpecialLocationsIds.length >= 3`, vykonáme nasledujúcu vetvu
		// a jej operácie aktualizujú príslušný stav.
		if (useUserStore().activeSpecialLocationsIds.length >= 3) {
			generateChest = true;
		} else {
			// Ak predchádzajúca podmienka neplatí, vykonáme alternatívnu vetvu.
			const specialLocation = getNewSpecialLocation(this);
			// Ak platí podmienka `specialLocation`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú
			// príslušný stav.
			if (specialLocation) {
				this.setSpecialLocation(specialLocation);
			} else {
				// Ak predchádzajúca podmienka neplatí, vykonáme alternatívnu vetvu.
				generateChest = true;
			}
		}
		// Ak platí podmienka `generateChest`, vykonáme nasledujúcu vetvu a jej operácie aktualizujú príslušný
		// stav.
		if (generateChest) {
			const chestrepo = ChestRepository.getInstance();
			// Ak platí podmienka `!chestrepo.initialized`, vykonáme nasledujúcu vetvu a jej operácie
			// aktualizujú príslušný stav.
			if (!chestrepo.initialized) {
				chestrepo.initialize(useTileRepository());
			}
			chestrepo.tryGenerateChest(
				ChestType.EXPLORE,
				clickedTileEntityCache!,
			);
		}
	}

	/**
	 * Vypočíta cache kľúč zo súradníc špeciálnej lokácie a uloží jej repository objekt do lokálnej cache.
	 *
	 * @param specialLocation Repository objekt novej špeciálnej lokácie vrátane jej súradníc a TileLabelu.
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private setSpecialLocation(
		specialLocation: SpecialLocationRepository,
	): void {
		const key = TileLabel.formatKey(
			TileLabelSkin.SPECIAL_LOCATION,
			this.tilerepo.getTileIndexByTileCoordinates(
				specialLocation.coordinates,
			),
		);
		// Synchronizujeme príslušnú cache so zmeneným stavom, aby ďalšie lookupy a rendering používali aktuálne
		// údaje.
		this.specialLocationsCache.set(key, specialLocation);
	}

	/**
	 * Overí, či je na zadanej súradnici evidovaná aktívna špeciálna lokácia.
	 *
	 * @param coordinates Mapové súradnice kontrolovanej lokácie.
	 * @returns True, ak cache obsahuje aktívnu špeciálnu lokáciu na súradnici; inak false.
	 */
	public isSpecialLocationActive(coordinates: TileCoordinates): boolean {
		const key = TileLabel.formatKey(
			TileLabelSkin.SPECIAL_LOCATION,
			this.tilerepo.getTileIndexByTileCoordinates(coordinates),
		);
		return this.specialLocationsCache.has(key);
	}

	/**
	 * Prispôsobí hlasitosť mestského ambientu úrovni hráča, vzdialenosti kamery od najbližšieho dostatočne
	 * rozvinutého sídla a aktuálnemu zoomu. Výsledok kombinuje používateľské maximum, konfiguračný limit,
	 * distance attenuation a zoom attenuation.
	 *
	 * @returns Metóda nevracia dátovú hodnotu; účinok vykoná zmenou stavu scény, cache, store alebo Phaser
	 *          objektov.
	 */
	private updateAmbientSounds(): void {
		// Ak platí podmienka `!useSettingsStore().soundsOn`, vykonáme ochrannú vetvu a aktuálna metóda alebo
		// callback sa následne ukončí.
		if (!useSettingsStore().soundsOn) return;

		// Aktualizujeme prehrávanie alebo hlasitosť zvuku podľa výsledku aktuálnej hernej operácie.
		const sound = this.sound.get(AudioNames.CITY_AMBIENT) as
			| Phaser.Sound.HTML5AudioSound
			| Phaser.Sound.WebAudioSound;

		// Ak platí podmienka `useUserStore().playerLevel < 25`, vykonáme ochrannú vetvu a aktuálna metóda alebo
		// callback sa následne ukončí.
		if (useUserStore().playerLevel < 25) {
			sound?.setVolume(0);
			return;
		}

		const zoom = this.cameras.main.zoom || 1;
		const midpoint = this.cameras.main.midPoint;
		const halfTileSize = this.tilerepo.TILE_SIZE / 2;
		const col = Math.trunc(
			(midpoint.y + halfTileSize) / this.tilerepo.TILE_SIZE,
		);
		const row = Math.trunc(
			(midpoint.x + halfTileSize) / this.tilerepo.TILE_SIZE,
		);

		const closestSettlement =
			this.tilerepo.getClosestExploredSettlementCoordinatesByMinLevel(
				{
					x: row,
					y: col,
				},
				25,
			);
		// Ak platí podmienka `closestSettlement === null`, vykonáme ochrannú vetvu a aktuálna metóda alebo
		// callback sa následne ukončí.
		if (closestSettlement === null) return;
		const distance = Phaser.Math.Distance.Between(
			row,
			col,
			closestSettlement.x,
			closestSettlement.y,
		);
		const {
			AmbientSoundMaxDistance,
			AmbientSoundZoomLevels,
			AmbientSoundMaxVolumePercent,
		} = AmbientSoundConfig;

		const settings = useSettingsStore();
		const maxSettingsVolume = settings.soundsVolume / 100; // normalized 0..1
		const distanceRatio = Phaser.Math.Clamp(
			1 - distance / AmbientSoundMaxDistance,
			0,
			1,
		); // 1 at distance=0, 0 at distance>=maxDistance
		// Apply camera zoom influence to ambient sound ratio.
		// If zoom <= FAR -> silence. If zoom >= CLOSE -> full (no attenuation).
		let zoomFactor = 1;
		// Ak platí podmienka `zoom <= AmbientSoundZoomLevels.FAR`, vykonáme nasledujúcu vetvu a jej operácie
		// aktualizujú príslušný stav.
		if (zoom <= AmbientSoundZoomLevels.FAR) {
			zoomFactor = 0;
			// Ak predchádzajúca podmienka neplatí, overíme alternatívu `zoom >= AmbientSoundZoomLevels.CLOSE`.
		} else if (zoom >= AmbientSoundZoomLevels.CLOSE) {
			zoomFactor = 1;
		} else {
			// Ak predchádzajúca podmienka neplatí, vykonáme alternatívnu vetvu.
			zoomFactor = Phaser.Math.Clamp(
				(zoom - AmbientSoundZoomLevels.FAR) /
					(AmbientSoundZoomLevels.CLOSE - AmbientSoundZoomLevels.FAR),
				0,
				1,
			);
		}

		// combine distance-based ratio with zoom factor
		const combinedRatio = distanceRatio * zoomFactor;
		const volume =
			maxSettingsVolume *
			AmbientSoundMaxVolumePercent *
			combinedRatio *
			100;
		sound?.setVolume(volume);
	}
}

export default MainMapScene;
